/**
 * The MapMark editor core: layered canvases, viewport gestures, one Mark tool
 * with live intent inference, vertex editing, hit-testing, selection and undo.
 * UI chrome lives in ui/editor-screen.ts and talks to this class through a
 * small event API.
 */
import type { ArrowPin, BoxLook, Callout, TextRun, CalloutTarget, Cut, Elevation, ElevMeta, ElevRef, Form, Item, MapDoc, Note, PathShape, Shape, SharedLabel, TestMethod, TestPoint, Vec, WorkArea, Highlight } from '../../shared/doc.ts';
import { AREA_COLOR_KEYS, ELEV_NOTE_MAX, plainOf, tidyRuns, withRunsInStep, ELEV_PREFIX_MAX, MATERIALS, TEST_NAME_MAX, cleanElevation, markupLabel, markupOf, markupsIn, materialByKey, normaliseMarkups, normalisePlacements, placementOf, placementsIn, withMarkupLabel } from '../../shared/doc.ts';
import { FAIL_LABEL, areasOfLabel, isSharedLabel, markupColor, markupLabelId, markupOfLabel } from '../model/markups.ts';
import { elevWarning, isElevation, nextElevNumber } from '../model/elev.ts';
import { canvasToBlob, releaseCanvas } from '../app/image.ts';
import { methodOf, nextTestNumber, numberingStart } from '../model/testid.ts';
import { calloutIsEmpty } from '../model/station.ts';
import { addArrow, arrowsOf, calloutsAt, markIdsOf, pointsAt, removeArrowAt, retargetArrows, updateArrow, withArrows, withoutMarks } from '../model/arrows.ts';
import { History, addItem, bringToFront, newId, removeItems, updateItem } from '../model/ops.ts';
import { GLOW_REST, type GlowState, type PenDrawing, drawPen, drawAnnotations, paintArrowGhost, drawEndHandles, endTabCentre, endTabSize, drawFormHandles, drawGlowTip, drawGlowTrail, drawHandles, drawRests, drawShapeGlow, erasePolygons, paintOutlines, drawShapes, drawSnapRing, drawLift, drawCutOutlines, drawCutPreview, liftUnder, paintBand, renderAll, releaseScratch } from '../render/draw.ts';
import { anchorInside, bandOutline, differencePolygons, expandOutward, keyVertices, polygonsOverlap, shapeToPolygon, unionPolygons } from './outline.ts';
import { cutRegions, cutsOf, inCut } from './cuts.ts';
import { boxAllowed, boxFree, nearestFree, segmentCrossesPoly, type MarkEnd } from './callout.ts';
import { Shake } from './shake.ts';
import { labelRests, nearestWhere, rubberBand } from './soils.ts';
import { fitPath, flattenPath, moveAnchor, pathOn, removeAnchor, translatePath } from './curve.ts';
import { boxAround, cornered, cornerRounding, radiusOf, ellipseAsPoly, formHandles, formKnob, formMids, formPath, formPolygon, insertPoint, moveEdge, moveHandle, moveSide, pointKind, rectCorners, removePoint, setPointKind, setRounding, toggleRound, translateForm, turnForm, type PointKind, type PolyForm, type RectForm } from './form.ts';
import { penForm, penGuides, snapToGuides, type GuideSnap, type PenKind } from './pen.ts';
import { fitOpenStroke, readSketch, outlineForm, type ReadingKey, type SketchReading } from './sketch.ts';
import { circleCentre, snapCorner, snapSide, snapToLine, type Snap } from './snap.ts';
import { bandEnds, beginPull, borderOf, pullTo, type BandEnd, type EndPull } from './ends.ts';
import { areaStroke, calloutColor, calloutContext, computeLayout, labelMeasure, layoutRects, shapeBounds, sharedMarks, type CalloutDraft, type ElevDraft, type LabelBox, type Layout, type PlanAround } from '../render/labels.ts';
import { AREA_FILL_ALPHA, BAND_ALPHA, FAIL, FAIL_AREA_FILL, INK, areaColors, metrics, materialColor, type Metrics } from '../render/style.ts';
import { sharedMeasurer } from '../render/text.ts';
import { boxLeft, glideAt, glided, glideTo, handWidth, type Glide, type Span } from './widths.ts';
import { useLift } from '../render/lift.ts';
import { fallbackIntent, strokeForm, type Intent, type IntentEngine } from '../vision/engine.ts';
import type { PlanStations } from '../vision/stations.ts';
import { paintDrawing, type DItem, type Drawing } from '../vision/drawing.ts';
import { refreshAutoStations, withAutoStations } from './autostations.ts';
import { LiveStroke } from '../vision/livestroke.ts';
import { dedupe, polylineLength } from './geom.ts';
import { clamp, dist, nearestOnPolyline, pointInPoly, polyBounds, polyCentroid, rectContains, rectToPoly, segmentIntersectsRect, segmentsIntersect, stopAtBorder, translatePts, type Rect } from './geom.ts';
import { edgeOffset, inkEdge } from '../vision/ink.ts';
import { projectOnRail } from '../vision/rails.ts';
import { Surface, type DownAction, type Sample, type StrokeInfo } from './input.ts';
import { PreciseDrag } from './precise.ts';
import { FIT_PAD, constrain, fitView, pan, pinchView, toImage, toScreen, viewEquals, zoomAround, type View } from './viewport.ts';

/**
 * mark – select, and draw (a band or a shape; on a soils map a work area)
 * test – soils: every tap places a test (it stays in hand until another tool is chosen)
 * elev – every tap places an elevation shot, its elevation typed in place
 * square, circle – one shape, drawn or dropped; callout – one callout: each then hands back
 */
export type ToolId = 'pen' | 'mark' | 'test' | 'elev' | 'square' | 'circle' | 'callout';
export type HitPart = 'marker' | 'label' | 'shape' | 'box' | 'target' | 'vertex' | 'midpoint' | 'end' | 'fpoint' | 'fmid' | 'fside' | 'frot' | 'frad' | 'edge';
/** What a drawn shape's sketch can be read as: its own form made perfect, a rectangle, a circle, an ellipse, or the plan's own outline around it. */
export type Reading = ReadingKey | 'plan';
export interface Hit {
  id: string; // item id, or a shared label's ('__area', '__area2' … for a markup's, '__fail' for the failing areas')
  part: HitPart;
  index?: number; // vertex / midpoint / end index; for a callout's 'target', which of its arrows
}
export interface EditorEvents {
  change(doc: MapDoc): void;
  selection(id: string | null, vertex: number | null): void;
  tool(tool: ToolId): void;
  viewChanged(): void;
  /** An item is being dragged (chrome around it steps aside) / was released. */
  dragging(active: boolean): void;
  /** Hold-to-mark feedback, with the screen point while holding. */
  hold(state: 'start' | 'armed' | 'end', p?: Vec): void;
  /** What the live stroke is being interpreted as (null when idle). */
  intent(label: string | null): void;
  /** Something the person should know about what just happened (shown briefly). */
  notice(text: string): void;
  /** Something that looks wrong and should be checked (shown longer, and plainly): never put right by the editor. */
  warn(text: string): void;
  /** A callout box being carried passed onto a place where it may not rest, or off one (felt, not said). */
  resting(ok: boolean): void;
  /** A point took hold of the plan (a corner, a line) or a rest became a corner: felt where the device can, seen always. */
  snapped(): void;
  /** Corners are being placed one by one (their count), or that is over (null). */
  building(count: number | null): void;
}
export interface EditorSettings {
  fingerPansAfterPen: boolean;
  /** Anchor points being dragged snap to the plan's lines. */
  snapAnchors: boolean;
}

const MAX_SCALE = 10;

interface Drag {
  hit: Hit;
  start: Vec;
  last: Vec;
  moved: boolean;
  origin: MapDoc;
  /** The label/callout box at the start (fixed for the whole drag). */
  base: Rect | null;
  /** Where a callout box last could have rested during this drag (where it goes back to if there is nowhere nearer). */
  lastValid: Vec | null;
  /** The last offset a moved mark was allowed (it never overlaps another placement's mark). */
  lastMove: Vec | null;
  /** A callout's arrow tip as shown when the drag began (the finger moves it from there). */
  tip: Vec | null;
  /** Boxes a dragged callout may not land on (the other callouts and notes, as laid out at the start). */
  avoid: Rect[];
  /** For an arrowhead drag: the end of its mark the head is on now (null = on the side facing the box). */
  face: MarkEnd | null;
  /** A band end being pulled, the press point's offset from the tab, and where the tab last was (never inside another placement's mark) with the border it gave. */
  end: { pull: EndPull; grip: Vec; lastTab: Vec | null; lastPoly: number[] | null } | null;
  /** A drawn shape's point in the hand (fine control, snapping, lift-off). */
  point: PreciseDrag | null;
  /** The turning knob in the hand stands below the shape (it was near the top of the screen). */
  knobBelow?: boolean;
  /**
   * An arrowhead in the hand: which arrow (a callout's, by its place among its arrows; a shared
   * label's, by its area), the mark it is on now, and the finger's last swings (a shake takes it off).
   */
  arrow?: { callout: number | null; area: string | null; mark: string | null; shake: Shake; tip?: Vec };
  /**
   * A box's side in the hand (`edge`): which (0 left, 1 right), the box as it was, how wide it may be
   * made (its widest word; its words on one line), the box measured at a width (or `exact`ly that wide),
   * and how wide its words are now.
   */
  resize?: { side: 0 | 1; rect: Rect; fit: { min: number; nat: number }; at: (w?: number, exact?: boolean) => { w: number; h: number }; w: number };
}


/** What carries a drawn shape and its handles: a mark, a work area, or a cut out of one. */
type Shaped = WorkArea | Highlight | Cut;
function isShaped(it: Item | undefined): it is Shaped {
  return !!it && (it.t === 'hl' || it.t === 'area' || it.t === 'cut');
}

/** Corners being placed one at a time: each touch places (or adjusts) one; the first closes the shape. */
/** A shape being placed point by point (the Pen, or Magic Mark's corners placed one at a time). */
interface Building {
  /** The points as placed (on what was under them). */
  pts: Vec[];
  /** Where the finger put each (a corner is landed again on the plan once both its sides are known). */
  raw: Vec[];
  /** 1: the outline curves through the point. */
  round: number[];
  /** A corner's rounding (image px; 0: sharp). */
  r: number[];
  /** Whether each took hold of the plan where it was placed. */
  snapped: boolean[];
  /** What the next point is. */
  kind: PenKind;
  /** The point in hand (its bar), or none. */
  sel: number | null;
  /** The touch in progress: a new point (index null), one being adjusted, a rounded corner's handle, or the middle of a side (`side`) — pulled, a new point there. */
  touch: { drag: PreciseDrag; index: number | null; moved: boolean; part: 'point' | 'radius' | 'mid'; side?: number } | null;
  /** A stretch being drawn by the finger (Draw), and whether it set off from the last point. */
  stroke: { pts: number[]; times: number[]; fromLast: boolean } | null;
  /** Each step's state before it (undo). */
  undo: { pts: Vec[]; raw: Vec[]; round: number[]; r: number[]; snapped: boolean[] }[];
}
/** A touch with the Pen before it is known what it is (see #penDown). */
interface PenPress {
  drag: PreciseDrag;
  /** The mark (or cut) it landed on. */
  over: string | null;
  /** A callout was being typed into: a tap only ends that. */
  typing: boolean;
  /** It landed on the shape in hand: a drag that sets off at once moves it. */
  moveable: boolean;
  /** It is the first point of a shape (else it may yet be a tap). */
  promoted: boolean;
  down: Vec;
  timer: number;
  shown: boolean;
}

/** Which side of the finger the loupe stands, and the offset it is gliding from (since t0) when it has just changed sides. */
type LoupeSide = { side: 'above' | 'left' | 'right'; from: Vec | null; t0: number };

/** Hold still this long (ms) and lift, and a corner is placed there (a shorter press is a tap). */
const HOLD_POINT_MS = 350;
/** With the Pen, a press on a mark held this long (ms) is a point there, not a tap on the mark. */
const PEN_HOLD_MS = 220;
/** A point being placed shows the loupe once the finger has stayed this long (ms): a quick tap flashes nothing. */
const LOUPE_DELAY_MS = 110;
/** A rounded corner's handle stands this far (screen px) inside the middle of its arc. */
const ROUNDING_OFF = 30;
/** A corner shows its rounding handle, rounded or not, when both its sides are at least this long on screen (px). */
const ROUNDING_ROOM = 150;
/** Within this of the screen's edge (px) a point being placed runs the plan on; at most this fast (px/s). */
/** How near the screen's edge a finger carrying something sets the plan running the other way (px), and how fast at the very edge (px/s): it eases in, so a nudge into the zone creeps and a push all the way runs. */
const EDGE_ZONE = 64;
const EDGE_SPEED = 960;
/** A handle's reach for a fingertip, screen px (Apple's 44 pt target, measured from its centre). */
const HANDLE_REACH = 24;
const MID_REACH = 18;

export class Editor {
  readonly stage: HTMLElement;
  readonly stack: HTMLElement;
  #base: HTMLCanvasElement;
  #overlay: HTMLCanvasElement;
  #live: HTMLCanvasElement;
  #bctx: CanvasRenderingContext2D;
  #octx: CanvasRenderingContext2D;
  #lctx: CanvasRenderingContext2D;
  #image: ImageBitmap;
  #history: History;
  #events: EditorEvents;
  #surface: Surface;
  #ro: ResizeObserver;
  #view: View = { scale: 1, tx: 0, ty: 0 };
  /** The view gliding somewhere of its own accord (to make room for a selection's handles): its frame. */
  #glideRaf = 0;
  /**
   * Where the screen's floating controls cover the plan (stage px). A shape's handle under one of
   * them could not be taken hold of — a tap there is a tap on the control — so the plan makes room.
   */
  coveredBy: () => Rect[] = () => [];
  #gesture: View | null = null; // transient view during pinch/pan (rendered every frame)
  #motionUntil = 0; // while now < motionUntil, shapes come from the raster cache
  #motionTimer = 0;
  /** The marks drawn once for the plan in motion; `lifted`: with the plan lifted under them (then laid over the plan as they are). */
  #shapesCache: { canvas: HTMLCanvasElement; key: readonly Item[]; w: number; h: number; scale: number; k: number; lifted: boolean } | null = null;
  #size = { w: 1, h: 1, dpr: 1 };
  #dirty = { base: true, overlay: true, live: false };
  #raf = 0;
  #layout: { doc: MapDoc; layout: Layout } | null = null;
  /**
   * The layout last made, kept when the one above is let go to be made again: every label is laid
   * out again from where it was (and stays there unless something has come to move it) — never
   * from nothing, which would send every label somewhere new.
   */
  #lastLayout: Layout | null = null;
  #calloutDraft: CalloutDraft | null = null;
  /**
   * The callout box in the hand. It follows the finger everywhere — across marks and other
   * callouts too, which is how it gets to the other side of them — and where it may not rest it is
   * `adrift`: lifted, its colour drained, its leaders let go. Let go of there, it glides to the
   * nearest place it may rest (`#settle`). `released`: on its way back down onto the plan.
   */
  #carried: { id: string; adrift: boolean; released: boolean } | null = null;
  #liftT = 0;
  #adriftT = 0;
  #carryClock = 0;
  /** The box let go adrift, gliding to where it may rest: `place` puts it at a point, `rests` says whether it may rest there. */
  #settle: { id: string; from: Vec; to: Vec; t0: number; place: (doc: MapDoc, at: Vec) => MapDoc; rests: (at: Vec) => boolean } | null = null;
  static readonly #SETTLE_MS = 520;
  #glowT0 = -Infinity;
  static readonly #BLOOM_MS = 200;
  #working: MapDoc | null = null; // transient doc during drags
  #selection: string | null = null;
  #vertexSel: number | null = null;
  /** The shape whose anchors are shown (double-tapped); its end tabs show whenever it is selected. */
  #editing: string | null = null;
  #endsCache = new WeakMap<Shape, { items: readonly Item[]; ends: BandEnd[] }>();
  #tool: ToolId = 'mark';
  /** The tool Esc hands back to from the others: Magic Mark, or on a soils map Test (or Elevation) once it has been used. */
  #sticky: 'mark' | 'test' | 'elev' = 'mark';
  /** The markup the next work area goes into on a soils map (the map's latest, until the next one is begun). */
  #markup = 1;
  /** A tap with Mark on bare plan does nothing on a soils map: said once, where tests come from. */
  #testHinted = false;
  #material = MATERIALS[0]!.key;
  /** Anchor points being dragged snap to the plan's lines (switched from a mark's panel or Settings). */
  snapAnchors = true;
  /** The placement the next mark goes into (the map's latest, until the next one is started). */
  #placement = 1;
  /** Border polygons of committed shapes, so yielding to them costs nothing per frame. */
  #polyCache = new WeakMap<Shape, number[]>();
  #drag: Drag | null = null;
  /** Edits folded into one undo step (typing into one callout field). */
  #coalesce: string | null = null;
  #stroke: number[] | null = null; // marking stroke in progress (image px)
  #intent: Intent | null = null;
  #intentLabel: string | null = null;
  #snapHint: { p: Vec; kind: 'node' | 'line' } | null = null;
  #engine: IntentEngine | null = null;
  #tracer: LiveStroke | null = null; // real-time tracer for the stroke in progress
  #fitted = false;
  /** When each stroke point was drawn (ms): the finger's pace tells a meant corner. */
  #times: number[] = [];
  /** Where the finger rested on purpose while drawing: corners the person placed. */
  #pins: Vec[] = [];
  #dwell: { at: Vec; t: number; timer: number } | null = null;
  /** What each recently drawn shape's sketch could otherwise be. */
  #building: Building | null = null;
  /** The press just handled as a point (its tap is not also a tap on the plan). */
  #swallowTap = false;
  /** A touch with the Pen, not yet a point or a tap. */
  #penPress: PenPress | null = null;
  /** What the Pen's next point is, from shape to shape. */
  #penKind: PenKind = 'corner';
  /** The last rounding set by hand (image px): the next rounded corner starts from it. */
  #lastRounding: number | null = null;
  /** The guides the point being placed has settled on (drawn while it is carried). */
  #guides: GuideSnap | null = null;
  #loupeTimer = 0;
  #lastFinger: Vec | null = null;
  /** The plan running on under a finger at the screen's edge. */
  #edge: { v: Vec; t: number; raf: number } | null = null;
  /** A square or circle being drawn out with the Square or Circle tool (image px; `down`: where the finger landed, screen px). */
  #placing: { kind: 'square' | 'circle'; start: Vec; at: Vec; moved: boolean; down: Vec } | null = null;
  /** The knob's turn is resting square (a detent is felt once on the way in). */
  #turnDetent = false;
  /** The exact point being placed, magnified beside the finger. */
  #loupe: { at: Vec; finger: Vec; snapped: boolean; fine: boolean } | null = null;
  #loupeEl: HTMLCanvasElement;
  /**
   * Where the loupe stands off the finger, kept from frame to frame so it moves as one thing with
   * the finger: above it, or to one side of it near the top of the screen. It keeps its side until
   * that side has no room (and goes back only once there is room to spare), and glides between
   * sides (`from`: the offset it is leaving, since `t0`) — it never jumps.
   */
  #loupeSide: LoupeSide | null = null;

  constructor(stage: HTMLElement, doc: MapDoc, image: ImageBitmap, settings: EditorSettings, events: EditorEvents) {
    this.stage = stage;
    this.#image = image;
    // placement and markup numbers are kept dense; a map opens on its latest placement and markup
    this.#history = new History(normaliseMarkups(normalisePlacements(doc).doc).doc);
    this.#placement = Math.max(1, this.placementCount);
    this.#markup = Math.max(1, this.markupCount);
    this.#areaColor = markupColor(this.doc.items, this.#markup) ?? AREA_COLOR_KEYS[0];
    this.#events = events;
    this.stack = document.createElement('div');
    this.stack.className = 'stack';
    this.#base = document.createElement('canvas');
    this.#overlay = document.createElement('canvas');
    this.#live = document.createElement('canvas');
    for (const c of [this.#base, this.#overlay, this.#live]) this.stack.append(c);
    this.#loupeEl = document.createElement('canvas');
    this.#loupeEl.className = 'loupe';
    this.#loupeEl.hidden = true;
    this.stack.append(this.#loupeEl);
    stage.append(this.stack);
    this.#bctx = this.#base.getContext('2d', { alpha: false })!;
    this.#octx = this.#overlay.getContext('2d')!;
    this.#lctx = this.#live.getContext('2d')!;
    this.#surface = new Surface(
      stage,
      {
        onDown: (p, info) => this.#onDown(p, info),
        onDrawStart: (p) => this.#onDrawStart(p),
        onHoldStart: (p) => this.#events.hold('start', p),
        onHoldCancel: () => this.#events.hold('end'),
        onMove: (samples, last) => this.#onMove(samples, last),
        onUp: (p, info, wasTap, held) => this.#onUp(p, info, wasTap, held),
        onCancel: () => this.#onCancel(),
        onPanEnd: () => this.#commitGesture(),
        onTap: (p) => this.#onTap(p),
        onLongPress: (p) => this.#onLongPress(p),
        onPinch: (a0, b0, a1, b1) => this.#onPinch(a0, b0, a1, b1),
        onPinchEnd: () => this.#commitGesture(),
        onTwoFingerTap: () => this.#onTwoFingerTap(),
        onWheelPan: (dx, dy) => this.#setView(pan(this.#view, dx, dy), true),
        onWheelZoom: (f, pivot) => this.#setView(zoomAround(this.#view, f, pivot, this.#limits()), true),
        onGestureStart: () => {
          this.#stopGlide();
          this.#gesture = this.#gesture ?? { ...this.#view };
        },
        onGestureZoom: (f, pivot) => {
          const g = this.#gesture ?? this.#view;
          this.#gesture = zoomAround(g, f, pivot, this.#limits());
          this.#gestureChanged();
        },
        onGestureEnd: () => this.#commitGesture(),
      },
      { fingerPansAfterPen: settings.fingerPansAfterPen },
    );
    this.snapAnchors = settings.snapAnchors;
    this.#surface.panHandler = (dx, dy) => this.#panBy(dx, dy);
    this.#ro = new ResizeObserver(() => this.resize());
    this.#ro.observe(stage);
    this.resize();
    if (import.meta.env.DEV) (globalThis as { __editor?: unknown }).__editor = this; // dev scripts (scripts/*.mjs) drive the view through this
  }

  /* ---------------------------------------------------------- accessors */

  get doc(): MapDoc {
    return this.#history.present;
  }
  get displayDoc(): MapDoc {
    return this.#working ?? this.doc;
  }
  /** (While corners are placed, Undo takes back the last one: there is something to undo from the first.) */
  get canUndo(): boolean {
    return (this.#building !== null && this.#building.pts.length > 0) || this.#history.canUndo;
  }
  get canRedo(): boolean {
    return this.#history.canRedo;
  }
  get selection(): string | null {
    return this.#selection;
  }
  get vertexSelection(): number | null {
    return this.#vertexSel;
  }
  /** The shape whose anchor points are showing (after Edit on its panel), if any. */
  get editing(): string | null {
    return this.#editing;
  }
  /**
   * Show (or put away) the selected mark's or area's points. An outline read off the plan (a Magic
   * Mark band, a plan outline) shows the few points that make it — its corners, straight sides and
   * round points, each a handle (see `pointEditing`); a legacy band its anchors.
   */
  setEditing(on: boolean): void {
    const id = this.#selection;
    const it = id ? this.getItem(id) : undefined;
    if (!it || (it.t !== 'hl' && it.t !== 'area')) return;
    if (it.shape.k !== 'stroke') return this.editPoints(on);

    const next = on ? it.id : null;
    if (next === this.#editing) return;
    this.#editing = next;
    this.#vertexSel = null;
    this.#dirty.overlay = true;
    this.#schedule();
    this.#events.selection(it.id, null);
  }
  /** The drawn shape whose points are being edited (Edit), when it is one that keeps no mark of it in the document. */
  #vectorEdit: string | null = null;
  /**
   * Whether a shape's points are being edited — its vectors: each point a handle (tap one for its
   * bar: Corner/Round, Delete point) and a dot mid-side to pull a new one out of. Else a drawn shape
   * is handled as a whole: its corners, a bar on each side (the side moves square to itself) and a
   * box's or oval's knob.
   */
  #pointsMode(it: Item | undefined): boolean {
    if (!isShaped(it) || it.shape.k !== 'path' || !it.shape.form) return false;
    return !!it.shape.edit || this.#vectorEdit === it.id;
  }
  /** Whether the selected shape's points are being edited (Done puts them away). */
  get pointEditing(): boolean {
    return this.#pointsMode(this.#selection ? this.getItem(this.#selection) : undefined);
  }
  /** Whether the selected shape can have its points edited (Edit): a drawn shape, a cut, or an outline read off the plan. */
  get canEditPoints(): boolean {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    return isShaped(it) && it.shape.k === 'path' && !this.#pointsMode(it);
  }
  /** Whether the selected mark is an outline read off the plan (a Magic Mark), whose points Edit shows. */
  get selectionIsOutline(): boolean {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    return !!it && (it.t === 'hl' || it.t === 'area') && it.shape.k === 'path' && !it.shape.form;
  }
  /**
   * Edit: the selected shape's points out — its vectors, to reshape it point by point — and Done puts
   * them away. A square or circle is made the custom shape of its corners (a circle's four round
   * points) for it, and a square that is still square when Done comes is a square again, its edges and
   * knob back. A Magic Mark (an outline read off the plan) shows the few points that make it — its
   * corners, straight sides and round points — and Done lets them go: an outline again, with its end
   * tabs back. Letting the shape go (selecting something else) is Done too.
   */
  editPoints(on: boolean): void {
    const id = this.#selection;
    const it = id ? this.getItem(id) : undefined;
    if (!isShaped(it)) return;
    if (!on) {
      if (this.#endPointEdit(it.id)) this.#events.selection(this.#selection, null);
      return;
    }
    if (this.#pointsMode(it)) return;
    this.#editing = null;
    this.#vertexSel = null;
    const form = it.shape.k === 'path' ? it.shape.form : undefined;
    if (form) {
      // a box or an ellipse: the custom shape of its corners (round points), for as long as its points are out
      if (form.f !== 'poly') {
        const poly: PolyForm = form.f === 'rect' ? cornered(form)! : ellipseAsPoly(form);
        this.commit(updateItem(this.doc, it.id, (x) => ({ ...x, shape: keeping((x as Shaped).shape, formShape(poly)) }) as Item));
      }
      this.#vectorEdit = it.id;
      this.#dirty.overlay = true;
      this.#schedule();
      this.#events.selection(it.id, null);
      return;
    }
    // an outline read off the plan: the few points that make it (kept in the document as its form, `edit`)
    const path = it.shape.k === 'path' ? it.shape : { k: 'path' as const, ...fitPath(shapeToPolygon(it.shape), this.#fitTol, this.#cornerReach) };
    const f = outlineForm(path, Math.max(1, 0.5 * this.metrics.u));
    if (!f) return this.#events.notice('That shape has no points to edit');
    const shape: PathShape = { ...(formShape(f) as PathShape), edit: true };
    this.#vectorEdit = it.id;
    this.commit(updateItem(this.doc, it.id, (x) => ({ ...x, shape }) as Item));
    this.#events.selection(it.id, null);
  }
  /**
   * A shape's points put away (Done): an outline read off the plan lets its form go (its end tabs
   * back), a custom shape that is still a box is a box again. No undo step of its own: undo goes on
   * to what was done before. True when it had its points out.
   */
  #endPointEdit(id: string): boolean {
    const it = this.getItem(id);
    const was = this.#vectorEdit === id;
    if (was) this.#vectorEdit = null;
    if (!isShaped(it) || it.shape.k !== 'path') return was;
    let shape: Shape | null = null;
    if (it.shape.edit) {
      const { form: _form, edit: _edit, ...outline } = it.shape;
      shape = outline;
    } else if (was && it.shape.form?.f === 'poly') {
      const f = it.shape.form;
      const box = asBox(f);
      // (each of its corners keeps its rounding)
      if (box) shape = formShape(f.r ? { ...box, r: rectCorners(box).map((c) => f.r![nearestIndex(polyPointsOf(f.pts), c)] ?? 0) } : box);
    }
    this.#vertexSel = null;
    if (shape) {
      const next = shape;
      const doc = this.#normalised(updateItem(this.doc, id, (x) => ({ ...x, shape: next }) as Item));
      if (this.#history.amend(doc)) {
        this.#coalesce = null;
        this.#invalidate(true);
        this.#events.change(doc);
      }
    } else if (was) {
      this.#dirty.overlay = true;
      this.#schedule();
    }
    return was || !!it.shape.edit;
  }

  /* ------------------------------------------------------------ cut out */

  /** The mark being cut into: every shape drawn now is taken out of it instead of made (null: shapes are marks). */
  #cutting: string | null = null;
  get cutting(): string | null {
    return this.#cutting;
  }
  /**
   * The cut taken in hand by a tap: only then does a drag inside it move it. A cut just drawn shows
   * its handles, but a drag inside it draws the next cut — over it, to cut out as one with it.
   */
  #cutTaken: string | null = null;
  /**
   * Cut out of the selected mark (or stop). While it goes on, every shape drawn — with Square or
   * Circle (a drag), a loop or a band with Mark, corners placed one by one — is a cut of the mark
   * (see `#addCut`), the mark itself untouched. It goes on, cut after cut, until Done, Esc or
   * another selection. Each cut is one undo step.
   */
  setCutting(on: boolean): void {
    const id = this.#selection;
    const it = id ? this.getItem(id) : undefined;
    const next = on && it && (it.t === 'hl' || it.t === 'area') ? it.id : null;
    if (next === this.#cutting) return;
    // done: the mark cut into is in hand again (its cuts go out of sight, and out of reach)
    if (!next && it?.t === 'cut') {
      this.#endPointEdit(it.id);
      this.#setSelection(it.of, null);
    }
    if (next && this.#tool !== 'pen' && this.#tool !== 'mark' && this.#tool !== 'square' && this.#tool !== 'circle') this.setTool('mark', true);
    this.cancelBuilding();
    this.#cutting = next;
    this.#editing = null;
    this.#vertexSel = null;
    this.#dirty.overlay = true;
    this.#dirty.live = true;
    this.#schedule();
    this.#events.selection(this.#selection, null);
  }
  /** Whether any of a callout's arrows was put by hand (then More offers them back to the middle). */
  hasPinnedArrows(id: string): boolean {
    const c = this.getItem(id);
    return c?.t === 'callout' && arrowsOf(c).some((a) => a.pinned || a.off);
  }
  /** Whether a callout has an arrow taken off (shaken off its mark). */
  hasArrowOff(id: string): boolean {
    const c = this.getItem(id);
    return c?.t === 'callout' && arrowsOf(c).some((a) => a.off);
  }
  /** Every arrow of a callout put by hand finds its own place on its mark again: the middle of its edge. */
  centreArrows(id: string): void {
    const c = this.getItem(id);
    if (c?.t !== 'callout') return;
    this.updateItem<Callout>(id, (x) => withArrows(x, arrowsOf(x).map((a) => ({ target: a.target, targetId: a.targetId }))));
  }

  /**
   * A shape drawn while cutting: a cut of the mark — an item of its own, selected with its handles out,
   * so it can be moved, resized, turned or reshaped at once, and at any time the mark is cut into again.
   */
  #addCut(of: string, drawn: Shape): void {
    const mark = this.getItem(of);
    if (!mark || (mark.t !== 'hl' && mark.t !== 'area')) {
      this.#cutting = null;
      return;
    }
    // a band drawn with Mark cuts out its outline
    const shape: Shape = drawn.k === 'stroke' ? { k: 'path', ...fitPath(shapeToPolygon(drawn), this.#fitTol, this.#cornerReach) } : drawn;
    if (!polygonsOverlap(shapeToPolygon(mark.shape), shapeToPolygon(shape))) return this.#events.notice('Draw over the mark to cut it out');
    const cut: Cut = { t: 'cut', id: newId(), of, shape };
    this.commit(addItem(this.doc, cut));
    this.#cutTaken = null;
    this.#setSelection(cut.id, null);
  }
  /** Is an item a cut of the mark being cut into? */
  #cutOfCutting(id: string | null): boolean {
    if (!id || !this.#cutting) return false;
    const it = this.getItem(id);
    return it?.t === 'cut' && it.of === this.#cutting;
  }

  /** The callout whose arrows are being chosen: a tap on a mark points it there, or no longer there. */
  #pointing: string | null = null;
  get pointing(): string | null {
    return this.#pointing;
  }
  /** Start (or finish) choosing which marks the selected callout points at. */
  setPointing(on: boolean): void {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    const next = on && it?.t === 'callout' ? it.id : null;
    if (next === this.#pointing) return;
    this.#pointing = next;
    this.#dirty.overlay = true;
    this.#schedule();
    this.#events.selection(this.#selection, null);
  }
  /**
   * Point a callout at a mark, or stop pointing it there. A callout keeps one arrow; an arrow
   * at a bare spot of the plan was only ever standing in for a mark, and gives way to the first.
   */
  toggleArrow(calloutId: string, markId: string): void {
    const c = this.getItem(calloutId);
    const hl = this.getItem(markId);
    if (!c || c.t !== 'callout' || !hl || (hl.t !== 'hl' && hl.t !== 'area')) return;
    if (pointsAt(c, markId)) {
      // its arrow taken off there: back on
      const k = arrowsOf(c).findIndex((a) => a.targetId === markId);
      if (arrowsOf(c)[k]?.off) return this.updateItem<Callout>(calloutId, (x) => updateArrow(x, k, ({ off: _o, ...a }) => a));
      if (arrowsOf(c).length === 1) return this.#events.notice('A callout keeps one arrow');
      this.updateItem<Callout>(calloutId, (x) => removeArrowAt(x, markId));
      return;
    }
    const arrow: CalloutTarget = { target: this.#clampToPlan(anchorOnShape(hl.shape)), targetId: markId };
    const free = arrowsOf(c).length === 1 && calloutContext(this.doc, this.metrics).aimsOf(c)[0]?.mark === null;
    this.updateItem<Callout>(calloutId, (x) => (free ? withArrows(x, [arrow]) : addArrow(x, arrow)));
  }
  get tool(): ToolId {
    return this.#tool;
  }
  get material(): string {
    return this.#material;
  }
  /** The placement the next mark goes into (1 … k, or k + 1: a new one). */
  get placement(): number {
    return this.#placement;
  }
  /** How many placements the map holds (numbered 1 … k, always dense). */
  get placementCount(): number {
    return placementsIn(this.doc.items).length;
  }
  /** The placements to choose from: every one on the map, plus the new one when that is being marked. */
  get placements(): number[] {
    const k = this.placementCount;
    const out = Array.from({ length: k }, (_, i) => i + 1);
    if (this.#placement > k) out.push(k + 1);
    return out;
  }
  /** Whether a placement has any marks. */
  placementHasMarks(n: number): boolean {
    return this.doc.items.some((x) => x.t === 'hl' && placementOf(x) === n);
  }
  /** The selected mark, or the mark the selected callout points at. */
  get selectedMark(): Highlight | null {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (!it) return null;
    if (it.t === 'hl') return it;
    if (it.t === 'callout') {
      for (const id of markIdsOf(it)) {
        const hl = this.getItem(id);
        if (hl && hl.t === 'hl') return hl;
      }
    }
    return null;
  }
  /** The placement of the selected mark (or of the selected callout's mark). */
  get selectionPlacement(): number | null {
    const hl = this.selectedMark;
    return hl ? placementOf(hl) : null;
  }
  /**
   * Choose the placement the next mark goes into. Nothing on the map changes: the
   * selection is let go (an untouched callout is dropped, as when tapping the plan)
   * and the placement's marks light up for a moment so it is plain which one it is.
   */
  setPlacement(n: number): void {
    const k = this.placementCount;
    this.#placement = Math.max(1, Math.min(k + 1, Math.round(n)));
    this.select(null);
    this.#flashPlacement(this.#placement);
  }
  /** Start the next placement: the next mark goes into a new one after the map's last. Returns its number. */
  newPlacement(): number {
    const k = this.placementCount;
    if (this.#placement <= k) this.#placement = k + 1;
    this.select(null);
    return this.#placement;
  }
  /**
   * Move a mark into another placement (k + 1 = a new one), deliberately: it leaves its
   * old placement, yields to the marks of every other placement and joins the marks of
   * its new one. A callout of its own stays with it; one it only shared (its old pour's)
   * lets it go, and its new pour's callout points at it. A move never brings a new callout.
   */
  movePlacement(id: string, n: number): void {
    const hl = this.getItem(id);
    if (!hl || hl.t !== 'hl') return;
    const k = this.placementCount;
    n = Math.max(1, Math.min(k + 1, Math.round(n)));
    if (placementOf(hl) === n) return;
    let moved = updateItem<Highlight>(this.doc, id, (x) => ({ ...x, placement: n }));
    moved = { ...moved, items: moved.items.map((x) => (x.t === 'callout' ? removeArrowAt(x, id) : x)) };
    const placed = this.#place(moved, id);
    const first = placed.ids[0];
    if (!first) {
      this.#setSelection(null, null);
      this.commit(placed.doc);
      this.#events.notice(`Pour ${n} already covers that mark`);
      return;
    }
    const own = this.#pourCallout(placed.doc, n, hl.material);
    this.commit(own ? this.#pointAt(placed.doc, own.id, placed.ids) : placed.doc);
    this.select(first);
  }
  /** A placement's marks glowing for a moment (after it is chosen). */
  #flash: { ids: string[]; t0: number } | null = null;
  static readonly #FLASH_MS = 700;
  #flashPlacement(n: number): void {
    this.#flashIds(this.doc.items.filter((x) => x.t === 'hl' && placementOf(x) === n).map((x) => x.id));
  }

  /* ------------------------------------------------------------ markups */

  /** The markup the next work area goes into (1 … k, or k + 1: a new one). */
  get markup(): number {
    return this.#markup;
  }
  /** How many markups the map holds (numbered 1 … k, always dense). */
  get markupCount(): number {
    return markupsIn(this.doc.items).length;
  }
  /** The markups to choose from: every one on the map, plus the new one when that is being marked. */
  get markups(): number[] {
    const k = this.markupCount;
    const out = Array.from({ length: k }, (_, i) => i + 1);
    if (this.#markup > k) out.push(k + 1);
    return out;
  }
  /** A markup's colour: its areas' (one being begun: the colour chosen for it). */
  markupColour(n: number): string {
    return markupColor(this.doc.items, n) ?? (n === this.#markup ? this.#areaColor : AREA_COLOR_KEYS[0]);
  }
  /** The markup of the selected work area (or of the selected markup label). */
  get selectionMarkup(): number | null {
    const sel = this.#selection;
    const fromLabel = markupOfLabel(sel);
    if (fromLabel !== null) return fromLabel;
    const it = sel ? this.getItem(sel) : undefined;
    return it?.t === 'area' && it.status !== 'fail' ? markupOf(it) : null;
  }
  /**
   * Choose the markup the next work area goes into (and leave Failing). Nothing on the map changes:
   * the selection is let go and the markup's areas light up for a moment, so it is plain which it is.
   */
  setMarkup(n: number): void {
    const k = this.markupCount;
    this.#markup = Math.max(1, Math.min(k + 1, Math.round(n)));
    this.#areaFail = false;
    this.#areaColor = this.markupColour(this.#markup);
    this.select(null);
    this.#flashIds(this.doc.items.filter((x) => x.t === 'area' && x.status !== 'fail' && markupOf(x) === this.#markup).map((x) => x.id));
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /**
   * Begin the next markup: the next work area goes into a new one after the map's last, in the next
   * colour of the palette no markup has yet (green, yellow, blue …). Returns its number.
   */
  newMarkup(): number {
    const k = this.markupCount;
    if (this.#markup <= k) {
      this.#markup = k + 1;
      this.#areaColor = this.#nextFreeColour();
    }
    this.#areaFail = false;
    this.select(null);
    this.#events.selection(this.#selection, this.#vertexSel);
    return this.#markup;
  }
  /**
   * Move a work area into another markup (k + 1: a new one), deliberately: it takes that markup's
   * colour, yields to the areas of every other markup and joins the areas of its new one it overlaps.
   */
  moveMarkup(id: string, n: number): void {
    const it = this.getItem(id);
    if (!it || it.t !== 'area') return;
    const k = this.markupCount;
    n = Math.max(1, Math.min(k + 1, Math.round(n)));
    if (it.status !== 'fail' && markupOf(it) === n) return;
    const colour = n <= k ? this.markupColour(n) : this.#nextFreeColour();
    const moved = updateItem<WorkArea>(this.doc, id, (a) => {
      const { status: _s, color: _c, markup: _m, ...rest } = a;
      return { ...rest, ...(colour !== AREA_COLOR_KEYS[0] ? { color: colour } : {}), ...(n !== 1 ? { markup: n } : {}) };
    });
    const placed = this.#place(moved, id);
    const first = placed.ids[0];
    this.commit(placed.doc);
    if (!first) {
      this.#setSelection(null, null);
      this.#events.notice(`Markup ${n} already covers that area`);
      return;
    }
    const now = this.getItem(first);
    if (now?.t === 'area') this.#markup = markupOf(now);
    this.#areaColor = colour;
    this.#areaFail = false;
    this.select(first);
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** The first colour of the palette no markup has yet. */
  #nextFreeColour(): string {
    const k = this.markupCount;
    const used = new Set(Array.from({ length: k }, (_, i) => markupColor(this.doc.items, i + 1) ?? AREA_COLOR_KEYS[0]));
    return AREA_COLOR_KEYS.find((c) => !used.has(c)) ?? AREA_COLOR_KEYS[k % AREA_COLOR_KEYS.length]!;
  }
  /** A markup's label's words (null: automatic). */
  markupText(n: number): string | null {
    return markupLabel(this.doc.soils, n).text;
  }
  /** Set a markup's label's words (empty: automatic again). */
  setMarkupText(n: number, text: string): void {
    const t = text.trim().slice(0, 80);
    // (a label taken off the map comes back with its words)
    const { hidden, ...cur } = markupLabel(this.doc.soils, n);
    if ((cur.text ?? '') === t && !hidden) return;
    this.commit({ ...this.doc, soils: withMarkupLabel(this.doc.soils, n, { ...cur, text: t || null }) });
  }
  /**
   * A label's own words as runs — a markup's or the failing areas' label, a test's or a shot's note, a
   * callout's own words, a note — what the side panel writes in (plain words are one run).
   */
  labelRuns(id: string): TextRun[] {
    const runs = (rich: TextRun[] | undefined, text: string | null | undefined): TextRun[] => rich ?? (text ? [{ t: text }] : []);
    if (isSharedLabel(id)) {
      const l = this.#sharedLabel(this.doc, id);
      return runs(l.rich, l.text);
    }
    const it = this.getItem(id);
    if (it?.t === 'test' || it?.t === 'elev') return runs(it.rich, it.note);
    if (it?.t === 'callout') return runs(it.rich, it.custom);
    if (it?.t === 'note') return runs(it.rich, it.text);
    return [];
  }
  /**
   * A label's own words set from runs, live: every edit under one `key` (a panel's time open on it)
   * one undo step. Trimmed and cut as the plain words are; the runs kept only when a stretch has a
   * look of its own. A label taken off the map comes back with its words.
   */
  setLabelRuns(id: string, runs: readonly TextRun[], key: string): void {
    const tidy = (max: number) => {
      const rich = tidyRuns(runs, max);
      return { text: rich ? plainOf(rich) : plainOf(runs).trim().slice(0, max), rich };
    };
    const put = <T extends { rich?: TextRun[] }>(o: T, rich: TextRun[] | null): T => {
      const { rich: _r, ...rest } = o;
      return (rich ? { ...rest, rich } : rest) as T;
    };
    let next: MapDoc;
    if (isSharedLabel(id)) {
      const { text, rich } = tidy(80);
      const { hidden: _h, ...cur } = this.#sharedLabel(this.doc, id);
      next = this.#withShared(this.doc, id, put({ ...cur, text: text || null }, rich));
    } else {
      const it = this.getItem(id);
      if (!it) return;
      if (it.t === 'test') {
        const { text, rich } = tidy(120);
        next = updateItem<TestPoint>(this.doc, id, (x) => put({ ...x, note: text }, rich));
      } else if (it.t === 'elev') {
        const { text, rich } = tidy(ELEV_NOTE_MAX);
        next = updateItem<Elevation>(this.doc, id, (x) => {
          const { note: _n, ...rest } = put(x, rich);
          return text ? { ...rest, note: text } : rest;
        });
      } else if (it.t === 'callout') {
        const { text, rich } = tidy(240);
        next = updateItem<Callout>(this.doc, id, (x) => put({ ...x, template: 'custom', custom: text }, rich));
      } else if (it.t === 'note') {
        const { text, rich } = tidy(240);
        next = updateItem<Note>(this.doc, id, (x) => put({ ...x, text }, rich));
      } else return;
    }
    this.commitCoalesced(next, key);
  }
  /** A label's box as laid out by hand (none: as its words make it). */
  labelLook(id: string): BoxLook | undefined {
    if (isSharedLabel(id)) return this.#sharedLabel(this.doc, id).look;
    const it = this.getItem(id);
    return it && 'look' in it ? it.look : undefined;
  }
  /** A label's box laid out by hand (`look` merged in; a key set to undefined goes): its size, where its lines sit, its width. */
  setLabelLook(id: string, change: Partial<Record<keyof BoxLook, BoxLook[keyof BoxLook] | undefined>>, key: string): void {
    const merge = (was: BoxLook | undefined): BoxLook | undefined => {
      const out: Record<string, unknown> = { ...(was ?? {}) };
      for (const [k, v] of Object.entries(change)) {
        if (v === undefined || (k === 'size' && v === 1)) delete out[k];
        else out[k] = v;
      }
      return Object.keys(out).length ? (out as BoxLook) : undefined;
    };
    const put = <T extends { look?: BoxLook }>(o: T): T => {
      const { look: was, ...rest } = o;
      const look = merge(was);
      return (look ? { ...rest, look } : rest) as T;
    };
    let next: MapDoc;
    if (isSharedLabel(id)) next = this.#withShared(this.doc, id, put(this.#sharedLabel(this.doc, id)));
    else {
      const it = this.getItem(id);
      if (!it || !('look' in it || it.t === 'test' || it.t === 'elev' || it.t === 'callout' || it.t === 'note')) return;
      next = updateItem(this.doc, id, (x) => put(x as Item & { look?: BoxLook }) as Item);
    }
    this.commitCoalesced(next, key);
  }
  /** Some marks glowing for a moment (a pour or markup just chosen). */
  #flashIds(ids: string[]): void {
    this.#flash = ids.length ? { ids, t0: performance.now() } : null;
    this.#dirty.overlay = true;
    this.#schedule();
  }

  get view(): View {
    return this.#gesture ?? this.#view;
  }
  get metrics(): Metrics {
    const d = this.displayDoc;
    return metrics(d.w, d.h, d.scale, d.kind);
  }
  /** The plan as drawn, back to front (its paper, its marks and labels, what is under way): the glass over it is drawn from them (ui/glass.ts). */
  get layers(): HTMLCanvasElement[] {
    return [this.#base, this.#overlay, this.#live];
  }
  get hasEngine(): boolean {
    return this.#engine !== null;
  }
  set fingerPansAfterPen(v: boolean) {
    this.#surface.opts.fingerPansAfterPen = v;
  }
  /** Plan analysis arrived: strokes now snap to the plan. */
  setEngine(engine: IntentEngine | null): void {
    this.#engine = engine;
    this.#tracer = null;
    if (import.meta.env.DEV) (globalThis as { __engine?: unknown }).__engine = engine; // dev scripts read the plan's lines through this
    if (engine && this.#drawing) engine.setLeaders(this.#drawing.leaders);
  }
  /** The plan redrawn as it was drawn (vision/drawing.ts), and whether it is shown: alone, or over the plan. */
  #drawing: Drawing | null = null;
  /** What the labels keep off besides the marks: the plan's own lettering, once it has been read. */
  #planAround: PlanAround = {};
  #showLines: 'off' | 'lines' | 'overlay' = 'off';
  #linesCache: HTMLCanvasElement | null = null;
  setDrawing(d: Drawing | null): void {
    this.#drawing = d;
    this.#linesCache = null;
    // the plan's lettering, line by line: labels placed from now on keep off it (one already placed stays)
    this.#planAround = d ? { textBoxes: d.items.flatMap((it) => (it.t === 't' && it.glyphs.length ? [textLineBox(it.glyphs)] : [])) } : {};
    this.#layout = null;
    this.#invalidate(false);
    // the plan's leaders are no line work: strokes never snap to them nor follow them (from the
    // next stroke: one under the finger keeps what it has)
    if (d && this.#engine) this.#engine.setLeaders(d.leaders);
    if (this.#showLines !== 'off') this.#invalidate(true);
  }
  get drawing(): Drawing | null {
    return this.#drawing;
  }
  get showLines(): 'off' | 'lines' | 'overlay' {
    return this.#showLines;
  }
  set showLines(v: 'off' | 'lines' | 'overlay') {
    this.#showLines = v;
    this.#linesCache = null;
    this.#invalidate(true);
  }
  /** Paint the plan's lines: alone in their own tones, or over the plan by kind (lines blue, leaders magenta, outlines orange). */
  #paintLines(ctx: CanvasRenderingContext2D, d: { w: number; h: number }): void {
    const dr = this.#drawing;
    if (!dr || this.#showLines === 'off') return;
    const overlay = this.#showLines === 'overlay';
    ctx.save();
    ctx.fillStyle = overlay ? 'rgba(255,255,255,0.72)' : '#fff';
    ctx.fillRect(0, 0, d.w, d.h);
    const colour = overlay ? (it: DItem) => (it.t !== 't' && (it.role === 'leader' || it.role === 'arrowhead') ? 'rgba(210,0,190,0.9)' : it.t === 'fill' && it.role === 'shade' ? 'transparent' : it.t === 'fill' ? 'rgba(240,120,0,0.85)' : it.t === 'arc' ? 'rgba(0,150,70,0.9)' : 'rgba(20,80,230,0.9)') : undefined;
    if (this.inMotion) {
      // while the view moves: the lines as drawn once, at the plan's own size
      if (!this.#linesCache) {
        const c = document.createElement('canvas');
        c.width = d.w;
        c.height = d.h;
        const g = c.getContext('2d')!;
        paintDrawing(g, dr, 1, colour);
        this.#linesCache = c;
      }
      ctx.drawImage(this.#linesCache, 0, 0, d.w, d.h);
    } else paintDrawing(ctx, dr, 1, colour);
    ctx.restore();
  }
  /** The plan's stationing (what its station labels say along its alignments): callouts are filled in from it. */
  #stations: PlanStations | null = null;
  setStationing(st: PlanStations | null): void {
    this.#stations = st;
  }
  /** Whether the plan's stationing is known (and has any alignment to measure along). */
  get hasStationing(): boolean {
    return !!this.#stations?.alignments.length;
  }
  /** A callout's alignment and stations as the plan gives them (null: it does not). */
  #fromPlan(id: string): Callout | null {
    const c = this.getItem(id);
    if (c?.t !== 'callout') return null;
    const next = withAutoStations({ ...c, template: c.template === 'custom' ? 'between' : c.template }, this.doc, this.#stations);
    return next.auto ? next : null;
  }
  /** Whether the plan gives the stations of a callout's marks. */
  canFillFromPlan(id: string): boolean {
    return this.#fromPlan(id) !== null;
  }
  /** Fill a callout's alignment and stations in from the plan again (after they were typed over): false when the plan does not say. */
  fillFromPlan(id: string): boolean {
    const next = this.#fromPlan(id);
    if (!next) return false;
    this.commit(updateItem<Callout>(this.doc, id, () => next));
    return true;
  }
  /** The plan lifted under the marks arrived (lift.ts; null: nothing to lift): the marks are drawn on it from now on. */
  setLift(lifted: CanvasImageSource | null): void {
    useLift(this.#image, lifted);
    this.#shapesCache = null;
    this.#invalidate(false);
  }
  /**
   * Choose a tool. It stays in hand until another is chosen by hand — Square draws square after
   * square, Callout calls out mark after mark (`keep`: the selection stays); choosing a tool by hand
   * lets the selection go.
   */
  setTool(t: ToolId, keep = false): void {
    if (t === 'test' && this.doc.kind !== 'soils') t = 'mark';
    if (t !== 'elev') this.#endElevEdit();
    if (t === this.#tool) return;
    if (t === 'mark' || t === 'test' || t === 'elev') this.#sticky = t;
    this.cancelBuilding();
    this.#tool = t;
    // cutting out of a mark goes on with whichever drawing tool is chosen: it says what shape is cut
    const cutsOn = this.#cutting !== null && (t === 'pen' || t === 'mark' || t === 'square' || t === 'circle');
    if (!keep && !cutsOn) this.select(null);
    this.#events.tool(t);
  }
  /** A tool chosen from the toolbar. Chosen again it simply stays in hand (another square, not no square): another tool, or Esc, changes it. */
  toggleTool(t: ToolId): void {
    this.setTool(t);
  }
  /** Esc: the tool in hand put away — the Pen, Shapes or Callout back to the tool in use before it (Test or Elevation stay sticky on a soils map), Test or Elevation back to Magic Mark. */
  putAwayTool(): void {
    const t = this.#tool;
    if (t !== 'mark') this.setTool(t === 'test' || t === 'elev' ? 'mark' : this.#sticky);
  }
  /** Whether a point of the plan is on the plan itself (not the backdrop round it). */
  #onPlan(ip: Vec): boolean {
    return ip.x >= 0 && ip.y >= 0 && ip.x <= this.doc.w && ip.y <= this.doc.h;
  }
  /** Material for the next highlight/callout (existing items change via their context chips). */
  setMaterial(key: string): void {
    this.#material = key;
  }
  getItem(id: string): Item | undefined {
    return this.doc.items.find((i) => i.id === id);
  }

  /* ------------------------------------------------------------ history */

  /** What `within` folds every commit into (one undo step). */
  #within: string | null = null;
  /**
   * Every change `fn` makes, one undo step with every other made under the same `key` since (a side
   * panel's controls, each applied as it is used: all of them, one step back).
   */
  within(key: string, fn: () => void): void {
    const was = this.#within;
    this.#within = key;
    try {
      fn();
    } finally {
      this.#within = was;
    }
  }
  commit(next: MapDoc): void {
    if (this.#within) return this.commitCoalesced(next, this.#within);
    this.#coalesce = null;
    const doc = this.#normalised(next);
    if (this.#history.commit(doc)) {
      this.#invalidate(true);
      this.#events.change(doc);
    }
  }
  /** Commit, folding repeated edits under the same key into one undo step. */
  commitCoalesced(next: MapDoc, key: string): void {
    const same = this.#coalesce === key;
    const doc = this.#normalised(next);
    const changed = same ? this.#history.amend(doc) : this.#history.commit(doc);
    this.#coalesce = key;
    if (changed) {
      this.#invalidate(true);
      this.#events.change(doc);
    }
  }
  replace(next: MapDoc): void {
    this.#history.reset(this.#normalised(next));
    this.#invalidate(true);
  }
  /**
   * Placement numbers stay dense (1 … k in the order made), so after a deletion they say
   * how many placements the map holds. The placement being marked follows its marks; once
   * it has none (or never had any) it is the next new one, k + 1. Markups the same.
   */
  #normalised(next: MapDoc): MapDoc {
    // words written plain over words with a look of their own: the words as written stand
    next = withRunsInStep(next);
    // a mark gone takes what was cut out of it along
    const marks = new Set(next.items.flatMap((x) => (x.t === 'hl' || x.t === 'area' ? [x.id] : [])));
    if (next.items.some((x) => x.t === 'cut' && !marks.has(x.of))) next = { ...next, items: next.items.filter((x) => x.t !== 'cut' || marks.has(x.of)) };
    // markup numbers stay dense (each label going with its markup), as placement numbers do
    const mk = normaliseMarkups(next);
    next = mk.doc;
    if (next.kind === 'soils') this.#markup = mk.map.get(this.#markup) ?? Math.min(this.#markup, mk.map.size + 1);
    // an area gone (or no longer of its label's markup or kind) takes the arrow put on it along
    const tidy = (label: SharedLabel | undefined, id: string): SharedLabel | undefined => {
      if (!label?.pins) return label;
      const own = new Set(areasOfLabel(next.items, id).map((a) => a.id));
      const kept = Object.entries(label.pins).filter(([aid]) => own.has(aid));
      if (kept.length === Object.keys(label.pins).length) return label;
      const { pins: _p, ...rest } = label;
      return kept.length ? { ...rest, pins: Object.fromEntries(kept) } : rest;
    };
    let soils = next.soils;
    for (const n of markupsIn(next.items)) {
      const was = markupLabel(soils, n);
      const now = tidy(was, markupLabelId(n))!;
      if (now !== was) soils = withMarkupLabel(soils, n, now);
    }
    const failLabel = tidy(soils.failLabel, FAIL_LABEL);
    if (failLabel !== soils.failLabel) soils = { ...soils, ...(failLabel ? { failLabel } : {}) };
    if (soils !== next.soils) next = { ...next, soils };
    const { doc, map } = normalisePlacements(next);
    this.#placement = map.get(this.#placement) ?? map.size + 1;
    // the callouts filled in from the plan follow their marks
    return refreshAutoStations(doc, this.#stations);
  }
  undo(): void {
    // while corners are being placed, undo takes back the last one
    if (this.#building) return this.undoBuildingPoint();
    this.#endSettle();
    this.#endFrame();
    const d = this.#history.undo();
    if (d) this.#afterHistory(d);
  }
  redo(): void {
    this.#endSettle();
    this.#endFrame();
    const d = this.#history.redo();
    if (d) this.#afterHistory(d);
  }
  #afterHistory(d: MapDoc): void {
    this.#coalesce = null;
    if (!d.items.some((x) => x.t === 'hl' && placementOf(x) === this.#placement)) this.#placement = placementsIn(d.items).length + 1;
    if (this.#markup > markupsIn(d.items).length + 1) this.#markup = markupsIn(d.items).length + 1;
    // a shot being typed into, undone away — or its value undone (the pad would go on from what the
    // shot no longer says): the typing is over. A shot just placed and not yet typed, something else
    // undone, is still being typed, as it was.
    if (this.#elevEditing) {
      const it = d.items.find((i) => i.id === this.#elevEditing);
      if (it?.t !== 'elev' || isElevation(it.value)) {
        this.#elevEditing = null;
        this.#elevDraft = null;
      }
    }
    // a shape's points being edited, undone back to a box or an oval: its points are put away
    if (this.#vectorEdit) {
      const it = d.items.find((i) => i.id === this.#vectorEdit);
      if (!isShaped(it) || it.shape.k !== 'path' || it.shape.form?.f !== 'poly') this.#vectorEdit = null;
    }
    if (this.#cutting && !d.items.some((i) => i.id === this.#cutting)) this.#cutting = null;
    // a selection undone away: while cutting, the mark again (cutting goes on); else nothing
    if (this.#selection && (isSharedLabel(this.#selection) ? !areasOfLabel(d.items, this.#selection).length : !d.items.some((i) => i.id === this.#selection))) this.#setSelection(this.#cutting, null);
    this.#vertexSel = null;
    this.#invalidate(true);
    this.#events.change(d);
    this.onHistory?.();
  }
  /** Called after an undo or a redo (the screen ends what it was typing: the field would no longer say what the map does). */
  onHistory: (() => void) | null = null;
  updateItem<T extends Item>(id: string, fn: (it: T) => T): void {
    this.commit(updateItem<T>(this.doc, id, fn));
  }
  patchDoc(patch: Partial<MapDoc>): void {
    this.commit({ ...this.doc, ...patch });
  }

  /* ---------------------------------------------------------- selection */

  select(id: string | null, vertex: number | null = null): void {
    if (id === this.#selection && vertex === this.#vertexSel) return;
    // typing a shot ends when the selection moves on, however it does: a slip of the finger is said,
    // a shot left untyped goes, and the other labels are free to move again
    if (this.#elevEditing && this.#elevEditing !== id) this.#endElevEdit(false);
    const prev = this.#selection;
    // a shot let go before its elevation was typed is no shot at all
    if (prev && prev !== id) this.#dropUntyped(prev);
    // letting an outline go puts its points away (Done)
    if (prev && prev !== id) this.#endPointEdit(prev);
    this.#setSelection(id, vertex);
    // a callout nobody typed into is not worth keeping — though it waits while its own mark is
    // adjusted first (a square thrown down, then resized, then its words typed)
    const waiting = this.#waitingCallout;
    this.#waitingCallout = null;
    for (const cid of new Set([prev, waiting])) {
      if (!cid || cid === id) continue;
      const it = this.getItem(cid);
      if (!it || it.t !== 'callout' || !calloutIsEmpty(it) || it.auto) continue; // (one filled in from the plan waits for its stations)
      if (id && pointsAt(it, id)) {
        this.#waitingCallout = cid;
        continue;
      }
      this.commit(removeItems(this.doc, new Set([cid])));
    }
  }
  /** An untouched callout left while its mark is adjusted: it goes when the selection moves on elsewhere. */
  #waitingCallout: string | null = null;
  #setSelection(id: string | null, vertex: number | null): void {
    if (id && id !== this.#selection) this.#glowT0 = performance.now(); // the glow blooms round what was just chosen
    this.#selection = id;
    this.#vertexSel = vertex;
    if (id !== this.#editing) this.#editing = null;
    if (id !== this.#vectorEdit) this.#vectorEdit = null;
    if (id !== this.#pointing) this.#pointing = null;
    // cutting goes on while the mark or one of its cuts is in hand
    if (id !== this.#cutting && !this.#cutOfCutting(id)) this.#cutting = null;
    // selecting a mark (or its callout) makes its pour the one being marked; a work area (or a markup's label), its markup
    const hl = this.selectedMark;
    if (hl) this.#placement = placementOf(hl);
    const mk = this.selectionMarkup;
    if (mk !== null && this.doc.kind === 'soils') {
      this.#markup = mk;
      this.#areaColor = this.markupColour(mk);
      this.#areaFail = false;
    }
    if (this.#elevEditing && id !== this.#elevEditing) {
      this.#elevEditing = null;
      this.#elevDraft = null;
      this.#layout = null;
    }
    this.#coalesce = null;
    this.#dirty.overlay = true;
    this.#schedule();
    this.#events.selection(id, vertex);
  }
  /** Delete the selection; a mark (or work area) takes the arrows pointing at it along, and a callout that pointed at nothing else. */
  deleteSelection(): void {
    const id = this.#selection;
    if (!id) return;
    if (isSharedLabel(id)) {
      // a markup's label (or the failing areas') taken off the map, with its arrows: its areas stay,
      // its words are kept, and giving it words again (Label on an area's bar) brings it back
      const label = this.#sharedLabel(this.doc, id);
      this.#setSelection(null, null);
      this.commit(this.#withShared(this.doc, id, { ...label, hidden: true }));
      return;
    }
    const it = this.getItem(id);
    if (!it) return;
    if (it.t === 'cut') {
      // a cut taken away: the mark it was cut out of is in hand again (cutting goes on)
      this.#setSelection(it.of, null);
      this.commit(removeItems(this.doc, new Set([id])));
      return;
    }
    this.#setSelection(null, null);
    this.commit(it.t === 'hl' || it.t === 'area' ? withoutMarks(this.doc, new Set([id])).doc : removeItems(this.doc, new Set([id])));
  }
  /** The material of the selected mark or callout (a callout shares its mark's). */
  get selectionMaterial(): string | null {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (!it) return null;
    if (it.t === 'hl') return it.material;
    if (it.t === 'callout') return this.selectedMark?.material ?? it.material;
    return null;
  }
  /**
   * Choose a material: for the next mark, and for the selection. Marks joined by a callout are
   * one statement — "FTB placed between …" — so one material: the selected mark (or callout),
   * the callouts pointing at it and every mark those point at change together.
   */
  applyMaterial(key: string): void {
    this.#material = key;
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (!it || (it.t !== 'hl' && it.t !== 'callout')) return;
    const marks = new Set<string>();
    const callouts = new Set<string>();
    const queue: Item[] = [it];
    while (queue.length) {
      const x = queue.pop()!;
      if (x.t === 'hl' && !marks.has(x.id)) {
        marks.add(x.id);
        queue.push(...calloutsAt(this.doc.items, x.id));
      } else if (x.t === 'callout' && !callouts.has(x.id)) {
        callouts.add(x.id);
        for (const id of markIdsOf(x)) {
          const hl = this.getItem(id);
          if (hl) queue.push(hl);
        }
      }
    }
    if (!this.doc.items.some((x) => (x.t === 'hl' && marks.has(x.id) && x.material !== key) || (x.t === 'callout' && callouts.has(x.id) && x.material !== key))) return;
    let doc: MapDoc = { ...this.doc, items: this.doc.items.map((x) => (x.t === 'hl' && marks.has(x.id) ? { ...x, material: key } : x.t === 'callout' && callouts.has(x.id) ? recolourCallout(x, key) : x)) };
    // in its new material a mark joins the marks of its placement it overlaps
    let selected = it.id;
    for (const id of marks) {
      if (!doc.items.some((x) => x.id === id)) continue; // taken into a mark placed before it
      const placed = this.#place(doc, id);
      doc = placed.doc;
      if (id === it.id && placed.ids[0]) selected = placed.ids[0];
    }
    if (!doc.items.some((x) => x.id === selected)) selected = doc.items.find((x) => x.t === 'hl' && marks.has(x.id))?.id ?? it.id;
    this.commit(doc);
    if (selected !== it.id) this.select(selected);
  }
  /** Edit a callout's text fields; keystrokes into one field are one undo step. */
  editCallout(id: string, patch: Partial<Pick<Callout, 'template' | 'alignment' | 'from' | 'to' | 'custom' | 'materialName' | 'ranges'>>, field: string): void {
    this.commitCoalesced(
      updateItem<Callout>(this.doc, id, (c) => {
        const next = { ...c, ...patch };
        if (next.ranges && !next.ranges.length) delete next.ranges;
        // stations typed over are the inspector's own from now on
        const typed = (['template', 'alignment', 'from', 'to', 'ranges'] as const).some((k) => k in patch && JSON.stringify(patch[k]) !== JSON.stringify(c[k]));
        if (typed) delete next.auto;
        return next;
      }),
      `callout:${id}:${field}`,
    );
  }
  /**
   * The callout being typed into, as its in-place editor shows it (null when none is): the
   * plan lays its box out from these lines, so the editor and the box are one size. True when
   * the box may have changed.
   */
  setCalloutDraft(draft: CalloutDraft | null): boolean {
    const a = this.#calloutDraft;
    if (a === draft || (a && draft && a.id === draft.id && JSON.stringify(a.lines) === JSON.stringify(draft.lines))) return false;
    this.#calloutDraft = draft;
    this.#layout = null;
    this.#invalidate(false);
    return true;
  }
  /** Bring the plan close enough that text of `size` image px reads at `minPx` on screen, keeping `around` (screen px) where it is. */
  ensureReadable(size: number, minPx: number, around: Vec): void {
    const now = size * this.view.scale;
    if (now >= minPx) return;
    this.#setView(zoomAround(this.#view, minPx / now, around, this.#limits()));
  }
  /** The callout box in the hand, and whether it is over a place where it may not rest. */
  get carrying(): { id: string; adrift: boolean } | null {
    return this.#carried && !this.#carried.released ? { id: this.#carried.id, adrift: this.#carried.adrift } : null;
  }
  /** Which edge of a callout box its leaders leave from ('top' only when every one does). */
  calloutSide(id: string): 'top' | 'bottom' | null {
    const leaders = this.layout().callouts.get(id)?.leaders;
    if (!leaders?.length) return null;
    return leaders.every((l) => l.side === 'top') ? 'top' : 'bottom';
  }
  /**
   * What the callouts' leaders touch that they never should — a mark, another callout or a note,
   * another leader — in words; empty when all is well. (Tests and dev scripts hold the router to it.)
   */
  leaderTouches(): string[] {
    const d = this.displayDoc;
    const l = this.layout();
    const marks = calloutContext(d, this.metrics).space.marks;
    const boxes: [string, Rect][] = [...[...l.callouts].map(([id, b]): [string, Rect] => [id, b.rect]), ...[...l.notes].map(([id, b]): [string, Rect] => [id, b.rect])];
    const out = new Set<string>();
    const all = [...l.callouts];
    all.forEach(([id, b], n) =>
      b.leaders.forEach((r, k) => {
        const name = `${b.lines[0] ?? id} [${k}]`;
        if (!r.clear) out.add(`${name} found no clear way`);
        for (let i = 0; i + 1 < r.pts.length; i++) {
          const a = r.pts[i]!;
          const last = i + 2 === r.pts.length;
          const q = last ? { x: r.tip.x - r.dir.x * 0.5, y: r.tip.y - r.dir.y * 0.5 } : r.pts[i + 1]!;
          for (const mk of marks) if (segmentCrossesPoly(a, q, mk)) out.add(`${name} runs through a mark`);
          for (const [oid, rect] of boxes) if (oid !== id && segmentIntersectsRect(a, q, rect)) out.add(`${name} runs under another box`);
          for (const [, ob] of all.slice(n + 1)) for (const o of ob.leaders) for (let j = 0; j + 1 < o.pts.length; j++) if (segmentsIntersect(a, q, o.pts[j]!, o.pts[j + 1]!)) out.add(`${name} crosses another callout's leader`);
        }
      }),
    );
    return [...out];
  }
  /** Every label box on the plan (tests, callouts, notes, the area label), in screen px. */
  labelRects(): Rect[] {
    const l = this.layout();
    const out: Rect[] = [];
    const push = (r: Rect | null) => {
      if (r) out.push(r);
    };
    for (const [id] of l.tests) push(this.anchorRect(id));
    for (const [id] of l.elevs) push(this.anchorRect(id, true));
    for (const [id] of l.callouts) push(this.anchorRect(id));
    for (const [id] of l.notes) push(this.anchorRect(id));
    for (const id of l.shared.keys()) push(this.anchorRect(id));
    return out;
  }
  /** Callout boxes attached to a mark, in screen px. */
  calloutRectsOf(hlId: string): Rect[] {
    const out: Rect[] = [];
    for (const it of this.displayDoc.items) if (it.t === 'callout' && pointsAt(it, hlId)) {
      const r = this.anchorRect(it.id);
      if (r) out.push(r);
    }
    return out;
  }
  /** Remove the selected vertex (polygons keep ≥ 3, lines keep ≥ 2). */
  deleteVertex(): void {
    const id = this.#selection,
      vi = this.#vertexSel;
    if (!id || vi === null) return;
    const it = this.getItem(id);
    if (!isShaped(it)) return;
    if (it.t === 'cut') {
      const f = it.shape.k === 'path' && it.shape.form?.f === 'poly' ? removePoint(it.shape.form, vi) : null;
      if (!f) return this.#events.notice('A shape keeps three points');
      this.#vertexSel = null;
      this.commit(updateItem(this.doc, id, (x) => ({ ...x, shape: keeping(it.shape, formShape(f)) }) as Item));
      this.#events.selection(this.#selection, null);
      return;
    }
    if (it.shape.k === 'path' && it.shape.form) {
      // a drawn shape keeps three points; an ellipse has none to take away
      const f = it.shape.form.f === 'poly' ? removePoint(it.shape.form, vi) : null;
      if (!f) return this.#events.notice('A shape keeps three points');
      this.#vertexSel = null;
      const placed = this.#place(updateItem(this.doc, id, (x) => ({ ...x, shape: keeping(it.shape, formShape(f)) }) as Item), id);
      this.commit(placed.doc);
      if (placed.ids[0] && placed.ids[0] !== id) this.select(placed.ids[0]);
      this.#events.selection(this.#selection, null);
      return;
    }
    const shape = toEditable(it.shape);
    const min = shape.k === 'stroke' ? 2 : 3;
    if (shape.pts.length / 2 <= min) return;
    this.#vertexSel = null;
    if (shape.k === 'path') {
      const next = removeAnchor(shape, vi);
      const placed = this.#place(updateItem(this.doc, id, (x) => ({ ...x, shape: keeping(it.shape, { k: 'path', ...next }) }) as Item), id);
      this.commit(placed.doc);
      if (placed.ids[0] && placed.ids[0] !== id) this.select(placed.ids[0]);
    } else {
      const pts = shape.pts.slice();
      pts.splice(vi * 2, 2);
      this.updateItem(id, (x) => ({ ...x, shape: { ...shape, pts } }) as Item);
    }
    this.#events.selection(id, null);
  }

  /* ------------------------------------------------------------ viewport */

  #limits(): { minScale: number; maxScale: number } {
    const fit = fitView(this.doc.w, this.doc.h, this.#screen(), FIT_PAD).scale;
    return { minScale: fit * 0.4, maxScale: MAX_SCALE };
  }
  #screen(): Rect {
    return { x: 0, y: 0, w: this.#size.w, h: this.#size.h };
  }
  #setView(v: View, motion = false, gliding = false): void {
    if (!gliding) this.#stopGlide();
    const c = constrain(v, this.doc.w, this.doc.h, this.#screen(), 120);
    this.#gesture = null;
    if (motion) this.#markMotion();
    if (viewEquals(c, this.#view)) {
      if (!motion) this.#invalidate(false);
      return;
    }
    this.#view = c;
    this.#invalidate(false);
    this.#events.viewChanged();
  }
  fit(): void {
    this.#setView(fitView(this.doc.w, this.doc.h, this.#screen(), FIT_PAD));
    this.#fitted = true;
  }
  zoomBy(f: number): void {
    this.#setView(zoomAround(this.#view, f, { x: this.#size.w / 2, y: this.#size.h / 2 }, this.#limits()));
  }
  /** Shift the view by screen px (e.g. to keep something above the keyboard). */
  nudge(dx: number, dy: number): void {
    this.#setView(pan(this.#view, dx, dy));
  }
  #panBy(dx: number, dy: number): void {
    this.#stopGlide();
    const g = this.#gesture ?? this.#view;
    this.#gesture = constrain(pan(g, dx, dy), this.doc.w, this.doc.h, this.#screen(), 120);
    this.#gestureChanged();
  }
  #onPinch(a0: Vec, b0: Vec, a1: Vec, b1: Vec): void {
    this.#stopGlide();
    const g = this.#gesture ?? this.#view;
    this.#gesture = constrain(pinchView(g, a0, b0, a1, b1, this.#limits()), this.doc.w, this.doc.h, this.#screen(), 120);
    this.#gestureChanged();
  }
  #gestureChanged(): void {
    this.#markMotion();
    this.#invalidate(false);
    this.#events.viewChanged();
  }
  #commitGesture(): void {
    if (!this.#gesture) return;
    const g = this.#gesture;
    this.#gesture = null;
    this.#motionUntil = 0;
    clearTimeout(this.#motionTimer);
    this.#setView(g);
    this.#invalidate(false);
  }
  #markMotion(): void {
    this.#motionUntil = performance.now() + 140;
    clearTimeout(this.#motionTimer);
    this.#motionTimer = window.setTimeout(() => {
      this.#motionUntil = 0;
      this.#invalidate(false);
    }, 150);
  }
  #stopGlide(): void {
    if (!this.#glideRaf) return;
    cancelAnimationFrame(this.#glideRaf);
    this.#glideRaf = 0;
  }

  get inMotion(): boolean {
    return this.#gesture !== null || performance.now() < this.#motionUntil;
  }
  toImage(p: Vec): Vec {
    return toImage(this.view, p);
  }
  toScreen(p: Vec): Vec {
    return toScreen(this.view, p);
  }
  /** How far a fitted curve may stray from the traced border (image px): a fraction of a pixel, never visible at any zoom. */
  get #fitTol(): number {
    return Math.max(0.25, 0.15 * this.metrics.u);
  }
  /** Corners of a border are turns of 35° within this much border length (image px). */
  get #cornerReach(): number {
    return 10 * this.metrics.u;
  }
  /** Snapping tolerance in image px: about 12 screen px at the current zoom. */
  get tolerance(): number {
    return clamp(12 / this.view.scale, 3, 60);
  }

  resize(): void {
    const r = this.stage.getBoundingClientRect();
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    if (w === this.#size.w && h === this.#size.h && dpr === this.#size.dpr && this.#fitted) return;
    this.#size = { w, h, dpr };
    for (const c of [this.#base, this.#overlay, this.#live]) {
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
      c.style.width = `${w}px`;
      c.style.height = `${h}px`;
    }
    this.#surface.relayout();
    if (!this.#fitted) this.fit();
    else this.#setView(this.#view);
    this.#invalidate(true);
  }

  /* ------------------------------------------------------------ rendering */

  #invalidate(docChanged: boolean): void {
    this.#dirty.base = true;
    this.#dirty.overlay = true;
    // corners being placed (and the loupe) ride on the live layer: they move with the plan
    if (this.#building || this.#loupe) this.#dirty.live = true;
    if (docChanged) this.#layout = this.#layout && this.#layout.doc === this.displayDoc ? this.#layout : null;
    this.#schedule();
  }
  /** Draw now what is waiting for the next frame (the zoom into a map copies the first drawing the moment there is one). */
  flush(): void {
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#render();
  }
  #schedule(): void {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#render();
    });
  }
  layout(): Layout {
    const d = this.displayDoc;
    if (this.#layout && this.#layout.doc === d) return this.#layout.layout;
    const prev = layoutRects(this.#layout?.layout ?? this.#lastLayout);
    const held = this.#carried;
    // a test's or shot's dot carried: every label holds still, only the leaders follow the dot
    const dg = this.#drag;
    // (a dot moved, or a box made narrower or wider: the other labels stay where they are — and while its frame glides)
    const frame = this.#frame && d === this.#working ? this.#frame.id : null;
    const holdAll = (!!dg?.moved && (dg.hit.part === 'marker' || dg.hit.part === 'edge')) || frame !== null;
    const layout = computeLayout(d, this.metrics, sharedMeasurer(), prev, this.#calloutDraft, held ? { id: held.id, adrift: held.adrift } : null, this.#elevDraft, this.#planAround, { holdAll, ...(frame ? { exact: frame } : {}) });
    this.#layout = { doc: d, layout };
    this.#lastLayout = layout;
    return layout;
  }
  layoutFor(doc: MapDoc): Layout {
    // what is exported is the finished callout (and shot), never the one being typed
    if (!this.#calloutDraft && !this.#elevDraft && this.#layout && this.#layout.doc === doc) return this.#layout.layout;
    return computeLayout(doc, metrics(doc.w, doc.h, doc.scale, doc.kind), sharedMeasurer(), layoutRects(this.#layout?.layout ?? this.#lastLayout), null, null, null, this.#planAround);
  }
  #applyTransform(ctx: CanvasRenderingContext2D): void {
    const { dpr } = this.#size;
    const v = this.view;
    ctx.setTransform(dpr * v.scale, 0, 0, dpr * v.scale, dpr * v.tx, dpr * v.ty);
  }
  #shapesLayer(d: MapDoc): { canvas: HTMLCanvasElement; lifted: boolean } | null {
    const key = d.items.filter((it) => it.t === 'area' || it.t === 'hl');
    if (key.length === 0) return null;
    const c = this.#shapesCache;
    if (c && c.w === d.w && c.h === d.h && c.scale === d.scale && c.key.length === key.length && c.key.every((it, i) => it === key[i])) return c;
    const k = Math.min(1, 4096 / Math.max(d.w, d.h));
    const canvas = c?.canvas ?? document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(d.w * k));
    canvas.height = Math.max(1, Math.round(d.h * k));
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.clearRect(0, 0, d.w, d.h);
    const m = metrics(d.w, d.h, d.scale, d.kind);
    // the plan lifted under the marks is part of the layer: the marks are multiplied over it here, and the layer laid over the plan as it is
    const lifted = drawLift(ctx, d, m, this.#image, false);
    drawShapes(ctx, d, m);
    this.#shapesCache = { canvas, key, w: d.w, h: d.h, scale: d.scale, k, lifted };
    return this.#shapesCache;
  }
  #backdrop(): string {
    const cs = getComputedStyle(this.stage);
    const v = cs.getPropertyValue('--bg-editor').trim();
    if (v) return v;
    const bg = cs.backgroundColor;
    return bg && !/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0\s*\)/.test(bg) && bg !== 'transparent' ? bg : '#d9dee3';
  }
  /** Called after a frame that drew anything (the glass over the plan follows it: ui/glass.ts). */
  onFrame: (() => void) | null = null;
  #render(): void {
    this.#stepCarry();
    this.#stepFrame();
    const drew = this.#dirty.base || this.#dirty.overlay || this.#dirty.live;
    const d = this.displayDoc;
    const m = this.metrics;
    if (this.#dirty.base) {
      const ctx = this.#bctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = this.#backdrop();
      ctx.fillRect(0, 0, this.#base.width, this.#base.height);
      this.#applyTransform(ctx);
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.25)';
      ctx.shadowBlur = 24 / this.#view.scale;
      ctx.shadowOffsetY = 6 / this.#view.scale;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, d.w, d.h);
      ctx.restore();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.#image, 0, 0, d.w, d.h);
      this.#paintLines(ctx, d);
      const layer = this.inMotion ? this.#shapesLayer(d) : null;
      if (layer) {
        ctx.save();
        ctx.globalCompositeOperation = layer.lifted ? 'source-over' : 'multiply';
        ctx.drawImage(layer.canvas, 0, 0, d.w, d.h);
        ctx.restore();
      } else {
        // the plan lifted under every mark first, so each reads on a shaded plan as on white paper
        drawLift(ctx, d, m, this.#image, false);
        // a mark being pulled, moved or reshaped yields to the marks of other placements as it goes
        const dg = this.#drag;
        const dragged = dg && dg.moved && (dg.hit.part === 'end' || dg.hit.part === 'shape' || dg.hit.part === 'vertex') ? d.items.find((i) => i.id === dg.hit.id) : undefined;
        if (dragged && dragged.t === 'hl') {
          drawShapes(ctx, d, m, dragged.id);
          paintOutlines(ctx, [shapeToPolygon(dragged.shape)], materialColor(dragged.material), m, BAND_ALPHA, 1, this.#cutsFor(d, placementOf(dragged), dragged.id), this.#cutsFor(d, placementOf(dragged), dragged.id, true));
        } else drawShapes(ctx, d, m);
      }
      this.#dirty.base = false;
    }
    if (this.#dirty.overlay) {
      const ctx = this.#octx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.#overlay.width, this.#overlay.height);
      this.#applyTransform(ctx);
      const sel = new Set<string>();
      if (this.#selection) sel.add(this.#selection);
      const layout = this.layout();
      const bloom = this.#bloom();
      const held = this.#carried;
      const ease = (t: number) => t * t * (3 - 2 * t);
      // (an arrowhead in the hand is ringed more strongly)
      const da = this.#drag?.arrow;
      const heldArrow = da && this.#drag ? { id: this.#drag.hit.id, arrow: da.area ?? da.callout ?? 0 } : null;
      // the selected box's grips (the side in the hand lit)
      const box = this.selectedBox();
      const gb = box && this.#gripsShown(box.rect) ? box : null;
      const grip = gb ? { rect: gb.rect, border: gb.border, ...(this.#drag?.hit.part === 'edge' && this.#drag.hit.id === gb.id ? { held: this.#drag.hit.index === 0 ? (0 as const) : (1 as const) } : {}) } : null;
      drawAnnotations(ctx, d, m, layout, { selection: sel, interactive: true, viewScale: this.view.scale, glow: bloom ?? GLOW_REST, ...(held ? { carried: { id: held.id, lift: ease(this.#liftT), adrift: ease(this.#adriftT), dpr: this.#size.dpr } } : {}), ...(heldArrow ? { heldArrow } : {}), ...(grip ? { grip } : {}) });
      const selItem = this.#selection ? d.items.find((i) => i.id === this.#selection) : undefined;
      // cutting out of a mark: every shape cut out of it shows as itself, dashed (the ones that overlap cut as one)
      if (this.#cutting) drawCutOutlines(ctx, cutsOf(d.items, this.#cutting).map((c) => shapeToPolygon(c.shape)), this.view.scale);
      if (isShaped(selItem) && !this.inMotion && (selItem.t === 'cut' || !this.#cutting)) {
        const color = selItem.t === 'hl' ? materialColor(selItem.material) : selItem.t === 'cut' ? INK : areaStroke(selItem);
        // a mark's end tabs, unless its anchor points are being edited (then only those show).
        // While an end is being pulled the tabs are the mark's own ends as they were, with the
        // pulled one riding on the edge as it is now: a passing shape never grows a tab of its own
        const drawn = selItem.shape.k === 'path' ? selItem.shape.form : undefined;
        if (drawn) {
          // a drawn shape is handled by its own points, always there while it is selected
          const dg = this.#drag;
          const inHand = dg && dg.moved && dg.hit.id === selItem.id && dg.hit.part === 'fpoint' ? (dg.hit.index ?? null) : null;
          const points = this.#pointsMode(selItem);
          const roundings = dg && dg.moved && dg.hit.part !== 'frad' ? [] : this.#roundingsOf(selItem, drawn).map((h) => h.at);
          drawFormHandles(ctx, drawn, color, this.view.scale, inHand ?? (drawn.f === 'poly' && points ? this.#vertexSel : null), !(dg && dg.moved), points || (dg && dg.moved && dg.hit.part !== 'frot') ? null : this.#knobOf(drawn), points, roundings);
        } else if (selItem.t === 'hl' && this.#editing !== selItem.id) {
          const dg = this.#drag;
          const pulling = dg && dg.hit.part === 'end' && dg.hit.id === selItem.id ? dg : null;
          const edge = pulling?.end?.pull.current ?? null;
          const was = pulling ? pulling.origin.items.find((i) => i.id === selItem.id) : undefined;
          const shape = was && was.t === 'hl' ? was.shape : selItem.shape;
          drawEndHandles(ctx, this.#endsOf(shape, selItem), color, m, this.view.scale, pulling ? (pulling.hit.index ?? null) : null, edge);
        }
        if (this.#editing === selItem.id && selItem.t !== 'cut') drawHandles(ctx, selItem.shape, color, this.view.scale, this.#vertexSel);
      }
      // choosing a callout's arrows: the marks it points at are lit
      const pointing = this.#pointing ? d.items.find((i) => i.id === this.#pointing) : undefined;
      if (pointing?.t === 'callout') {
        for (const id of markIdsOf(pointing)) {
          const hl = d.items.find((i) => i.id === id);
          if (hl?.t === 'hl' || hl?.t === 'area') drawShapeGlow(ctx, hl.shape, m, this.view.scale, hl.t === 'area');
        }
      }
      if (this.#snapHint) drawSnapRing(ctx, this.#snapHint.p, this.view.scale, this.#snapHint.kind);
      // an arrow shaken off its mark draws itself back into its box, and is gone
      const fx = this.#detachFx;
      let detaching = false;
      if (fx) {
        const t = Math.min(1, (performance.now() - fx.t0) / Editor.#DETACH_MS);
        if (t < 1) {
          const e = 1 - (1 - t) ** 3;
          paintArrowGhost(ctx, fx.pts, fx.color, m, 1 - e, t < 0.5 ? 1 : 1 - (t - 0.5) / 0.5);
          detaching = true;
        } else this.#detachFx = null;
      }
      let animating = bloom !== null || this.#carryMoving() || detaching || (this.#frame !== null && !this.#frame.still);
      const flash = this.#flash;
      if (flash) {
        const p = Math.min(1, (performance.now() - flash.t0) / Editor.#FLASH_MS);
        if (p < 1) {
          ctx.save();
          ctx.globalAlpha = 1 - p * p;
          for (const fid of flash.ids) {
            const it = d.items.find((i) => i.id === fid);
            if (it && (it.t === 'hl' || it.t === 'area')) drawShapeGlow(ctx, it.shape, m, this.view.scale, it.t === 'area');
          }
          ctx.restore();
          animating = true;
        } else this.#flash = null;
      }
      this.#dirty.overlay = animating;
      if (animating) this.#schedule();
    }
    if (this.#dirty.live) {
      const ctx = this.#lctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.#live.width, this.#live.height);
      this.#applyTransform(ctx);
      this.#dirty.live = false;
      this.#drawLive(ctx, m); // may set the flag again to keep a fade going
      this.#drawLoupe(d);
    }
    if (drew) this.onFrame?.();
  }
  /**
   * The loupe: the plan around the point being placed, magnified in a circle beside the finger
   * (never under it), with the shapes as thin outlines and a crosshair on the exact point — the
   * fingertip hides what matters most, so it is shown here instead (Vogel & Baudisch's Shift, 2007).
   */
  #drawLoupe(d: MapDoc): void {
    const el = this.#loupeEl;
    const lp = this.#loupe;
    if (!lp) {
      if (!el.hidden) el.hidden = true;
      this.#loupeSide = null;
      return;
    }
    const D = 136; // CSS px across (≈ 26 mm)
    const { dpr } = this.#size;
    const px = Math.round(D * dpr);
    if (el.width !== px) {
      el.width = el.height = px;
      el.style.width = el.style.height = `${D}px`;
    }
    // above the finger, else beside it; always wholly on screen, and steady (see #loupeSide)
    const W = this.#size.w,
      H = this.#size.h;
    const reach = 48 + D / 2; // from the finger to the loupe's middle: clear of the fingertip and the nail
    const offsetOf = (side: 'above' | 'left' | 'right'): Vec => (side === 'above' ? { x: 0, y: -reach } : { x: side === 'left' ? -reach : reach, y: 0 });
    const room = (side: 'above' | 'left' | 'right', spare: number): boolean => {
      const o = offsetOf(side);
      if (side === 'above') return lp.finger.y + o.y - D / 2 >= 8 + spare;
      return side === 'left' ? lp.finger.x + o.x - D / 2 >= 8 + spare : lp.finger.x + o.x + D / 2 <= W - 8 - spare;
    };
    const now = performance.now();
    let ls = this.#loupeSide;
    // a side of its own: to the left of the finger where it can (a right hand is below and to the right)
    const beside = (): 'left' | 'right' => (room('left', 0) ? 'left' : 'right');
    let side = ls?.side ?? (room('above', 0) ? 'above' : beside());
    if (side === 'above' && !room('above', 0)) side = beside();
    else if (side !== 'above' && room('above', 40)) side = 'above';
    else if (side !== 'above' && !room(side, 0)) side = side === 'left' ? 'right' : 'left';
    if (ls && ls.side !== side) {
      // leaving one side for another: from wherever it stands now
      const was = this.#loupeOffset(ls, offsetOf, now);
      ls = { side, from: was, t0: now };
    } else if (!ls) ls = { side, from: null, t0: now };
    this.#loupeSide = ls;
    const off = this.#loupeOffset(ls, offsetOf, now);
    if (ls.from) {
      // still gliding: the next frame carries it on
      this.#dirty.live = true;
      this.#schedule();
    }
    const cx = clamp(lp.finger.x + off.x, D / 2 + 8, W - D / 2 - 8);
    const cy = clamp(lp.finger.y + off.y, D / 2 + 8, H - D / 2 - 8);
    el.style.translate = `${Math.round(cx - D / 2)}px ${Math.round(cy - D / 2)}px`;
    el.classList.toggle('snapped', lp.snapped);
    el.classList.toggle('fine', lp.fine);
    if (el.hidden) el.hidden = false;
    const ctx = el.getContext('2d')!;
    const z = clamp(this.view.scale * 3, this.view.scale * 1.5, 8); // image px → CSS px in the loupe
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, px, px);
    ctx.setTransform(dpr * z, 0, 0, dpr * z, px / 2 - dpr * z * lp.at.x, px / 2 - dpr * z * lp.at.y);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.#image, 0, 0, d.w, d.h);
    // every mark as a thin outline, the one in the hand as it is now
    ctx.lineJoin = 'round';
    for (const it of d.items) {
      if (it.t !== 'hl' && it.t !== 'area') continue;
      const poly = this.#polyOf(it.shape);
      ctx.beginPath();
      for (let i = 0; i < poly.length; i += 2) (i ? ctx.lineTo(poly[i]!, poly[i + 1]!) : ctx.moveTo(poly[i]!, poly[i + 1]!));
      ctx.closePath();
      ctx.strokeStyle = it.t === 'hl' ? materialColor(it.material) : areaStroke(it);
      ctx.globalAlpha = it.id === this.#drag?.hit.id ? 1 : 0.6;
      ctx.lineWidth = 1.6 / z;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // the shape being placed, as it will be
    const pd = this.#penDrawing();
    if (pd) {
      ctx.beginPath();
      if (pd.form) {
        const path = formPath(pd.form);
        pathOn(ctx, path.pts, path.h);
      } else pd.pts.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
      ctx.strokeStyle = this.doc.kind === 'soils' ? this.#nextAreaColors().stroke : materialColor(this.#material);
      ctx.lineWidth = 1.6 / z;
      ctx.stroke();
    }
    // the crosshair on the exact point: a gap in the middle so the point itself shows
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const c = D / 2;
    ctx.lineCap = 'round';
    for (const [w, col] of [[3.2, 'rgba(255,255,255,0.9)'], [1.4, lp.snapped ? '#1863DC' : '#111']] as const) {
      ctx.beginPath();
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        ctx.moveTo(c + dx! * 5, c + dy! * 5);
        ctx.lineTo(c + dx! * 16, c + dy! * 16);
      }
      ctx.strokeStyle = col;
      ctx.lineWidth = w;
      ctx.stroke();
    }
    if (lp.snapped) {
      ctx.beginPath();
      ctx.arc(c, c, 8, 0, Math.PI * 2);
      ctx.strokeStyle = '#1863DC';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }
  /** Where the loupe stands off the finger now: its side's place, or on the way there (an ease out, no overshoot). */
  #loupeOffset(ls: LoupeSide, offsetOf: (side: 'above' | 'left' | 'right') => Vec, now: number): Vec {
    const to = offsetOf(ls.side);
    if (!ls.from) return to;
    const p = matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : Math.min(1, (now - ls.t0) / 220);
    if (p >= 1) {
      ls.from = null;
      return to;
    }
    const e = 1 - (1 - p) ** 3;
    return { x: ls.from.x + (to.x - ls.from.x) * e, y: ls.from.y + (to.y - ls.from.y) * e };
  }
  /** The selection glow while it comes up: a short, plain ease — no bounce. Null once at rest (or where motion is unwanted). */
  #bloom(): GlowState | null {
    const p = (performance.now() - this.#glowT0) / Editor.#BLOOM_MS;
    if (!(p < 1) || !this.#selection || matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const out = 1 - Math.pow(1 - p, 3);
    return { alpha: out, grow: 0.7 + 0.3 * out };
  }
  /** What the live layer showed last, so a changed reading can fade over: polygons, or the glow trail. */
  #liveShown: { polys: number[][]; trail: number[] | null; version: number } | null = null;
  #liveFrom: { polys: number[][]; trail: number[] | null; t0: number } | null = null;
  static readonly #FADE_MS = 200;

  #drawLive(ctx: CanvasRenderingContext2D, m: Metrics): void {
    const soilsMap = this.doc.kind === 'soils';
    const areaInk = this.#nextAreaColors();
    const tint = soilsMap ? areaInk.stroke : materialColor(this.#material);
    const pf = this.#placingForm();
    if (pf && this.#cutting) {
      // a square or circle being cut out: the plan under it through a veil, its edge dashed
      drawCutPreview(ctx, [formPolygon(pf, 3)], this.view.scale);
    } else if (pf) {
      const poly = formPolygon(pf, 3);
      liftUnder(ctx, this.doc, m, this.#image, [{ poly, band: soilsMap ? m.areaBorder : m.bandEdge }], false);
      if (soilsMap) {
        ctx.save();
        ctx.beginPath();
        for (let i = 0; i < poly.length; i += 2) (i ? ctx.lineTo(poly[i]!, poly[i + 1]!) : ctx.moveTo(poly[i]!, poly[i + 1]!));
        ctx.closePath();
        ctx.globalAlpha = AREA_FILL_ALPHA;
        ctx.fillStyle = areaInk.fill;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = areaInk.stroke;
        ctx.lineWidth = m.areaBorder;
        ctx.stroke();
        ctx.restore();
      } else paintOutlines(ctx, [poly], tint, m, BAND_ALPHA, 1, this.#cutsFor(this.doc, this.#placement, null), this.#cutsFor(this.doc, this.#placement, null, true));
      drawFormHandles(ctx, pf, tint, this.view.scale, null, false);
    }
    const pd = this.#penDrawing();
    if (pd) drawPen(ctx, pd, this.#cutting ? INK : tint, soilsMap ? areaInk.fill : tint, this.view.scale);
    const stroke = this.#stroke;
    if (!stroke) {
      this.#liveShown = null;
      this.#liveFrom = null;
      return;
    }
    // Interpret the stroke so far (throttled to the frame rate by the dirty flag).
    this.#intent = this.#analyze(stroke, false);
    const label = this.#intent.kind === 'none' ? null : this.#intent.label;
    if (label !== this.#intentLabel) {
      this.#intentLabel = label;
      this.#events.intent(label);
    }
    ctx.save();
    const it = this.#intent;
    const soils = this.doc.kind === 'soils';
    const color = soils ? areaInk.stroke : materialColor(this.#material);
    // the mark being drawn yields to the marks of other placements: nothing of its body shows inside
    // them, and its band keeps out of lower placements (the seam is one band wide, as when committed)
    const cuts = soils ? [] : this.#cutsFor(this.doc, this.#placement, null);
    const bandCuts = soils ? [] : this.#cutsFor(this.doc, this.#placement, null, true);
    // the plan lifted under the mark as it is drawn, as it will be under the finished mark
    const band = soils ? m.areaBorder : m.bandEdge;
    const under =
      it.kind === 'band' ? (it.polys ?? [it.pts]) : it.kind === 'trace' || it.kind === 'free-stroke' ? [bandOutline(it.pts, it.width)] : it.kind === 'shape' ? [formPolygon(it.form, 3)] : it.kind === 'region' || it.kind === 'free-poly' ? [it.pts] : [];
    if (this.#cutting && it.kind !== 'none') {
      // what will be cut out, as it is read: the plan under it through a veil, its edge dashed
      drawCutPreview(ctx, under.filter((poly) => poly.length >= 6), this.view.scale);
      if (it.kind === 'shape' && this.#pins.length) drawRests(ctx, this.#pins, color, this.view.scale);
      drawGlowTip(ctx, { x: stroke[stroke.length - 2]!, y: stroke[stroke.length - 1]! }, color, this.view.scale);
      ctx.restore();
      return;
    }
    liftUnder(ctx, this.doc, m, this.#image, under.map((poly) => ({ poly, band })), false);
    if (it.kind === 'band') {
      // The mark itself, live. The finger's recent route glows while the plan is still being
      // read; when the reading changes shape, the old one fades as the new one appears.
      // what has been read so far always shows; the glowing trail is added while the newest
      // stretch has no reading yet, and a small glow rides under the finger throughout
      const thinking = it.thinking ?? false;
      const polys = it.polys ?? [it.pts];
      const trail = thinking ? recentRoute(stroke, 8 * this.tolerance) : null;
      const version = it.version ?? 0;
      const now = performance.now();
      const prev = this.#liveShown;
      if (prev && prev.version !== version) this.#liveFrom = { polys: prev.polys, trail: prev.trail, t0: now };
      this.#liveShown = { polys, trail, version };
      const from = this.#liveFrom;
      let p = 1;
      if (from) {
        p = Math.min(1, (now - from.t0) / Editor.#FADE_MS);
        p = p * p * (3 - 2 * p); // ease
        if (p < 1) {
          // the trail only fades out once a reading has taken its place; it never fades against itself
          if (from.trail && !trail) drawGlowTrail(ctx, from.trail, color, this.view.scale, 1 - p);
          if (from.polys.length) paintOutlines(ctx, from.polys, color, m, BAND_ALPHA * (1 - p), 1 - p, cuts, bandCuts);
          this.#dirty.live = true; // keep animating
          this.#schedule();
        } else this.#liveFrom = null;
      }
      if (polys.length) paintOutlines(ctx, polys, color, m, BAND_ALPHA * p, p, cuts, bandCuts);
      if (trail) drawGlowTrail(ctx, trail, color, this.view.scale);
      else drawGlowTip(ctx, { x: stroke[stroke.length - 2]!, y: stroke[stroke.length - 1]! }, color, this.view.scale);
    } else if (it.kind === 'none') {
      drawGlowTrail(ctx, recentRoute(stroke, 8 * this.tolerance), color, this.view.scale);
    } else if (it.kind === 'trace' || it.kind === 'free-stroke') {
      paintBand(ctx, it.pts, it.width, color, m);
      erasePolygons(ctx, cuts);
    } else if (it.kind === 'shape') {
      // the shape itself, perfect already, with its corners where they will land
      const poly = formPolygon(it.form, 3);
      if (soils) {
        ctx.beginPath();
        for (let i = 0; i < poly.length; i += 2) (i ? ctx.lineTo(poly[i]!, poly[i + 1]!) : ctx.moveTo(poly[i]!, poly[i + 1]!));
        ctx.closePath();
        ctx.globalAlpha = AREA_FILL_ALPHA;
        ctx.fillStyle = areaInk.fill;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = color;
        ctx.lineWidth = m.areaBorder;
        ctx.lineJoin = 'round';
        ctx.stroke();
      } else paintOutlines(ctx, [poly], color, m, BAND_ALPHA, 1, cuts, bandCuts);
      drawFormHandles(ctx, it.form, color, this.view.scale, null, false);
      if (this.#pins.length) drawRests(ctx, this.#pins, color, this.view.scale);
      drawGlowTip(ctx, { x: stroke[stroke.length - 2]!, y: stroke[stroke.length - 1]! }, color, this.view.scale);
    } else if (it.kind === 'region' || it.kind === 'free-poly') {
      ctx.beginPath();
      ctx.moveTo(it.pts[0]!, it.pts[1]!);
      for (let i = 2; i < it.pts.length; i += 2) ctx.lineTo(it.pts[i]!, it.pts[i + 1]!);
      ctx.closePath();
      ctx.globalAlpha = soils ? AREA_FILL_ALPHA : BAND_ALPHA;
      ctx.fillStyle = soils ? areaInk.fill : color;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineWidth = m.areaBorder;
      ctx.lineJoin = 'round';
      ctx.stroke();
      erasePolygons(ctx, cuts);
    }
    ctx.restore();
  }
  /**
   * The borders of the marks a mark of `placement` yields to: every mark of another placement
   * (but `except`) — or, `lowerOnly`, of an earlier placement (the ones its colour band keeps
   * out of, so a seam is one band wide).
   */
  #cutsFor(doc: MapDoc, placement: number, except: string | null, lowerOnly = false): number[][] {
    const out: number[][] = [];
    for (const x of doc.items) {
      if (x.t !== 'hl' || x.id === except) continue;
      const p = placementOf(x);
      if (p === placement || (lowerOnly && p > placement)) continue;
      out.push(this.#polyOf(x.shape));
    }
    return out;
  }
  #polyOf(shape: Shape): number[] {
    let p = this.#polyCache.get(shape);
    if (!p) {
      p = shapeToPolygon(shape);
      this.#polyCache.set(shape, p);
    }
    return p;
  }
  /**
   * What the stroke means so far. Open strokes come from the real-time tracer
   * (midline between lines, the line itself, or the finger's own path); closed
   * loops and scribbles are regions, read from the plan on every frame.
   */
  #analyze(stroke: number[], final: boolean): Intent {
    const tol = this.tolerance;
    const width = this.metrics.bandDefault;
    const live = this.#tracer;
    const pts = dedupe(stroke, 1);
    if (polylineLength(pts) < 1.5 * tol) return { kind: 'none' };
    const form = strokeForm(pts, tol);
    // a loop is a shape: what was drawn, made perfect — its corners landed on the plan's
    if (form.closed) {
      const shape = this.#readLoop(stroke);
      if (shape) return shape;
    }
    if (!this.#engine || !live) return fallbackIntent(stroke, tol, width);
    if (form.closed || form.scribble) return this.#engine.analyze(stroke, tol, width);
    if (final) {
      const band = live.finish();
      return { kind: 'band', pts: band.pts, path: band.path, width: band.width, label: band.label };
    }
    const r = live.update();
    const label = r.mode === 'mid' || r.mode === 'edge' ? 'Between lines' : r.mode === 'snap' ? 'Along line' : 'Free line';
    return { kind: 'band', pts: r.outline, polys: r.polys, path: r.path, width: r.width ?? width, label, version: r.version, thinking: r.thinking };
  }

  /** A loop read as what was drawn, made perfect, landed on the plan. */
  #readLoop(stroke: number[]): Intent | null {
    const reading = readSketch(stroke, { tol: this.tolerance, times: this.#times, pinned: this.#pins });
    if (!reading) return null;
    const r = this.#sketchSnap();
    const forms = { ...reading.forms };
    forms[reading.primary] = this.#snapForm(forms[reading.primary], r);
    // the square it stands for is a box (it resizes like one) while its corners are still square
    const box = asBox(forms.rect);
    if (box) forms.rect = box;
    const landed: SketchReading = { ...reading, forms };
    return { kind: 'shape', form: forms[reading.primary], reading: landed, label: READING_LABEL[reading.primary] };
  }
  /** How far a sketched corner reaches for the plan's corner (image px): a little more than a fingertip. */
  #sketchSnap(): number {
    return 2.2 * this.tolerance;
  }
  /**
   * A form's corners landed on the plan's corners beneath them (with the Snap switch on and the
   * plan read): each takes the plan's corner its two sides run along, or the line along one side.
   */
  #snapForm(f: Form, r: number): Form {
    const engine = this.#engine;
    if (!engine || !this.snapAnchors) return f;
    if (f.f === 'rect') return f;
    if (f.f === 'ellipse') {
      // a circle drawn round a circular symbol is centred on it (its size stays as drawn)
      if (f.rx !== f.ry) return f;
      const o = circleCentre(engine, { x: f.cx, y: f.cy }, f.rx);
      return o ? { ...f, cx: o.x, cy: o.y } : f;
    }
    const P = formHandles(f);
    const n = P.length;
    const pts = f.pts.slice();
    P.forEach((p, i) => {
      if (f.round[i]) return;
      const a = P[(i - 1 + n) % n]!,
        b = P[(i + 1) % n]!;
      const d1 = unitVec({ x: a.x - p.x, y: a.y - p.y }),
        d2 = unitVec({ x: b.x - p.x, y: b.y - p.y });
      const s = snapCorner(engine, p, d1, d2, r);
      if (!s) return;
      pts[2 * i] = s.p.x;
      pts[2 * i + 1] = s.p.y;
    });
    return { f: 'poly', pts, round: f.round.slice() };
  }

  /* ----------------------------------------------------------- hit test */

  /**
   * The ends of a mark's band (cached per shape and document). The seam where the mark
   * abuts another placement's mark is an end too, whatever the band does behind it.
   */
  #endsOf(shape: Shape, it: WorkArea | Highlight): BandEnd[] {
    const d = this.displayDoc;
    const hit = this.#endsCache.get(shape);
    if (hit && hit.items === d.items) return hit.ends;
    const border = borderOf(shape);
    const u = this.metrics.u;
    let ends: BandEnd[] = [];
    if (border) {
      const cuts = it.t === 'hl' ? this.#cutsFor(d, placementOf(it), it.id) : [];
      const near = (p: Vec, c: number[]) => nearestOnPolyline(p, c, true).d <= 2;
      const seam = cuts.length ? (a: Vec, b: Vec) => cuts.some((c) => near(a, c) && near(b, c) && near({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, c)) : null;
      ends = bandEnds(border, 4 * u, 0.12 * Math.max(d.w, d.h), 8 * u, seam);
    }
    this.#endsCache.set(shape, { items: d.items, ends });
    return ends;
  }
  /** Dev scripts: what a press at a screen point would grab, and the selected mark's end tabs. */
  probe(p: Vec): { hit: Hit | null; ends: BandEnd[] } {
    const ip = this.toImage(p);
    const it = this.#selection ? this.displayDoc.items.find((i) => i.id === this.#selection) : undefined;
    return { hit: this.#gripHit(ip) ?? this.#endHit(ip) ?? this.#handleHit(ip) ?? this.hitTest(ip), ends: it && (it.t === 'hl' || it.t === 'area') ? this.#endsOf(it.shape, it) : [] };
  }
  /**
   * The selected label's box as laid out, when its width is for the hand (`fit`): a markup's label,
   * a callout, a note, a test's badge with words after its name, a shot's tag — not while it is
   * typed into, carried, or while a callout's arrows are chosen.
   */
  selectedBox(): { id: string; rect: Rect; fit: { min: number; nat: number }; border: number } | null {
    const id = this.#selection;
    if (!id || this.#pointing || this.#building || this.#carried?.id === id) return null;
    const l = this.layout();
    const m = this.metrics;
    let box: LabelBox | undefined;
    let border = 0;
    if (isSharedLabel(id)) {
      box = l.shared.get(id);
      border = m.pillBorder;
    } else {
      const it = this.getItem(id);
      if (it?.t === 'test') {
        box = l.tests.get(id);
        border = box?.badge?.tail ? m.badgeBorder : 0;
      } else if (it?.t === 'elev') box = it.id === this.#elevEditing ? undefined : l.elevs.get(id);
      else if (it?.t === 'callout') {
        box = l.callouts.get(id);
        border = m.calloutBorder;
      } else if (it?.t === 'note') {
        box = l.notes.get(id);
        border = m.calloutBorder;
      }
    }
    if (!box?.fit || (box as { editing?: boolean }).editing) return null;
    return { id, rect: box.rect, fit: box.fit, border };
  }
  /**
   * A side of the selected label's box (its grip, on the middle of the side, on the selection ring;
   * or anywhere along the side): a fingertip's reach outside it, and a little inside it — never so far
   * in that the middle of a small box is not still the box's, to carry it.
   */
  #gripHit(ip: Vec): Hit | null {
    const b = this.selectedBox();
    if (!b || !this.#gripsShown(b.rect)) return null;
    const vs = this.view.scale;
    const r = b.rect;
    // (its own pinpoint, under a grip when the plan is far out, is the pinpoint's: it is moved, not the box)
    const own = this.getItem(b.id);
    if ((own?.t === 'test' || own?.t === 'elev') && dist(own.p, ip) * vs <= (this.metrics.markerR + this.metrics.markerHalo) * vs + 10) return null;
    const ring = (b.border / 2 + this.metrics.leaderHalo) * vs; // the white beyond the side, screen px
    // With Test or Elevation in hand a tap beside a box places: only the grip as drawn (and a hair) is the grip's
    const placing = this.#tool === 'test' || this.#tool === 'elev';
    const half = placing ? Math.max(16, Math.min(28, r.h * vs * 0.5)) / 2 + 6 : (r.h * vs) / 2 + 12;
    if (Math.abs((ip.y - r.y - r.h / 2) * vs) > half) return null;
    const reach = placing ? 12 : 22;
    const inward = Math.min(placing ? 6 : 12, (r.w * vs) / 4);
    let best: Hit | null = null;
    let bd = Infinity;
    for (const side of [0, 1] as const) {
      // screen px beyond the side, outwards
      const out = side === 0 ? (r.x - ip.x) * vs : (ip.x - r.x - r.w) * vs;
      if (out < -inward || out > ring + reach) continue;
      const d = Math.abs(out - ring);
      if (d < bd) {
        bd = d;
        best = { id: b.id, part: 'edge', index: side };
      }
    }
    return best;
  }
  /**
   * A box's side carried (`dx` image px from where it was taken): the finger says how wide its words
   * may be (`handWidth`: the side in the hand where the finger is, never off the plan; the other side
   * out or in with it about the box's middle, or held by the plan's edge), never narrower than its
   * widest word nor wider than its words on one line — and the box is as wide as its words as they wrap
   * within that, on its middle, on the plan: always just so, its side never beyond the finger. When
   * its words wrap otherwise its frame glides to the new width (`#frame`), and that is felt.
   */
  #resizeTo(origin: MapDoc, dg: Drag, dx: number): MapDoc {
    const rz = dg.resize;
    if (!rz) return origin;
    const u = this.metrics.u;
    const r = rz.rect;
    const finger = (rz.side === 1 ? r.x + r.w : r.x) + dx;
    const room = Math.max(rz.fit.min, Math.min(rz.fit.nat, handWidth(r, rz.side, finger, u, origin.w - u)));
    const w = rz.at(room).w;
    const to: Span = { x: boxLeft(r.x + r.w / 2, w, u, origin.w - u), w };
    const now = performance.now();
    let f = this.#frame;
    if (!f || f.id !== dg.hit.id) f = this.#frame = { id: dg.hit.id, glide: { from: { x: r.x, w: r.w }, to: { x: r.x, w: r.w }, t0: now }, draw: (s) => this.#widthAt(origin, dg, s, false), still: false };
    if (Math.abs(to.x - f.glide.to.x) > 0.01 || Math.abs(to.w - f.glide.to.w) > 0.01) {
      f.glide = glideTo(f.glide, f.glide.from, to, now);
      f.still = false;
    }
    if (w !== rz.w) {
      rz.w = w;
      // (felt as its words take another shape — not a buzz, when they take many in a sweep)
      if (now - this.#feltAt > 70) {
        this.#feltAt = now;
        this.#events.snapped();
      }
    }
    return f.draw(glideAt(f.glide, now));
  }
  /** A box's grips are drawn and taken only where it is large enough on the screen for them to be about it (far out, they would be larger than it). */
  #gripsShown(r: Rect): boolean {
    const vs = this.view.scale;
    return r.h * vs >= 12 && r.w * vs >= 28;
  }
  /**
   * The document with a box in the hand drawn across `s` (plan px), on the middle it had (up and down)
   * as its lines come and go, on the plan: its frame (`exact`, its words wrapped within it) — or, let
   * go (`settled`), as wide as its words are there, no width of its own when that is on one line.
   */
  #widthAt(origin: MapDoc, dg: Drag, s: Span, settled: boolean): MapDoc {
    const rz = dg.resize!;
    const natural = settled && s.w >= rz.fit.nat - 0.5;
    const h = rz.at(natural ? undefined : s.w, !settled).h;
    const u = this.metrics.u;
    const at = { x: s.x, y: Math.max(u, Math.min(origin.h - u - h, rz.rect.y + rz.rect.h / 2 - h / 2)) };
    const look = (cur: BoxLook | undefined): BoxLook | undefined => {
      const { w: _w, ...rest } = cur ?? {};
      const next: BoxLook = natural ? rest : { ...rest, w: settled ? Math.round(s.w) : s.w };
      return Object.keys(next).length ? next : undefined;
    };
    const id = dg.hit.id;
    if (isSharedLabel(id)) {
      const label = this.#sharedLabel(origin, id);
      const { look: _l, ...rest } = label;
      const lk = look(label.look);
      return this.#withShared(origin, id, { ...rest, pos: at, ...(lk ? { look: lk } : {}) });
    }
    return updateItem(origin, id, (it) => {
      const { look: was, ...rest } = it as Item & { look?: BoxLook };
      const lk = look(was);
      const out = { ...rest, ...(lk ? { look: lk } : {}) } as Item;
      if (out.t === 'test' || out.t === 'elev') return { ...out, label: at };
      if (out.t === 'callout') return { ...out, box: at };
      if (out.t === 'note') return { ...out, p: at };
      return out;
    });
  }
  /**
   * The frame of a box made narrower or wider (its id; how it glides; the document with it drawn
   * across a span; whether it has come to rest; the document as it was let go), from the finger's
   * first move until it has glided to where it was let go — the document then having it so.
   */
  #frame: { id: string; glide: Glide; draw: (s: Span) => MapDoc; still: boolean; done?: MapDoc } | null = null;
  /** When a box's words last took another shape under the finger, felt (ms). */
  #feltAt = 0;
  #stepFrame(): void {
    const f = this.#frame;
    if (!f) return;
    const now = performance.now();
    // (let go, and come to where it was let go: the document has it just so — or changed since, by an
    // undo or a word typed: that is shown at once)
    if (this.#drag?.hit.id !== f.id && (glided(f.glide, now) || (f.done && f.done !== this.doc))) return this.#endFrame();
    if (f.still) return;
    this.#working = f.draw(glideAt(f.glide, now));
    f.still = glided(f.glide, now);
    this.#layout = null;
    this.#dirty.overlay = true;
    this.#events.viewChanged();
  }
  #endFrame(): void {
    // (a box still in the hand keeps its frame: the next move goes on from where it is drawn)
    if (!this.#frame || this.#drag?.hit.id === this.#frame.id) return;
    this.#frame = null;
    this.#working = null;
    this.#layout = null;
    this.#invalidate(true);
  }
  /** The end tab under a point (screen-sized), checked before anything else; there are no tabs while the anchors are being edited. */
  #endHit(ip: Vec): Hit | null {
    const id = this.#selection;
    if (!id || this.#editing === id) return null;
    const it = this.displayDoc.items.find((i) => i.id === id);
    // a drawn shape is handled by its points, not by band ends
    if (!it || it.t !== 'hl' || (it.shape.k === 'path' && it.shape.form)) return null;
    const vs = this.view.scale;
    let best: Hit | null = null,
      bd = Infinity;
    this.#endsOf(it.shape, it).forEach((end, k) => {
      // the tab as it is drawn (sized with the mark, on its colour band), with a fingertip's reach around it
      const c = endTabCentre(end, this.metrics);
      const dx = ((ip.x - c.x) * end.along.x + (ip.y - c.y) * end.along.y) * vs;
      const dy = ((ip.x - c.x) * end.out.x + (ip.y - c.y) * end.out.y) * vs;
      const size = endTabSize(end.width * vs);
      if (Math.abs(dx) <= size.reachAlong && Math.abs(dy) <= Math.max(size.reachAcross, (this.metrics.bandEdge * vs) / 2 + 10)) {
        const d = Math.hypot(dx, dy);
        if (d < bd) {
          bd = d;
          best = { id, part: 'end', index: k };
        }
      }
    });
    return best;
  }
  /** Anchor handles of the shape being edited (after its end tabs). */
  #handleHit(ip: Vec): Hit | null {
    const id = this.#selection;
    if (!id || this.#editing !== id) return null;
    const it = this.displayDoc.items.find((i) => i.id === id);
    if (!it || (it.t !== 'area' && it.t !== 'hl')) return null;
    const pts = it.shape.k === 'rect' ? rectToPoly(shapeBounds(it.shape)) : it.shape.pts;
    const closed = it.shape.k !== 'stroke';
    const rv = 14 / this.view.scale;
    let best: Hit | null = null,
      bd = Infinity;
    const shown = it.shape.k === 'path' ? Array.from({ length: pts.length / 2 }, (_, i) => i) : keyVertices(pts, closed, 16 / this.view.scale);
    for (const i of shown) {
      const d = Math.hypot(pts[2 * i]! - ip.x, pts[2 * i + 1]! - ip.y);
      if (d < rv && d < bd) {
        bd = d;
        best = { id, part: 'vertex', index: i };
      }
    }
    return best;
  }

  hitTest(ip: Vec): Hit | null {
    const d = this.displayDoc;
    const m = this.metrics;
    const layout = this.layout();
    const slop = 10 / this.view.scale;
    // an arrowhead is a handle a fingertip takes, however small it is drawn: a fingertip's reach round
    // the head, which stands just off its mark — over the mark past its tip only the head as drawn (and
    // a hair) is the arrow's, so a tap on the mark right by it is still the mark's
    const headReach = Math.max(m.markerR, 0.6 * m.arrowLen) + slop;
    const onHead = (l: { tip: Vec; dir?: Vec }): boolean => {
      if (dist(l.tip, ip) <= headReach) return true;
      const u = l.dir;
      if (!u) return false;
      const mid = { x: l.tip.x - u.x * 0.5 * m.arrowLen, y: l.tip.y - u.y * 0.5 * m.arrowLen };
      return dist(mid, ip) <= 24 / this.view.scale && (ip.x - l.tip.x) * u.x + (ip.y - l.tip.y) * u.y <= 0;
    };
    const inRect = (r: Rect) => rectContains({ x: r.x - slop, y: r.y - slop, w: r.w + 2 * slop, h: r.h + 2 * slop }, ip);
    const items = d.items;
    // cutting out of a mark: its cuts are what is touched (the newest on top)
    if (this.#cutting) {
      const cuts = cutsOf(items, this.#cutting);
      for (let i = cuts.length - 1; i >= 0; i--) if (pointInPoly(ip, shapeToPolygon(cuts[i]!.shape))) return { id: cuts[i]!.id, part: 'shape' };
    }
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i]!;
      if (it.t === 'note') {
        const b = layout.notes.get(it.id);
        if (b && inRect(b.rect)) return { id: it.id, part: 'label' };
        if (it.target && dist(it.target, ip) <= m.markerR + slop) return { id: it.id, part: 'target' };
      } else if (it.t === 'callout') {
        const b = layout.callouts.get(it.id);
        if (b && inRect(b.rect)) return { id: it.id, part: 'box' };
        // each arrowhead as shown (aimed at its mark's middle, or where it was put) is a finger-sized handle
        const heads: { tip: Vec; dir?: Vec }[] = b?.leaders ?? [{ tip: it.target }];
        const k = heads.findIndex(onHead);
        if (k >= 0) return { id: it.id, part: 'target', index: k };
      }
    }
    // the shared labels' arrowheads (one onto each work area, each failing area): handles, as a callout's are
    for (const id of layout.shared.keys()) {
      const k = (layout.soilsLeaders.get(id) ?? []).findIndex(onHead);
      if (k >= 0) return { id, part: 'target', index: k };
    }
    // the dots of tests and elevation shots, then their labels (the nearest dot under the finger wins).
    // With Test or Elevation in hand a tap places one: only the dot itself (and a hair) is the dot's,
    // so the next one can be placed right beside it
    const placing = this.#tool === 'test' || this.#tool === 'elev';
    // (the pinpoints are small — or not drawn at all: a finger still takes one from a fingertip's reach)
    const dotReach = placing ? m.markerR + m.markerHalo + 4 / this.view.scale : Math.max(m.markerR + m.markerHalo + slop, 18 / this.view.scale);
    let near: { id: string; d: number } | null = null;
    for (const it of items) {
      if (it.t !== 'test' && it.t !== 'elev') continue;
      const d = dist(it.p, ip);
      if (d <= dotReach && (!near || d < near.d)) near = { id: it.id, d };
    }
    if (near) return { id: near.id, part: 'marker' };
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i]!;
      if (it.t !== 'test' && it.t !== 'elev') continue;
      const b = (it.t === 'test' ? layout.tests : layout.elevs).get(it.id);
      if (b && inRect(b.rect)) return { id: it.id, part: 'label' };
    }
    for (const [id, b] of layout.shared) if (inRect(b.rect)) return { id, part: 'label' };
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i]!;
      if (it.t !== 'area' && it.t !== 'hl') continue;
      if (this.#markHit(d, it, ip, slop)) return { id: it.id, part: 'shape' };
    }
    return null;
  }
  #shapeHit(s: Shape, p: Vec, slop: number): boolean {
    if (s.k === 'rect') return rectContains(shapeBounds(s), p);
    if (s.k === 'poly') return pointInPoly(p, s.pts);
    if (s.k === 'path') return pointInPoly(p, flattenPath(s.pts, s.h));
    return nearestOnPolyline(p, s.pts, false).d <= s.width / 2 + slop;
  }
  /** Is a point on a mark: on its shape, and not in what is cut out of it. */
  #markHit(doc: MapDoc, it: WorkArea | Highlight, p: Vec, slop: number): boolean {
    return this.#shapeHit(it.shape, p, slop) && !inCut(cutRegions(cutsOf(doc.items, it.id)), p);
  }
  /** The selected shape's turning knob on screen, if it has one (the chrome keeps clear of it). */
  knobScreen(id: string): Vec | null {
    if (id !== this.#selection) return null;
    const f = this.#formOf(this.displayDoc, id);
    const k = f ? this.#knobOf(f) : null;
    return k ? this.toScreen(k.knob) : null;
  }
  /** (dev) The points of the shape being placed. */
  get buildingPoints(): Vec[] {
    return this.#building?.pts.map((p) => ({ ...p })) ?? [];
  }
  /** The selected drawn shape's rounding handles on screen: each corner's index and where its handle stands. */
  roundingScreens(): { i: number; x: number; y: number }[] {
    const id = this.#selection;
    const it = id ? this.displayDoc.items.find((x) => x.id === id) : undefined;
    const f = isShaped(it) && it.shape.k === 'path' ? it.shape.form : undefined;
    if (!it || !f) return [];
    return this.#roundingsOf(it, f).map((h) => ({ i: h.i, ...this.toScreen(h.at) }));
  }
  /** A mark's or area's border as a polygon in screen px (for chrome that keeps to the shape itself). */
  outlineScreen(id: string): number[] | null {
    const it = this.displayDoc.items.find((i) => i.id === id);
    if (!it || (it.t !== 'hl' && it.t !== 'area')) return null;
    const poly = shapeToPolygon(it.shape);
    const out: number[] = [];
    for (let i = 0; i < poly.length; i += 2) {
      const s = this.toScreen({ x: poly[i]!, y: poly[i + 1]! });
      out.push(s.x, s.y);
    }
    return out;
  }
  /** Where an item is on screen: a label's or box's rect, a mark's bounds; a test's label with its dot (`labelOnly`: its label alone). */
  anchorRect(id: string, labelOnly = false): Rect | null {
    const d = this.displayDoc;
    const layout = this.layout();
    let r: Rect | null = null;
    if (isSharedLabel(id)) r = layout.shared.get(id)?.rect ?? null;
    else {
      const it = d.items.find((i) => i.id === id);
      if (!it) return null;
      if (it.t === 'test' || it.t === 'elev') {
        const b = (it.t === 'test' ? layout.tests : layout.elevs).get(id);
        const m = this.metrics;
        const mr = { x: it.p.x - m.markerR, y: it.p.y - m.markerR, w: 2 * m.markerR, h: 2 * m.markerR };
        r = b ? (labelOnly ? b.rect : union(b.rect, mr)) : mr;
      } else if (it.t === 'callout') r = layout.callouts.get(id)?.rect ?? null;
      else if (it.t === 'note') r = layout.notes.get(id)?.rect ?? null;
      else r = shapeBounds(it.shape);
    }
    if (!r) return null;
    const a = this.toScreen({ x: r.x, y: r.y });
    const b = this.toScreen({ x: r.x + r.w, y: r.y + r.h });
    return { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
  }

  /* -------------------------------------------------------------- input */

  /**
   * With the Mark tool a shape that is not selected does not capture touches: a
   * hold on it starts a new mark (the next placement begins where the last one
   * ends, so the finger lands on it), a drag pans, and a tap selects it (soils:
   * places a test in the area). Only the selected shape moves with the finger;
   * select one by tapping or long-pressing it.
   */
  #capturable(hit: Hit | null): hit is Hit {
    if (!hit) return false;
    // cutting out of a mark, its cuts are what is taken: the selected one's handles take hold, and one
    // tapped moves with the finger; a touch anywhere else — inside a cut just drawn too — draws the next cut
    if (this.#cutting) {
      const it = this.getItem(hit.id);
      if (it?.t !== 'cut') return false;
      return hit.part === 'shape' ? hit.id === this.#selection && hit.id === this.#cutTaken : true;
    }
    // with the Pen a shape is tapped to take it in hand (see #penTap) — only the shape in hand moves
    // with the finger; a label, callout or test is taken as ever
    if (this.#tool === 'pen' && hit.part === 'shape') return hit.id === this.#selection;
    // with Square or Circle in hand, a touch on the plan or on a mark makes one — but the shape just
    // made (the selection) moves with the finger, and a label, callout or test is taken as ever
    if ((this.#tool === 'square' || this.#tool === 'circle') && hit.part === 'shape') return hit.id === this.#selection;
    // with Test in hand a work area never takes a touch (a tap places a test in it, a drag moves the plan)
    if (hit.part === 'shape' && (this.#tool === 'callout' || this.#tool === 'test' || this.#tool === 'elev' || (this.#tool === 'mark' && hit.id !== this.#selection))) return false;
    // the Callout tool is after a mark: an arrowhead lying on one does not take its tap
    // (an arrowhead is no trap for a test or a shot placed beside it: those tools place, as everywhere)
    if (hit.part === 'target' && (this.#tool === 'callout' || this.#tool === 'test' || this.#tool === 'elev')) return false;
    return true;
  }

  #onDown(p: Sample, info: StrokeInfo): DownAction {
    this.#endSettle();
    this.#endFrame();
    this.#stopGlide(); // a finger down takes the plan where it is
    // a first finger down means no pan or pinch is under way: a view one left behind is the view
    this.#commitGesture();
    // while a callout's arrows are being chosen, a finger only pans (its taps choose marks)
    if (this.#pointing) return 'pan';
    const ip = this.toImage(p);
    if (this.#building) {
      // once the Pencil has been used, a finger only moves the plan (as when marking)
      if (info.kind === 'touch' && this.#surface.penSeen && this.#surface.opts.fingerPansAfterPen) return 'pan';
      return this.#buildDown(p, ip);
    }
    const handle = this.#gripHit(ip) ?? this.#formHit(ip) ?? this.#endHit(ip) ?? this.#handleHit(ip);
    const raw = handle ?? this.hitTest(ip);
    const hit = this.#capturable(raw) ? raw : null;
    if (hit) return this.#beginDrag(hit, p, ip);
    // the Pen: a point (or, on a mark, perhaps a tap on it)
    if (this.#tool === 'pen') return this.#penDown(p, ip, raw, info);
    // Magic Mark marks the plan: a press on the backdrop round it (held or not) only moves the plan
    if (this.#tool === 'mark') return !this.#onPlan(ip) ? 'pan' : info.kind === 'pen' ? 'draw' : 'hold';
    // the Square and Circle tools draw at once: a touch is a shape (two fingers still move the plan);
    // with Test a touch that moves pans, and one that does not is a test
    if (this.#tool === 'square' || this.#tool === 'circle') return 'draw';
    return 'pan';
  }
  /** Something already there taken by the finger: its drag begins. */
  #beginDrag(hit: Hit, p: Sample, ip: Vec): DownAction {
    {
      // the box a label/callout starts from is fixed now: re-laying it out per frame would jitter
      const layout = this.layout();
      let base: Rect | null = null;
      let resize: Drag['resize'];
      if (hit.part === 'edge') {
        this.#endFrame();
        const b = this.selectedBox();
        const at = b && b.id === hit.id ? labelMeasure(this.doc, b.id, this.metrics, sharedMeasurer()) : null;
        if (b && at) resize = { side: hit.index === 0 ? 0 : 1, rect: b.rect, fit: b.fit, at, w: b.rect.w };
      }
      if (isSharedLabel(hit.id)) base = layout.shared.get(hit.id)?.rect ?? null;
      else if (hit.part === 'label') base = (layout.tests.get(hit.id) ?? layout.elevs.get(hit.id))?.rect ?? null;
      else if (hit.part === 'box' || hit.part === 'target') base = layout.callouts.get(hit.id)?.rect ?? null;
      let end: Drag['end'] = null;
      if (hit.part === 'end' && hit.index !== undefined) {
        const it = this.getItem(hit.id);
        const border = it && (it.t === 'hl' || it.t === 'area') ? borderOf(it.shape) : null;
        const e = it && (it.t === 'hl' || it.t === 'area') ? this.#endsOf(it.shape, it)[hit.index] : undefined;
        if (border && e) end = { pull: beginPull(border, e, this.#engine, this.tolerance, this.metrics.u, 0.7 * Math.max(this.doc.w, this.doc.h), { w: this.doc.w, h: this.doc.h }), grip: { x: ip.x - e.mid.x, y: ip.y - e.mid.y }, lastTab: null, lastPoly: null };
      }
      let tip: Vec | null = null;
      let face: MarkEnd | null = null;
      let arrow: Drag['arrow'];
      if (hit.part === 'target' && isSharedLabel(hit.id)) {
        const leader = layout.soilsLeaders.get(hit.id)?.[hit.index ?? 0];
        tip = leader?.tip ?? null;
        face = leader?.face ?? null;
        if (leader?.of) arrow = { callout: null, area: leader.of, mark: leader.of, shake: new Shake() };
      } else if (hit.part === 'target') {
        const it = this.getItem(hit.id);
        if (it?.t === 'callout') {
          const leader = layout.callouts.get(it.id)?.leaders[hit.index ?? 0];
          tip = leader?.tip ?? it.target;
          face = leader?.face ?? null;
          // (the arrow by its place among the callout's arrows: an arrow taken off has no leader)
          const k = leader?.arrow ?? hit.index ?? 0;
          arrow = { callout: k, area: null, mark: arrowsOf(it)[k]?.targetId ?? null, shake: new Shake() };
        } else if (it?.t === 'note') tip = it.target;
      }
      // the first arrowhead taken in hand says how one is taken off
      if (arrow && !this.#arrowHinted) {
        this.#arrowHinted = true;
        this.#events.notice('Shake it to take the arrow off');
      }
      // a dragged callout box never lands on another callout or a note; a soils label never on
      // another label or box, nor on a test's dot (its own included)
      const avoid: Rect[] = [];
      if (hit.part === 'box') {
        for (const [cid, cb] of layout.callouts) if (cid !== hit.id) avoid.push(cb.rect);
        for (const nb of layout.notes.values()) avoid.push(nb.rect);
      }
      if (this.#isSoilsLabel(hit)) avoid.push(...this.#labelAvoid(layout, hit.id));
      // (nothing is put down under the legend)
      else if (hit.part === 'box' && layout.legend) avoid.push(layout.legend.rect);
      // a drawn shape's point (or the middle of a side, which becomes one; or a box's side) moves finely and lands exactly
      let point: PreciseDrag | null = null;
      if (hit.part === 'fpoint' || hit.part === 'fmid' || hit.part === 'fside') {
        const it = this.getItem(hit.id);
        const f = isShaped(it) && it.shape.k === 'path' ? it.shape.form : undefined;
        const at = f ? (hit.part === 'fpoint' ? formHandles(f) : formMids(f))[hit.index ?? 0] : undefined;
        if (at) {
          point = new PreciseDrag(at, p);
          this.#loupe = { at, finger: { x: p.x, y: p.y }, snapped: false, fine: false };
          this.#dirty.live = true;
        }
      }
      // a test's or a shot's dot: carried finely, the loupe showing the very point (the fingertip hides it)
      if (hit.part === 'marker') {
        const it = this.getItem(hit.id);
        // (the loupe comes once the dot moves: a tap on it flashes nothing)
        if (it?.t === 'test' || it?.t === 'elev') point = new PreciseDrag(it.p, p);
      }
      // a rounded corner's handle: carried finely (no loupe: it is the curve that is watched)
      if (hit.part === 'frad') {
        const at = this.#formHandleAt(hit);
        if (at) point = new PreciseDrag(at, p);
      }
      let knobBelow = false;
      if (hit.part === 'frot') {
        const f = this.#formOf(this.doc, hit.id);
        const kn = f ? this.#knobOf(f) : null;
        const up = f ? formKnob(f) : null;
        knobBelow = !!(kn && up && dist(kn.at, up.at) > 1e-6);
      }
      this.#drag = { hit, start: ip, last: ip, moved: false, origin: this.doc, base, lastValid: base ? { x: base.x, y: base.y } : null, lastMove: null, tip, avoid, face, end, point, knobBelow, ...(arrow ? { arrow } : {}), ...(resize ? { resize } : {}) };
      return hit.part === 'fpoint' || hit.part === 'fmid' || hit.part === 'fside' || hit.part === 'frot' || hit.part === 'frad' ? 'point' : 'grab';
    }
  }
  #onDrawStart(p: Sample): void {
    if (this.#tool === 'pen') return this.#penHeld(p);
    const ip = this.toImage(p);
    if (this.#tool === 'square' || this.#tool === 'circle') {
      this.#placing = { kind: this.#tool, start: this.#clampToPlan(ip), at: this.#clampToPlan(ip), moved: false, down: { x: p.x, y: p.y } };
      this.#dirty.live = true;
      this.#schedule();
      return;
    }
    this.#stroke = [ip.x, ip.y];
    this.#times = [p.t];
    this.#pins = [];
    this.#restAt(ip, p.t);
    // held still: the loupe shows exactly where a corner would go if the finger lifts here
    this.#loupe = { at: this.#snapAny(ip) ?? ip, finger: { x: p.x, y: p.y }, snapped: false, fine: false };
    const m = this.metrics;
    this.#tracer = this.#engine ? new LiveStroke(this.#engine, this.tolerance, { width: m.bandDefault, margin: 0 }) : null;
    this.#tracer?.add([ip]);
    this.#intent = null;
    this.#intentLabel = null;
    this.#events.hold('armed', p);
    this.#dirty.live = true;
    this.#schedule();
  }
  #onMove(samples: Sample[], last: Sample): void {
    if (this.#stroke) {
      const s = this.#stroke;
      const added: Vec[] = [];
      for (const smp of samples) {
        const ip = this.toImage(smp);
        const n = s.length;
        if (n < 2 || Math.hypot(ip.x - s[n - 2]!, ip.y - s[n - 1]!) > 0.75) {
          s.push(ip.x, ip.y);
          this.#times.push(smp.t);
          added.push(ip);
        }
        this.#restAt(ip, smp.t);
      }
      if (added.length) this.#tracer?.add(added);
      // the loupe is for points: once the finger is drawing, it is only shown while it rests
      if (this.#loupe && Math.hypot(last.x - this.#loupe.finger.x, last.y - this.#loupe.finger.y) > 8) this.#loupe = null;
      this.#lastFinger = { x: last.x, y: last.y };
      this.#edgePan(last);
      this.#dirty.live = true;
      this.#schedule();
      return;
    }
    if (this.#penPress) return this.#penMove(last);
    if (this.#building?.touch || this.#building?.stroke) return this.#buildMove(last);
    const pl = this.#placing;
    if (pl) {
      pl.at = this.#clampToPlan(this.toImage(last));
      if (Math.hypot(last.x - pl.down.x, last.y - pl.down.y) > 8) pl.moved = true;
      this.#lastFinger = { x: last.x, y: last.y };
      if (pl.moved) this.#edgePan(last);
      this.#dirty.live = true;
      this.#schedule();
      return;
    }
    const dg = this.#drag;
    if (!dg) return;
    // an arrowhead shaken: off its mark
    if (dg.arrow && dg.arrow.shake.add(last.x, last.y, last.t)) return this.#shakeOff(dg);
    const ip = this.toImage(last);
    const dx = ip.x - dg.start.x;
    const dy = ip.y - dg.start.y;
    if (dg.point) {
      if (!dg.moved && Math.hypot(dx, dy) * this.view.scale < 3) return;
      const f0 = this.#formOf(dg.origin, dg.hit.id);
      if (!dg.moved && dg.hit.part === 'fmid' && f0?.f === 'poly') {
        // (editing points) pulling the middle of a side out puts a new point there
        const k = dg.hit.index ?? 0;
        dg.origin = updateItem(dg.origin, dg.hit.id, (x) => ({ ...x, shape: keeping((x as Shaped).shape, formShape(insertPoint(f0, k))) }) as Item);
        dg.hit = { id: dg.hit.id, part: 'fpoint', index: k + 1 };
      }
      const it = dg.origin.items.find((i) => i.id === dg.hit.id);
      const k = dg.hit.index ?? 0;
      if (dg.hit.part === 'frad') dg.point.move(last, this.view.scale, () => null, 0, 0);
      else {
        const snap = (q: Vec) => (!isShaped(it) ? null : dg.hit.part === 'fside' ? this.#sideSnap(it, k, q) : this.#pointSnap(it, k, q));
        const m = dg.point.move(last, this.view.scale, snap, 1.2 * this.tolerance, 2.6 * this.tolerance);
        if (m.took) this.#events.snapped();
        this.#loupe = { at: m.at, finger: { x: last.x, y: last.y }, snapped: dg.point.hold !== null, fine: dg.point.fine };
        this.#dirty.live = true;
      }
    }
    if (!dg.moved && Math.hypot(dx, dy) * this.view.scale < (dg.point ? 3 : 4)) return;
    if (!dg.moved && dg.hit.part === 'midpoint') {
      // Dragging a midpoint inserts a vertex there first.
      dg.origin = this.#insertVertex(dg.origin, dg.hit.id, dg.hit.index!, { x: dg.start.x, y: dg.start.y });
      dg.hit = { id: dg.hit.id, part: 'vertex', index: dg.hit.index! + 1 };
    }
    if (!dg.moved) this.#events.dragging(true);
    dg.moved = true;
    this.#working = this.#applyDrag(dg, dx, dy, ip);
    dg.last = ip;
    // an arrowhead under the finger: the loupe shows where its tip is on the mark
    if (dg.arrow?.tip) {
      this.#loupe = { at: dg.arrow.tip, finger: { x: last.x, y: last.y }, snapped: dg.arrow.mark !== null, fine: false };
      this.#dirty.live = true;
    }
    this.#invalidate(true);
    // carried to the screen's edge, the plan runs on under it (and what is carried with it)
    this.#lastFinger = { x: last.x, y: last.y };
    this.#edgePan(last);
  }
  #applyDrag(dg: Drag, dx: number, dy: number, ip: Vec): MapDoc {
    const { origin, hit, base } = dg;
    const clampP = (p: Vec): Vec => ({ x: Math.max(0, Math.min(origin.w, p.x)), y: Math.max(0, Math.min(origin.h, p.y)) });
    if (hit.part === 'target' && dg.arrow) return this.#dragArrow(origin, dg, ip);
    if (hit.part === 'edge') return this.#resizeTo(origin, dg, dx);
    if (this.#isSoilsLabel(hit)) {
      // A soils label is carried as a callout box is: it follows the finger everywhere (over other
      // labels too — that is how it gets past them), the plan's edge alone holding it like a rubber
      // band; where it may not rest (off the plan, on another label or box, on a test's dot) it is adrift
      if (!base) return origin;
      const m = metrics(origin.w, origin.h, origin.scale, origin.kind);
      const give = 48 * m.u;
      const at = { x: rubberBand(base.x + dx, m.u, origin.w - m.u - base.w, give), y: rubberBand(base.y + dy, m.u, origin.h - m.u - base.h, give) };
      const rests = this.#labelRests(origin, hit.id, { ...at, w: base.w, h: base.h }, dg.avoid);
      if (rests) dg.lastValid = at;
      this.#carry(hit.id, rests);
      return this.#labelAt(origin, hit.id, at);
    }
    const it = origin.items.find((i) => i.id === hit.id);
    if (!it) return origin;
    switch (it.t) {
      case 'test':
      case 'elev': {
        // the dot alone moves, carried finely (dg.point), where it is: its label stays where it is —
        // put by hand or placed automatically (2026-10-23, Adam: "when moving a dot it doesn't move
        // the label with it") — only its leader follows the dot
        const p = clampP(dg.point && hit.part === 'marker' ? dg.point.at : { x: it.p.x + dx, y: it.p.y + dy });
        // (a label placed automatically is kept where it stands: from now on it is where it was put —
        // Place its label automatically, on the bar, lets the layout have it again)
        const r = (it.t === 'test' ? this.#lastLayout?.tests : this.#lastLayout?.elevs)?.get(it.id)?.rect;
        const label = it.label ?? (r ? { x: r.x, y: r.y } : null);
        return updateItem<TestPoint | Elevation>(origin, it.id, (t) => ({ ...t, p, label }));
      }
      case 'callout': {
        const m = metrics(origin.w, origin.h, origin.scale, origin.kind);
        const cc = calloutContext(origin, m);
        const aims = cc.aimsOf(it);
        if (!base) return origin;
        // The box follows the finger everywhere: over marks and other callouts too — that is how it
        // is carried to the other side of them. Only the plan's edge holds it, like a rubber band
        // (there is nothing beyond to reach). Where it may not rest — off the plan, on a mark, too
        // close to a mark of its own for the arrowhead, on another callout or a note — it is adrift.
        const own = aims.map((a) => a.mark);
        const band = (v: number, lo: number, hi: number): number => {
          const give = 48 * m.u;
          const pull = (over: number) => (1 - 1 / ((over * 0.55) / give + 1)) * give;
          return v < lo ? lo - pull(lo - v) : v > hi ? hi + pull(v - hi) : v;
        };
        const at = [band(base.x + dx, m.u, origin.w - m.u - base.w), band(base.y + dy, m.u, origin.h - m.u - base.h)];
        const rests = boxFree({ x: at[0]!, y: at[1]!, w: base.w, h: base.h }, own, cc.space, dg.avoid);
        if (rests) dg.lastValid = { x: at[0]!, y: at[1]! };
        this.#carry(it.id, rests);
        return this.#boxAt(origin, it, { x: at[0]!, y: at[1]! }, base.h);
      }
      case 'note': {
        if (hit.part === 'target') return updateItem<Note>(origin, it.id, (n) => ({ ...n, target: n.target ? clampP({ x: n.target.x + dx, y: n.target.y + dy }) : null }));
        return updateItem<Note>(origin, it.id, (n) => ({ ...n, p: { x: n.p.x + dx, y: n.p.y + dy } }));
      }
      case 'cut': {
        // a cut: reshaped by its handles as any drawn shape, or moved whole with the finger
        const reshaped = this.#formDrag(origin, it, dg, ip, []);
        if (reshaped) return reshaped;
        return updateItem(origin, it.id, (x) => ({ ...x, shape: moveShape((x as Cut).shape, dx, dy) }) as Item);
      }
      case 'area':
      case 'hl': {
        // the marks of other placements (an area: the areas of other markups): no handle of this mark ever enters one, and the mark never overlaps one
        const cuts = this.#yieldsTo(origin, it);
        if (hit.part === 'end' && dg.end) {
          // the end follows the tab along the plan: out to extend, back to shorten — and the tab
          // stops at another placement's mark, right on its border, however far the finger goes
          const { pull, grip } = dg.end;
          const from = dg.end.lastTab ?? pull.end.mid;
          const tab = stopAtBorder(from, { x: ip.x - grip.x, y: ip.y - grip.y }, cuts);
          // a tab that has not moved (held at a seam) leaves the mark exactly as it is
          if (dist(tab, from) < 0.5) return dg.end.lastPoly ? updateItem(origin, it.id, (x) => ({ ...x, shape: { k: 'poly', pts: dg.end!.lastPoly! } }) as Item) : origin;
          const poly = pullTo(pull, tab);
          dg.end.lastTab = tab;
          dg.end.lastPoly = poly;
          return updateItem(origin, it.id, (x) => ({ ...x, shape: { k: 'poly', pts: poly } }) as Item);
        }
        const reshaped = this.#formDrag(origin, it, dg, ip, cuts);
        if (reshaped) return reshaped;
        if (hit.part === 'vertex' && hit.index !== undefined) {
          // Magnetic snapping to the plan's lines: an anchor lands just inside the outer edge of the
          // nearest line, the way every border sits
          let target = clampP(ip);
          this.#snapHint = null;
          const snapped = this.snapAnchors ? this.#snapToEdge(it, hit.index, target) : null;
          if (snapped) {
            target = snapped;
            this.#snapHint = { p: snapped, kind: 'line' };
          }
          // an anchor never enters another placement's mark: it lands on that mark's border
          for (const c of cuts) {
            if (!pointInPoly(target, c)) continue;
            target = nearestOnPolyline(target, c, true).p;
            this.#snapHint = { p: target, kind: 'line' };
          }
          const shape = toEditable(it.shape);
          if (shape.k === 'path') {
            const next = moveAnchor(shape, hit.index, target);
            return updateItem(origin, it.id, (x) => ({ ...x, shape: keeping(it.shape, { k: 'path', ...next }) }) as Item);
          }
          const pts = shape.pts.slice();
          pts[hit.index * 2] = target.x;
          pts[hit.index * 2 + 1] = target.y;
          return updateItem(origin, it.id, (x) => ({ ...x, shape: { ...shape, pts } }) as Item);
        }
        // the whole mark moves with the finger but never onto another placement's mark: it stops
        // where it meets one (sliding along it while one axis is still free)
        // the whole mark moves with the finger, but its middle stays on the plan: it can go right up
        // to the plan's edge and past it by half, never be flung off where it cannot be reached
        const bb = polyBounds(this.#polyOf(it.shape));
        const mx = bb.x + bb.w / 2,
          my = bb.y + bb.h / 2;
        let mv = { x: clamp(dx, -mx, origin.w - mx), y: clamp(dy, -my, origin.h - my) };
        if (cuts.length) {
          const poly = expandOutward(this.#polyOf(it.shape), -0.3); // a hair inside, so abutting exactly is not overlapping
          const free = (v: Vec) => !cuts.some((c) => polygonsOverlap(translatePts(poly, v.x, v.y), c));
          const lv = dg.lastMove ?? { x: 0, y: 0 };
          mv = [mv, { x: dx, y: lv.y }, { x: lv.x, y: dy }].find(free) ?? lv;
        }
        dg.lastMove = mv;
        const moved = updateItem(origin, it.id, (a) => ({ ...a, shape: moveShape((a as WorkArea | Highlight).shape, mv.x, mv.y) }) as Item);
        // what is cut out of it comes along
        moved.items = moved.items.map((x) => (x.t === 'cut' && x.of === it.id ? { ...x, shape: moveShape(x.shape, mv.x, mv.y) } : x));
        // its arrows come along; a box the person placed keeps its offset from a mark it points at
        // alone (one that points at other marks too stays where it was put)
        return {
          ...moved,
          items: moved.items.map((x) => {
            if (x.t !== 'callout' || !pointsAt(x, it.id)) return x;
            const arrows = arrowsOf(x).map((a) => (a.targetId === it.id ? { ...a, target: clampP({ x: a.target.x + mv.x, y: a.target.y + mv.y }) } : a));
            const alone = arrows.every((a) => a.targetId === it.id);
            return { ...withArrows(x, arrows), box: x.box && alone ? clampP({ x: x.box.x + mv.x, y: x.box.y + mv.y }) : x.box };
          }),
        };
      }
    }
  }
  /**
   * Where an anchor dragged to p belongs: on the nearest line within the snapping tolerance,
   * just inside that line's outer edge on the side away from the shape's inside (read from the
   * ink where it is clean, from the line's weight otherwise). Null when no line is near.
   */
  #snapToEdge(it: WorkArea | Highlight, i: number, p: Vec): Vec | null {
    const engine = this.#engine;
    if (!engine) return null;
    const tol = this.tolerance;
    const shape = toEditable(it.shape);
    // the line the anchor sits on keeps it while the finger stays anywhere near that line
    // (an anchor slides along its own line; it does not hop to a neighbouring one), otherwise
    // the nearest line within the snapping tolerance takes it
    let at: Vec | null = null,
      n: Vec = { x: 0, y: 1 },
      thick = 0;
    const home = engine.nearest({ x: shape.pts[2 * i]!, y: shape.pts[2 * i + 1]! }, Math.max(3, 0.5 * engine.rails.heavy + 2));
    if (home) {
      const rail = engine.rails.railOf(home.seg.id);
      const pr = projectOnRail(rail, p, 2.5 * tol);
      if (pr) {
        at = { x: pr.x, y: pr.y };
        n = { x: -pr.ty, y: pr.tx };
        thick = rail.thick;
      }
    }
    if (!at) {
      const h = engine.nearest(p, tol, (s) => s.len < 4);
      if (!h) return null;
      const seg = h.seg,
        k = h.segIndex;
      const ax = seg.pts[2 * k]!,
        ay = seg.pts[2 * k + 1]!,
        bx = seg.pts[2 * k + 2]!,
        by = seg.pts[2 * k + 3]!;
      const tl = Math.hypot(bx - ax, by - ay) || 1;
      at = h.point;
      n = { x: -(by - ay) / tl, y: (bx - ax) / tl };
      thick = seg.thick;
    }
    thick = Math.min(thick, 1.3 * engine.rails.heavy);
    let moved: number[];
    if (shape.k === 'path') {
      const m = moveAnchor(shape, i, at);
      moved = flattenPath(m.pts, m.h);
    } else {
      moved = shape.pts.slice();
      moved[2 * i] = at.x;
      moved[2 * i + 1] = at.y;
    }
    const probe = thick / 2 + 3;
    const inA = pointInPoly({ x: at.x + n.x * probe, y: at.y + n.y * probe }, moved);
    const inB = pointInPoly({ x: at.x - n.x * probe, y: at.y - n.y * probe }, moved);
    let out: Vec;
    if (inA !== inB) out = inA ? { x: -n.x, y: -n.y } : n;
    else {
      const c = polyCentroid(moved);
      out = (at.x - c.x) * n.x + (at.y - c.y) * n.y >= 0 ? n : { x: -n.x, y: -n.y };
    }
    const luma = engine.luma;
    const d = (luma ? inkEdge(luma, at, out, thick) : null) ?? edgeOffset(thick);
    return { x: at.x + out.x * d, y: at.y + out.y * d };
  }
  #insertVertex(origin: MapDoc, id: string, afterIndex: number, p: Vec): MapDoc {
    return updateItem(origin, id, (x) => {
      if (x.t !== 'area' && x.t !== 'hl') return x;
      const shape = toEditable(x.shape);
      if (shape.k === 'path') return x;
      const pts = shape.pts.slice();
      pts.splice((afterIndex + 1) * 2, 0, p.x, p.y);
      return { ...x, shape: { ...shape, pts } } as Item;
    });
  }
  /**
   * The finger is at ip: where it rests (within a few screen px) for a moment, that is a corner it
   * placed — a finger slows and stops at a corner it means (Sezgin et al. 2001). The rest shows in
   * the loupe and as a ring on the stroke.
   */
  #restAt(ip: Vec, t: number): void {
    const r = 5 / this.view.scale;
    const d = this.#dwell;
    if (d && dist(d.at, ip) <= r) return;
    if (d) clearTimeout(d.timer);
    const at = { ...ip };
    const timer = window.setTimeout(() => {
      if (!this.#stroke || this.#dwell?.at !== at) return;
      const last = this.#pins[this.#pins.length - 1];
      // one corner per rest, and a rest right where the stroke began is only the hold that started it
      const s = this.#stroke;
      if (last && dist(last, at) < 4 * r) return;
      if (s.length <= 4) return;
      this.#pins.push(at);
      this.#events.snapped();
      // the loupe shows the rest as a corner — unless the stroke is running along the plan's lines (a band)
      const it = this.#intent;
      if (!(it?.kind === 'band' && it.label !== 'Free line')) this.#loupe = { at: this.#snapAny(at) ?? at, finger: this.toScreen(at), snapped: false, fine: false };
      this.#dirty.live = true;
      this.#schedule();
    }, 170);
    this.#dwell = { at, t, timer };
  }
  /** The plan's corner or line nearest a point, whatever way the sides go (for a point whose sides are not drawn yet). */
  #snapAny(p: Vec, r = this.tolerance): Vec | null {
    const engine = this.#engine;
    if (!engine || !this.snapAnchors) return null;
    const s = engine.snapPoint(p, r);
    return s.kind ? s.p : null;
  }

  #onUp(p: Sample, _info: StrokeInfo, wasTap: boolean, held: number): void {
    this.#swallowTap = false;
    this.#stopEdgePan();
    if (this.#placing) {
      this.#finishPlacing();
      this.#swallowTap = wasTap;
      return;
    }
    if (this.#penPress) {
      this.#penUp(p);
      this.#swallowTap = wasTap;
      return;
    }
    if (this.#building) {
      this.#buildUp(p, wasTap);
      this.#swallowTap = wasTap;
      return;
    }
    if (this.#stroke) {
      const stroke = this.#stroke;
      this.#stroke = null;
      this.#endRest();
      this.#loupe = null;
      this.#dirty.live = true;
      this.#events.hold('end');
      this.#events.intent(null);
      this.#intentLabel = null;
      // held still, then lifted: a corner is placed there — the first of a shape placed corner by corner
      if (!wasTap && held >= Math.min(HOLD_POINT_MS, this.#surface.opts.tapTime) && polylineLength(stroke) * this.view.scale < 10) {
        this.#tracer = null;
        const ip = this.toImage(p);
        const at = this.#snapAny(ip);
        this.#startBuilding(at ?? ip, ip, at !== null);
        this.#schedule();
        return;
      }
      const intent = this.#analyze(stroke, true);
      // dev diagnostics: the finished tracer's reading (read by scripts/mark-shot.mjs)
      if (import.meta.env.DEV && this.#tracer) (globalThis as { __lastTrace?: unknown }).__lastTrace = { tol: this.tolerance, raw: this.#tracer.raw.slice(), closes: this.#tracer.closes.slice(), stretches: this.#tracer.debugStretches.map((st) => ({ mode: st.mode, width: Math.round(st.width), thick: st.thick.map((t) => +t.toFixed(1)), start: { x: Math.round(st.start.x), y: Math.round(st.start.y) }, a: st.a ? decimate(st.a) : null, b: st.b ? decimate(st.b) : null })) };
      this.#intent = null;
      this.#tracer = null;
      this.#engine?.clearCache();
      this.#commitIntent(intent);
      this.#schedule();
      return;
    }
    const dg = this.#drag;
    this.#drag = null;
    this.#snapHint = null;
    if (this.#loupe) {
      this.#loupe = null;
      this.#dirty.live = true;
      this.#schedule();
    }
    if (!dg) return;
    if (dg.point && dg.moved && this.#working) {
      // the point lands where it was before the finger rolled off it
      dg.point.at = dg.point.release(p);
      this.#working = this.#applyDrag(dg, 0, 0, dg.point.at);
    }
    if (dg.hit.part === 'frot' && dg.moved && this.#working) {
      // a box or oval turned half round is the same shape: it keeps a turn within a quarter either way (its knob's side stays its top)
      const f = this.#formOf(this.#working, dg.hit.id);
      if (f && f.f !== 'poly') {
        const half = Math.round(f.rot / Math.PI) * Math.PI;
        if (half !== 0) this.#working = updateItem(this.#working, dg.hit.id, (x) => ({ ...x, shape: keeping((x as WorkArea | Highlight).shape, formShape({ ...f, rot: f.rot - half })) }) as Item);
      }
    }
    if (dg.moved && this.#working) {
      let w = this.#working;
      this.#working = null;
      let selectId: string | null = dg.hit.id;
      let vertex: number | null = dg.hit.part === 'vertex' || ((dg.hit.part === 'fpoint' || dg.hit.part === 'frad') && this.#formOf(w, dg.hit.id)?.f === 'poly' && this.#pointsMode(this.getItem(dg.hit.id))) ? (dg.hit.index ?? null) : null;
      const moved = w.items.find((i) => i.id === dg.hit.id);
      if (dg.hit.part === 'frad') {
        const f = this.#formOf(w, dg.hit.id);
        if (f && f.f !== 'ellipse') this.#lastRounding = radiusOf(f, dg.hit.index ?? 0) || this.#lastRounding;
      }
      if (moved && (moved.t === 'hl' || moved.t === 'area') && (dg.hit.part === 'end' || dg.hit.part === 'shape' || dg.hit.part === 'vertex' || dg.hit.part === 'fpoint' || dg.hit.part === 'fmid' || dg.hit.part === 'fside' || dg.hit.part === 'frot' || dg.hit.part === 'frad')) {
        // a pulled band becomes a fitted path again; every moved shape yields to the marks of
        // other placements and takes in any mark of its own placement it now overlaps
        if (dg.hit.part === 'end' && moved.shape.k === 'poly') {
          const fitted = fitPath(moved.shape.pts, this.#fitTol, this.#cornerReach);
          const was = dg.origin.items.find((i) => i.id === moved.id);
          w = updateItem(w, moved.id, (x) => ({ ...x, shape: was && (was.t === 'hl' || was.t === 'area') ? keeping(was.shape, { k: 'path', ...fitted }) : { k: 'path', ...fitted } }) as Item);
        }
        const before = w.items.find((i) => i.id === moved.id);
        const placed = this.#place(w, moved.id);
        w = placed.doc;
        selectId = placed.ids[0] ?? null;
        const after = selectId ? w.items.find((i) => i.id === selectId) : undefined;
        if (!before || !after || before !== after) vertex = null; // the shape changed: its anchors are new
      }
      if ((dg.hit.part === 'box' && moved?.t === 'callout') || this.#isSoilsLabel(dg.hit)) {
        if (this.#carried?.id === dg.hit.id) {
          w = moved?.t === 'callout' ? this.#letGo(dg, w, moved) : this.#letGoLabel(dg, w);
          this.commit(w);
          this.select(selectId, vertex);
          // else when it has come to rest
          if (!this.#settle) {
            this.#events.dragging(false);
          }
          return;
        }
      }
      // a box let go: as wide as its words are where it was let go (the document has it so at once), its
      // frame gliding the last of the way there
      const f = dg.hit.part === 'edge' && dg.resize ? this.#frame : null;
      if (f && f.id === dg.hit.id) {
        w = this.#widthAt(dg.origin, dg, f.glide.to, true);
        this.commit(w);
        f.done = this.doc;
        if (!f.still) this.#working = f.draw(glideAt(f.glide, performance.now()));
        this.select(selectId, vertex);
        this.#events.dragging(false);
        this.#schedule();
        return;
      }
      this.commit(w);
      this.select(selectId, vertex);
      this.#events.dragging(false);
    } else {
      this.#working = null;
      // a tap selects (a callout is typed into from Edit on its bar, never by tapping it again)
      // a tap on a custom shape's point selects that point (a box's corner or an ellipse's handle is only a handle)
      if (dg.hit.part === 'vertex' || ((dg.hit.part === 'fpoint' || dg.hit.part === 'frad') && this.#formOf(this.doc, dg.hit.id)?.f === 'poly' && this.#pointsMode(this.getItem(dg.hit.id)))) this.select(dg.hit.id, dg.hit.index ?? null);
      else if (dg.hit.part === 'midpoint' || dg.hit.part === 'fmid' || dg.hit.part === 'fside' || dg.hit.part === 'fpoint' || dg.hit.part === 'frot' || dg.hit.part === 'frad') this.select(dg.hit.id, null);
      else if (!(dg.hit.part === 'shape' && (this.#tool === 'test' || this.#tool === 'elev'))) this.select(dg.hit.id);
      this.#invalidate(true);
    }
  }
  /**
   * A drawn shape (a mark's or a cut's) reshaped by one of its handles: a point (a box's corner, an
   * ellipse's handle), a side (the bar in its middle: the side moves square to itself), or the knob
   * (it turns about its middle). Null when the handle in hand is none of those.
   */
  #formDrag(origin: MapDoc, it: WorkArea | Highlight | Cut, dg: Drag, ip: Vec, cuts: readonly number[][]): MapDoc | null {
    const { hit } = dg;
    const clampP = (p: Vec): Vec => ({ x: Math.max(0, Math.min(origin.w, p.x)), y: Math.max(0, Math.min(origin.h, p.y)) });
    const form = it.shape.k === 'path' ? it.shape.form : undefined;
    if (!form) return null;
    if ((hit.part === 'fpoint' || hit.part === 'fside') && hit.index !== undefined && dg.point) {
      // a point or a side, where the fine drag has it (on the plan's corner or line it holds); it never
      // enters another placement's mark: it lands on that mark's border
      let target = clampP(dg.point.at);
      for (const c of cuts) if (pointInPoly(target, c)) target = nearestOnPolyline(target, c, true).p;
      const min = this.#minSize();
      const f = hit.part === 'fside' ? (form.f === 'rect' ? moveSide(form, hit.index, target, min) : form.f === 'poly' ? moveEdge(form, hit.index, target) : form) : moveHandle(form, hit.index, target, min);
      return updateItem(origin, it.id, (x) => ({ ...x, shape: keeping(it.shape, formShape(f)) }) as Item);
    }
    if (hit.part === 'frad' && hit.index !== undefined && dg.point && form.f !== 'ellipse') {
      // a rounded corner's handle: pulled in along the middle of its corner, the corner rounds more
      const c = cornerRounding(form, hit.index);
      if (!c) return null;
      const f = setRounding(form, hit.index, this.#roundingAt(c, dg.point.at));
      return updateItem(origin, it.id, (x) => ({ ...x, shape: keeping(it.shape, formShape(f)) }) as Item);
    }
    if (hit.part === 'frot') {
      // the knob turns the shape about its centre: square to the screen, and to a quarter turn, within 3°
      const c = form.f === 'poly' ? polyCentroid(formPolygon(form)) : { x: form.cx, y: form.cy };
      // the knob stands above the shape (or below it, near the top of the screen): the shape turns so it stays under the finger
      const below = dg.knobBelow ?? false;
      let turn = Math.atan2(ip.y - c.y, ip.x - c.x) + (below ? -Math.PI / 2 : Math.PI / 2);
      // (only with Snap on: off, it turns to exactly where the finger has it)
      const quarter = Math.PI / 2;
      const off = turn - Math.round(turn / quarter) * quarter;
      const detent = this.snapAnchors && Math.abs(off) < (3 * Math.PI) / 180;
      if (detent) turn -= off;
      if (this.#turnDetent !== detent) {
        this.#turnDetent = detent;
        if (detent) this.#events.snapped();
      }
      return updateItem(origin, it.id, (x) => ({ ...x, shape: keeping(it.shape, formShape(turnForm(form, turn))) }) as Item);
    }
    return null;
  }
  /**
   * A carried callout box is let go. Where it may rest, that is where it stays. Over a place it may
   * not, it goes to the nearest place it may (failing any, back where it last could have rested):
   * the document says so at once, and the box glides there.
   */
  #letGo(dg: Drag, w: MapDoc, c: Callout): MapDoc {
    const held = this.#carried!;
    const at = c.box;
    const base = dg.base;
    held.released = true;
    if (!held.adrift || !at || !base) return w;
    const cc = calloutContext(w, metrics(w.w, w.h, w.scale, w.kind));
    const own = cc.aimsOf(c).map((a) => a.mark);
    const to = nearestFree({ x: at.x, y: at.y, w: base.w, h: base.h }, own, cc.space, dg.avoid) ?? dg.lastValid ?? { x: base.x, y: base.y };
    const rested = this.#boxAt(w, c, to, base.h);
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches && dist(at, to) > 0.5) {
      const avoid = dg.avoid;
      this.#settle = {
        id: c.id,
        from: at,
        to,
        t0: performance.now(),
        place: (doc, p) => updateItem<Callout>(doc, c.id, (x) => ({ ...x, box: p })),
        rests: (p) => boxFree({ ...p, w: base.w, h: base.h }, own, cc.space, avoid),
      };
    } else held.adrift = false;
    return rested;
  }
  /**
   * A carried soils label is let go: where it may rest, there it stays; adrift, it goes to the
   * nearest place it may (failing any, back where it last could), gliding there.
   */
  #letGoLabel(dg: Drag, w: MapDoc): MapDoc {
    const held = this.#carried!;
    const base = dg.base;
    held.released = true;
    const at = this.#labelPos(w, dg.hit.id);
    if (!held.adrift || !at || !base) return w;
    const m = metrics(w.w, w.h, w.scale, w.kind);
    const avoid = dg.avoid;
    const rests = (p: Vec) => this.#labelRests(w, dg.hit.id, { ...p, w: base.w, h: base.h }, avoid);
    const to = nearestWhere({ ...at, w: base.w, h: base.h }, (r) => rests(r), 4 * m.u, 400 * m.u) ?? dg.lastValid ?? { x: base.x, y: base.y };
    const id = dg.hit.id;
    const rested = this.#labelAt(w, id, to);
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches && dist(at, to) > 0.5) {
      this.#settle = { id, from: at, to, t0: performance.now(), place: (doc, p) => this.#labelAt(doc, id, p), rests };
    } else held.adrift = false;
    return rested;
  }
  /** What is in the hand now may rest where it is, or may not (felt as it changes). */
  #carry(id: string, rests: boolean): void {
    const held = this.#carried;
    if (!held || held.id !== id) this.#carried = { id, adrift: !rests, released: false };
    else {
      held.released = false;
      if (held.adrift === rests) {
        held.adrift = !rests;
        this.#events.resting(rests);
      }
    }
  }
  /**
   * May a soils label rest here: wholly on the plan, off every other label and box and every test's
   * dot — and a shared area label, being a callout of the map's own, as a callout box may: off every
   * mark, and far enough from its own areas for its arrowheads.
   */
  #labelRests(doc: MapDoc, id: string, r: Rect, avoid: readonly Rect[]): boolean {
    const m = metrics(doc.w, doc.h, doc.scale, doc.kind);
    if (!labelRests(r, { x: 0, y: 0, w: doc.w, h: doc.h }, avoid, m.u)) return false;
    if (!isSharedLabel(id)) return true;
    const cc = calloutContext(doc, m);
    return boxAllowed(r, sharedMarks(doc, cc, id), cc.space);
  }
  /** A soils label that is carried like a callout box: a test's label, a shot's tag, or a shared area label (its box, not one of its arrowheads). */
  #isSoilsLabel(hit: Hit): boolean {
    if (hit.part !== 'label') return false;
    if (isSharedLabel(hit.id)) return true;
    const t = this.getItem(hit.id)?.t;
    return t === 'test' || t === 'elev';
  }
  /**
   * A shared label's arrowhead carried, as a callout's is: it slides along the side of its area that
   * faces the label (only the finger's travel along the area counts), goes round onto an end carried
   * past it, and stays put there from now on (a pin on the label, by the area's id).
   */
  /**
   * An arrowhead carried by the finger (2026-10-24, Adam: "maximum control to the user"): its tip is
   * the point of its mark's outline nearest the finger — any side, an end too — so it goes where it
   * is put, smoothly, and stays there. A callout's arrow carried onto (or right by) another mark it
   * does not point at yet points at that mark from then on (with a tick under the finger); a shared
   * label's arrow keeps to its own area. A shake takes it off (`#shakeOff`).
   */
  #dragArrow(origin0: MapDoc, dg: Drag, ip: Vec): MapDoc {
    const a = dg.arrow!;
    // its label (its callout's box) holds still while its arrow is carried, and stays where it stood
    // once the arrow is let go: laid out afresh, it would go looking for a better place as the arrow
    // moves — the label is where it was, the arrow where it was put (Auto position puts it back)
    const origin = this.#boxHeld(origin0, dg);
    const m = metrics(origin.w, origin.h, origin.scale, origin.kind);
    const marks = calloutContext(origin, m).space.marks;
    const f = { x: Math.max(0, Math.min(origin.w, ip.x)), y: Math.max(0, Math.min(origin.h, ip.y)) };
    const reach = 28 / this.view.scale;
    const near = (mk: (typeof marks)[number]) => (pointInPoly(f, mk.pts) ? 0 : nearestOnPolyline(f, mk.pts, true).d);
    const onOutline = (mk: (typeof marks)[number]) => nearestOnPolyline(f, mk.pts, true).p;
    if (a.area !== null) {
      const mk = marks.find((x) => x.id === a.area);
      if (!mk) return origin;
      const label = this.#sharedLabel(origin, dg.hit.id);
      a.tip = onOutline(mk);
      return this.#withSharedPins(origin, dg.hit.id, { ...(label.pins ?? {}), [a.area]: { target: a.tip } });
    }
    const c = origin.items.find((i): i is Callout => i.id === dg.hit.id && i.t === 'callout');
    const k = a.callout ?? 0;
    if (!c || !arrowsOf(c)[k]) return origin;
    // the marks it may point at: of its kind, not pointed at by its other arrows
    const kind = origin.kind === 'soils' ? 'area' : 'hl';
    const others = new Set(arrowsOf(c).flatMap((x, i) => (i !== k && x.targetId ? [x.targetId] : [])));
    const own = a.mark ? marks.find((x) => x.id === a.mark) : undefined;
    let best: { mk: (typeof marks)[number]; d: number } | null = null;
    for (const mk of marks) {
      if (mk.id === a.mark || others.has(mk.id) || origin.items.find((i) => i.id === mk.id)?.t !== kind) continue;
      const d = near(mk);
      if (d <= reach && (!best || d < best.d)) best = { mk, d };
    }
    // (it keeps to its own mark while the finger is by it: another takes it only once the finger is on that one)
    if (best && (!own || near(own) > reach || best.d === 0)) {
      a.mark = best.mk.id;
      this.#events.snapped();
    }
    const mk = a.mark ? marks.find((x) => x.id === a.mark) : undefined;
    const tip = mk ? onOutline(mk) : f;
    a.tip = tip;
    return updateItem<Callout>(origin, c.id, (x) => updateArrow(x, k, () => ({ target: tip, targetId: mk ? mk.id : null, pinned: true })));
  }
  /**
   * An arrow shaken off its mark: taken off — a callout's one of several taken away, its last one off
   * (the callout keeps its mark), a shared label's onto that area off — in one undo step, and the
   * arrow as it was drawn draws itself back into its box and fades (`#detachFx`).
   */
  #shakeOff(dg: Drag): void {
    const a = dg.arrow;
    if (!a) return;
    const layout = this.layout();
    // (its label stays where it stands as the arrow goes)
    const origin = this.#boxHeld(dg.origin, dg);
    let ghost: { pts: Vec[]; color: string } | null = null;
    let doc: MapDoc = origin;
    if (a.area !== null) {
      const route = layout.soilsLeaders.get(dg.hit.id)?.find((l) => l.of === a.area);
      const area = origin.items.find((i) => i.id === a.area);
      if (route) ghost = { pts: route.pts, color: area?.t === 'area' ? areaStroke(area) : '#1B7F3B' };
      const label = this.#sharedLabel(origin, dg.hit.id);
      const pin = label.pins?.[a.area] ?? { target: route?.tip ?? { x: 0, y: 0 } };
      doc = this.#withSharedPins(origin, dg.hit.id, { ...(label.pins ?? {}), [a.area]: { ...pin, off: true } });
    } else {
      const c = origin.items.find((i): i is Callout => i.id === dg.hit.id && i.t === 'callout');
      if (!c) return;
      const k = a.callout ?? 0;
      const route = layout.callouts.get(c.id)?.leaders.find((l, i) => (l.arrow ?? i) === k);
      if (route) ghost = { pts: route.pts, color: calloutColor(origin, c) };
      const arrows = arrowsOf(c);
      const on = arrows.filter((x) => !x.off);
      doc = updateItem<Callout>(origin, c.id, (x) => (on.length > 1 ? withArrows(x, arrows.filter((_, i) => i !== k)) : updateArrow(x, k, (y) => ({ ...y, off: true }))));
    }
    this.#drag = null;
    this.#working = null;
    this.#loupe = null;
    this.#dirty.live = true;
    this.#stopEdgePan();
    this.commit(doc);
    this.#events.dragging(false);
    this.#events.snapped();
    this.#events.notice('Arrow taken off');
    if (ghost && ghost.pts.length >= 2 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#detachFx = { ...ghost, t0: performance.now() };
      this.#dirty.overlay = true;
    }
    this.#invalidate(true);
  }
  /** The document with the dragged arrow's label (or callout box) kept where it stood as the drag began. */
  #boxHeld(doc: MapDoc, dg: Drag): MapDoc {
    const base = dg.base;
    if (!base) return doc;
    const at = { x: base.x, y: base.y };
    if (isSharedLabel(dg.hit.id)) {
      const label = this.#sharedLabel(doc, dg.hit.id);
      return label.pos ? doc : this.#withShared(doc, dg.hit.id, { ...label, pos: at });
    }
    return updateItem<Callout>(doc, dg.hit.id, (c) => (c.box ? c : { ...c, box: at }));
  }
  /** An arrow shaken off, drawing itself back into its box (its polyline as it was, box end first). */
  #detachFx: { pts: Vec[]; color: string; t0: number } | null = null;
  static readonly #DETACH_MS = 460;
  /** The first arrowhead taken in hand on this map has said how an arrow is taken off. */
  #arrowHinted = false;
  /** A shared label as the document keeps it: a markup's, or the failing areas'. */
  #sharedLabel(doc: MapDoc, id: string): SharedLabel {
    if (id === FAIL_LABEL) return doc.soils.failLabel ?? { text: null, pos: null };
    return markupLabel(doc.soils, markupOfLabel(id) ?? 1);
  }
  /** The document with a shared label set. */
  #withShared(doc: MapDoc, id: string, label: SharedLabel): MapDoc {
    if (id === FAIL_LABEL) return { ...doc, soils: { ...doc.soils, failLabel: label } };
    return { ...doc, soils: withMarkupLabel(doc.soils, markupOfLabel(id) ?? 1, label) };
  }
  /** A shared label with these arrows put by hand (none: every arrow at the middle of its area). */
  #withSharedPins(doc: MapDoc, id: string, pins: Record<string, ArrowPin> | undefined): MapDoc {
    const { pins: _p, ...rest } = this.#sharedLabel(doc, id);
    return this.#withShared(doc, id, pins && Object.keys(pins).length ? { ...rest, pins } : rest);
  }
  /** Whether a shared label has an arrow put by hand. */
  sharedArrowsPinned(id: string): boolean {
    const label = this.#sharedLabel(this.doc, id);
    return !!label.pins && Object.keys(label.pins).length > 0;
  }
  /** Whether a shared label has an arrow taken off (shaken off its area): Arrows back brings them all back. */
  sharedArrowsOff(id: string): boolean {
    return Object.values(this.#sharedLabel(this.doc, id).pins ?? {}).some((p) => p.off);
  }
  /** A shared label's arrows back to the middles of their areas (and any taken off, back on). */
  resetSharedArrows(id: string): void {
    if (!this.sharedArrowsPinned(id)) return;
    this.commit(this.#withSharedPins(this.doc, id, undefined));
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** Whether a shared label was put somewhere by hand (Auto puts it back). */
  sharedLabelPinned(id: string): boolean {
    return this.#sharedLabel(this.doc, id).pos !== null;
  }
  /** A shared label placed automatically again. */
  autoPlaceShared(id: string): void {
    const label = this.#sharedLabel(this.doc, id);
    if (label.pos === null) return;
    this.commit(this.#withShared(this.doc, id, { ...label, pos: null }));
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** The words of the failing areas' label (null: automatic). */
  get failText(): string | null {
    return this.doc.soils.failLabel?.text ?? null;
  }
  /** Set the failing areas' label's words (empty: automatic again). */
  setFailText(text: string): void {
    const t = text.trim().slice(0, 80);
    const { hidden, ...cur } = this.#sharedLabel(this.doc, FAIL_LABEL);
    if ((cur.text ?? '') === t && !hidden) return;
    this.commit(this.#withShared(this.doc, FAIL_LABEL, { ...cur, text: t || null }));
  }
  /**
   * A shared label at a place. Carried to the other side of an area, an arrow put by hand on the
   * area's side goes back to the middle of the side now facing the label (as a callout's does), so
   * the line never runs across the area.
   */
  #sharedLabelAt(doc: MapDoc, id: string, at: Vec, h: number): MapDoc {
    let pins = this.#sharedLabel(doc, id).pins;
    if (pins) {
      const cc = calloutContext(doc, metrics(doc.w, doc.h, doc.scale, doc.kind));
      const cy = at.y + h / 2;
      const kept: Record<string, ArrowPin> = {};
      for (const [aid, pin] of Object.entries(pins)) {
        const mk = cc.space.marks.find((x) => x.id === aid);
        if (!mk || pin.face || (mk.mid.y >= cy ? 1 : -1) === (pin.target.y < mk.mid.y ? 1 : -1)) kept[aid] = pin;
      }
      pins = kept;
    }
    const d = this.#withSharedPins(doc, id, pins);
    return this.#withShared(d, id, { ...this.#sharedLabel(d, id), pos: at });
  }
  /** Where a soils label may not rest (as laid out now): every other label and box, and every test's dot (a test label's own too). */
  #labelAvoid(layout: Layout, id: string): Rect[] {
    const m = this.metrics;
    const out: Rect[] = [];
    for (const [tid, b] of layout.tests) if (tid !== id) out.push(b.rect);
    for (const [eid, b] of layout.elevs) if (eid !== id) out.push(b.rect);
    for (const [sid, b] of layout.shared) if (sid !== id) out.push(b.rect);
    for (const b of layout.callouts.values()) out.push(b.rect);
    for (const b of layout.notes.values()) out.push(b.rect);
    if (layout.legend) out.push(layout.legend.rect);
    // (keeping clear of the pinpoints as drawn — or, with none, of the leaders' ends)
    const r = (layout.dot.r > 0 ? layout.dot.r + layout.dot.halo : m.leader / 2 + m.leaderHalo) + m.gap;
    for (const it of this.displayDoc.items) if (it.t === 'test' || it.t === 'elev') out.push({ x: it.p.x - r, y: it.p.y - r, w: 2 * r, h: 2 * r });
    return out;
  }
  /** A soils label's pinned place (null: placed automatically). */
  #labelPos(doc: MapDoc, id: string): Vec | null {
    if (isSharedLabel(id)) return this.#sharedLabel(doc, id).pos;
    const t = doc.items.find((i) => i.id === id);
    return t?.t === 'test' || t?.t === 'elev' ? t.label : null;
  }
  /** A soils label pinned at a place. */
  #labelAt(doc: MapDoc, id: string, at: Vec): MapDoc {
    if (isSharedLabel(id)) return this.#sharedLabelAt(doc, id, at, this.layout().shared.get(id)?.rect.h ?? 0);
    return updateItem<TestPoint | Elevation>(doc, id, (t) => ({ ...t, label: at }));
  }
  /**
   * A callout with its box at a place. Carried to the other side of a mark, an arrow put by hand
   * on its side goes back to the middle of the side now facing the box, so the line never runs
   * across the mark.
   */
  #boxAt(doc: MapDoc, c: Callout, at: Vec, h: number): MapDoc {
    const aims = calloutContext(doc, metrics(doc.w, doc.h, doc.scale, doc.kind)).aimsOf(c);
    const cy = at.y + h / 2;
    const arrows = arrowsOf(c).map((a, k): CalloutTarget => {
      const mk = aims[k]?.mark;
      if (!a.pinned || a.face || !mk) return a;
      const nowSign = mk.mid.y >= cy ? 1 : -1;
      const pinnedSign = a.target.y < mk.mid.y ? 1 : -1;
      return nowSign === pinnedSign ? a : { target: a.target, targetId: a.targetId };
    });
    return updateItem<Callout>(doc, c.id, (x) => ({ ...withArrows(x, arrows), box: { x: at.x, y: at.y } }));
  }
  /** Whether the carried box is still moving: being raised or set down, letting go or taking hold, or gliding to rest. */
  #carryMoving(): boolean {
    const held = this.#carried;
    if (!held) return false;
    const lift = held.released && !this.#settle ? 0 : 1;
    return this.#settle !== null || this.#liftT !== lift || this.#adriftT !== (held.adrift ? 1 : 0);
  }
  /** One frame of the carried box's motion. */
  #stepCarry(): void {
    const held = this.#carried;
    const now = performance.now();
    const dt = Math.min(64, now - this.#carryClock);
    this.#carryClock = now;
    if (!held) return;
    const st = this.#settle;
    if (st) {
      // a spring with no bounce: quick away, soft into place
      const t = (now - st.t0) / 1000;
      const k = 1 - (1 + 16 * t) * Math.exp(-16 * t);
      if (now - st.t0 >= Editor.#SETTLE_MS) {
        this.#endSettle();
      }
      else {
        const at = { x: st.from.x + (st.to.x - st.from.x) * k, y: st.from.y + (st.to.y - st.from.y) * k };
        this.#working = st.place(this.doc, at);
        // it takes hold of its marks again as soon as it is somewhere it may rest
        held.adrift = !st.rests(at);
        this.#layout = null;
        this.#events.viewChanged(); // what floats beside it keeps beside it
      }
    }
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const towards = (v: number, target: number, ms: number) => (still ? target : v < target ? Math.min(target, v + dt / ms) : Math.max(target, v - dt / ms));
    this.#liftT = towards(this.#liftT, held.released && !this.#settle ? 0 : 1, 160);
    const was = this.#adriftT;
    this.#adriftT = towards(was, held.adrift ? 1 : 0, 170);
    if (was >= 1 !== this.#adriftT >= 1) this.#layout = null; // its leaders come back as it takes hold
    if (held.released && !this.#settle && this.#liftT === 0 && this.#adriftT === 0) this.#carried = null;
  }
  /** The glide to rest is over (or cut short: the document already has the box where it was going). */
  #endSettle(): void {
    if (!this.#settle) return;
    this.#settle = null;
    this.#working = null;
    if (this.#carried) this.#carried.adrift = false;
    this.#layout = null;
    this.#invalidate(true);
    this.#events.dragging(false);
  }
  /** Taps: on a grabbed item they act on it, otherwise the tool decides what to create. */
  #onTap(p: Sample): void {
    if (this.#swallowTap) {
      this.#swallowTap = false;
      return;
    }
    const ip = this.toImage(p);
    if (this.#pointing) {
      // a tap on a mark points the callout at it (or no longer at it); a tap anywhere else is done
      const mark = this.#markAt(ip);
      if (mark) this.toggleArrow(this.#pointing, mark.id);
      else this.setPointing(false);
      return;
    }
    // a tap on the selection's own handles (a point, an end tab, an anchor) was the press itself: it
    // selected the point, and is no tap on the plan (the handles lie on the outline, half outside it).
    // With Test in hand a tap inside a work area places a test: there only a tap on a point's very dot is the point's
    // (a box's grips are for a drag: a tap on one is a tap on what is there)
    const handle = this.#formHit(ip) ?? this.#endHit(ip) ?? this.#handleHit(ip);
    if (handle) {
      const at = this.#formHandleAt(handle);
      const onDot = !at || (handle.part === 'fpoint' && dist(at, ip) * this.view.scale <= 10);
      if (onDot || (this.#tool !== 'test' && this.#tool !== 'elev')) return;
    }
    const raw = this.hitTest(ip);
    // With Test (or Elevation) in hand a tap anywhere on a work area (selected or not) places a test (a shot) in it.
    const hit = this.#capturable(raw) && !(raw.part === 'shape' && (this.#tool === 'test' || this.#tool === 'elev')) ? raw : null;
    // (a label, callout or test was tapped, and selected: whatever tool is in hand stays there)
    if (hit) return;
    if (this.#tool === 'pen') return this.#penTap(ip, raw);
    // cutting out of a mark: a tap on a cut takes it in hand; anywhere else, the mark again (cutting goes on)
    if (this.#cutting) return this.#tapWhileCutting(ip);
    // with a tool that places (a test, a shot, a callout), a tap that lets something go — a label, a
    // test, a mark — does only that; the next tap places. (A shot being typed is finished, and the
    // next placed, by the one tap; a callout being typed, by its own rule below.)
    const sel = this.#selection;
    if (sel && (this.#tool === 'test' || this.#tool === 'elev') && sel !== this.#elevEditing) {
      // (a tap inside the work area in hand places its test there, as anywhere in an area: that is no tap elsewhere)
      if (raw?.id !== sel) return this.select(null);
      this.select(null);
    }
    if (sel && this.#tool === 'callout' && this.getItem(sel)?.t !== 'callout') return this.select(null);
    switch (this.#tool) {
      case 'mark': {
        // a tap on a mark selects it, on the plan it lets the selection go
        const shape = raw && raw.part === 'shape' ? raw.id : null;
        if (!shape && !this.#selection && this.doc.kind === 'soils' && this.#onPlan(ip) && !this.#testHinted) {
          this.#testHinted = true;
          this.#events.notice('To place tests, choose Test');
        }
        this.select(shape);
        break;
      }
      case 'test':
        // tests are taken on the plan: a tap on the backdrop round it places nothing
        if (!this.#onPlan(ip)) return this.#events.notice('Tap on the plan to place a test');
        this.addTest(ip);
        break;
      case 'elev':
        // a shot being typed: a tap anywhere finishes it, and does only that — the next tap places the
        // next shot (2026-10-24, Adam: as a tap that lets something go does with the Pen)
        if (this.#elevEditing) {
          this.#endElevEdit();
          this.select(null);
          return;
        }
        // a shot is taken on the plan
        if (!this.#onPlan(ip)) {
          this.#endElevEdit();
          return this.#events.notice('Tap on the plan to place an elevation');
        }
        this.addElev(ip);
        break;
      case 'callout': {
        // a callout for the mark (or work area) under the finger, else for that spot of the plan; the
        // tool stays in hand — but the tap that ends the typing of one makes no other
        if (this.#endsTyping()) return;
        if (!this.#onPlan(ip)) return this.#events.notice('Tap on the plan to call out a spot');
        const mark = this.#markAt(ip);
        if (mark?.t === 'hl') this.addCallout(mark.material, ip, mark.id);
        else if (mark) this.addCallout(null, ip, mark.id);
        else this.addCallout(this.doc.kind === 'concrete' ? this.#material : null, ip, null);
        break;
      }
    }
  }
  /** A tap while cutting: on a cut, that cut in hand (a drag in it then moves it); anywhere else, the mark again (cutting goes on). */
  #tapWhileCutting(ip: Vec): void {
    const at = this.hitTest(ip);
    const it = at ? this.getItem(at.id) : undefined;
    this.#cutTaken = it?.t === 'cut' ? it.id : null;
    this.select(it?.t === 'cut' ? it.id : this.#cutting);
  }
  /**
   * With a callout selected (just made and being typed into, or tapped), a tap on the plan lets it go
   * and does nothing more: the tap that ends the typing never makes something else.
   */
  #endsTyping(): boolean {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (it?.t !== 'callout') return false;
    this.select(null);
    return true;
  }
  /** The topmost mark (or on a soils map, work area) under a point. */
  #markAt(ip: Vec): Highlight | WorkArea | null {
    const items = this.displayDoc.items;
    const slop = 10 / this.view.scale;
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i]!;
      if ((it.t === 'hl' || it.t === 'area') && this.#markHit(this.displayDoc, it, ip, slop)) return it;
    }
    return null;
  }
  #onCancel(): void {
    // a second finger arriving while points are placed pans or zooms: the points stay (the one being carried, or a stretch being drawn, goes)
    if (this.#building) {
      this.#building.touch = null;
      this.#building.stroke = null;
    }
    if (this.#penPress) {
      clearTimeout(this.#penPress.timer);
      this.#penPress = null;
    }
    this.#guides = null;
    this.#stopEdgePan();
    clearTimeout(this.#loupeTimer);
    // and one arriving while a square or circle is drawn out: it is not drawn
    this.#placing = null;
    this.#loupe = null;
    this.#endRest();
    if (this.#stroke) {
      this.#stroke = null;
      this.#tracer = null;
      this.#events.hold('end');
      this.#events.intent(null);
      this.#intentLabel = null;
    }
    if (this.#drag?.moved) this.#events.dragging(false);
    this.#drag = null;
    this.#settle = null;
    this.#carried = null;
    this.#frame = null;
    this.#liftT = this.#adriftT = 0;
    this.#working = null;
    this.#snapHint = null;
    this.#dirty.live = true;
    this.#invalidate(true);
  }
  #onLongPress(p: Sample): void {
    const ip = this.toImage(p);
    const hit = this.hitTest(ip);
    this.#drag = null;
    this.#dirty.live = true;
    if (hit) this.select(hit.id);
    else this.select(null);
    this.#invalidate(true);
  }

  /* --------------------------------------------------------- operations */

  #commitIntent(intent: Intent): void {
    if (intent.kind === 'none') return;
    if (intent.kind === 'band' && intent.pts.length < 6) return;
    if (intent.kind === 'shape') {
      this.#commitShape(formShape(intent.form), false);
      return;
    }
    let shape: Shape = intent.kind === 'trace' || intent.kind === 'free-stroke' ? { k: 'stroke', pts: intent.pts, width: intent.width } : { k: 'path', ...fitPath(intent.pts, this.#fitTol, this.#cornerReach) };
    // an outline too thin for a curve of three anchors is kept as the polygon it is, or not at all
    if (shape.k === 'path' && shape.pts.length < 6) {
      if (intent.pts.length < 6 || Math.abs(polyAreaOf(intent.pts)) < 4 * this.tolerance * this.tolerance) return this.#events.notice('Too small to mark — draw it a little larger');
      shape = { k: 'poly', pts: intent.pts };
    }
    this.#commitShape(shape, intent.kind === 'band');
  }
  /**
   * A new mark (or work area) on the map, placed among the others; a drawn shape keeps what else
   * its sketch could be, one tap away.
   */
  #commitShape(shape: Shape, band: boolean): void {
    this.#addShape(shape, band);
  }
  #addShape(shape: Shape, band: boolean): void {
    if (this.#cutting) return this.#addCut(this.#cutting, shape);
    const id = newId();
    if (this.doc.kind === 'soils') {
      // a work area goes into the markup being marked, in its colour; a failing one stands apart
      const markup = Math.max(1, Math.min(this.markupCount + 1, this.#markup));
      const colour = this.#areaFail ? AREA_COLOR_KEYS[0] : this.markupColour(markup);
      const area: WorkArea = { t: 'area', id, shape, ...(this.#areaFail ? { status: 'fail' as const } : {}), ...(colour !== AREA_COLOR_KEYS[0] ? { color: colour } : {}), ...(!this.#areaFail && markup !== 1 ? { markup } : {}) };
      const placed = this.#place(addItem(this.doc, area), id);
      const first = placed.ids[0];
      if (!first) {
        // drawn wholly over another markup's area: nothing of it is left
        const under = this.doc.items.find((x): x is WorkArea => x.t === 'area' && x.status !== 'fail' && markupOf(x) !== markup && polygonsOverlap(shapeToPolygon(x.shape), shapeToPolygon(shape)));
        this.#events.notice(under ? `That is markup ${markupOf(under)} — tap ${markupOf(under)} in the strip to add to it` : 'Nothing left of that area');
        return;
      }
      this.commit(placed.doc);
      this.select(first);
      return;
    }
    const placement = this.#placement;
    const item: Highlight = { t: 'hl', id, material: this.#material, shape, placement };
    const placed = this.#place(addItem(this.doc, item), id);
    const first = placed.ids[0];
    if (!first) {
      // drawn wholly over another placement: nothing of it is left
      const under = this.#cutsFor(this.doc, placement, null).length ? this.doc.items.find((x) => x.t === 'hl' && placementOf(x) !== placement && polygonsOverlap(shapeToPolygon(x.shape), shapeToPolygon(shape))) : undefined;
      this.#events.notice(under && under.t === 'hl' ? `That is pour ${placementOf(under)} — tap ${placementOf(under)} to add to it` : 'Nothing left of that mark');
      return;
    }
    const own = this.#pourCallout(placed.doc, placement, item.material);
    if (own) {
      // The pour's callout speaks for every mark of the pour: it grows an arrow to each new,
      // separate one (a mark that joined another is pointed at already).
      this.commit(this.#pointAt(placed.doc, own.id, placed.ids));
      // a callout nobody has typed into yet is still waiting for its words: it stays the selection
      this.select(calloutIsEmpty(own) ? own.id : first);
      return;
    }
    // A pour's first mark comes with its callout, ready to be filled in (one undo step for both).
    // Marks of the pour left without one (their callout was let go before it was typed into) are its too.
    const mark = placed.doc.items.find((x) => x.id === first);
    const point = this.#clampToPlan(mark && mark.t === 'hl' ? (band ? anchorInside(shapeToPolygon(mark.shape)) : anchorOnShape(mark.shape)) : anchorOnShape(shape));
    let c = this.#newCallout(item.material, point, first);
    for (const x of placed.doc.items) {
      if (x.t === 'hl' && x.id !== first && placementOf(x) === placement && x.material === item.material && !calloutsAt(placed.doc.items, x.id).length) c = addArrow(c, { target: this.#clampToPlan(anchorOnShape(x.shape)), targetId: x.id });
    }
    // its alignment and stations, read off the plan where it says
    c = withAutoStations(c, placed.doc, this.#stations);
    this.commit(addItem(placed.doc, c));
    this.select(c.id);
  }

  /* ------------------------------------------------------ drawn shapes */

  /**
   * The shape a drawn shape becomes when it is cut (yielding to another pour) or joined with
   * another mark: its new outline, still handled by its points (a custom shape). A Magic Mark
   * band stays an outline, as before.
   */
  #reshaped(outline: number[], hadForm: boolean): Shape {
    const path = fitPath(outline, this.#fitTol, this.#cornerReach);
    // an outline too thin for a curve of three anchors is kept as the polygon it is
    if (path.pts.length < 6) return { k: 'poly', pts: outline };
    const f = hadForm ? outlineForm(path, Math.max(1, 0.5 * this.metrics.u)) : null;
    // a box cut square (by a neighbour's straight side) is still a box
    return f ? formShape(asBox(f) ?? f) : { k: 'path', ...path };
  }
  /** The next work area drawn on a soils map is a failing one. */
  #areaFail = false;
  /** The next work area's colour: a palette name (AREA_COLORS) or a colour of the inspector's own (#rrggbb). */
  #areaColor: string = AREA_COLOR_KEYS[0];
  /** The colours the next work area drawn gets: its markup's, or a failing area's red. */
  #nextAreaColors(): { fill: string; stroke: string } {
    // (the markup's colour as its areas have it now: an undone recolouring is undone here too)
    return this.#areaFail ? { fill: FAIL_AREA_FILL, stroke: FAIL } : areaColors(this.markupColour(Math.max(1, Math.min(this.markupCount + 1, this.#markup))));
  }
  /** The colour of the markup in hand: the selected area's (or label's), else the one being marked (as the strip shows it). */
  get areaColor(): string {
    const n = this.selectionMarkup;
    return n !== null ? this.markupColour(n) : this.#areaColor;
  }
  /**
   * Choose a markup's colour: the selected area's markup (or label's), else the one being marked —
   * every area of it takes the colour, and its label, its arrows and any callout on it follow. A
   * failing area chosen is a work area of the markup being marked from now on, in that colour.
   */
  applyAreaColor(color: string): void {
    const key = color.toLowerCase();
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    let doc = this.doc;
    let n = this.selectionMarkup ?? this.#markup;
    if (it?.t === 'area' && it.status === 'fail') {
      n = Math.max(1, Math.min(this.markupCount + 1, this.#markup));
      doc = updateItem<WorkArea>(doc, it.id, (a) => {
        const { status: _s, markup: _m, ...rest } = a;
        return n === 1 ? rest : { ...rest, markup: n };
      });
    }
    this.#areaFail = false;
    if (n === this.#markup || this.selectionMarkup === null) this.#areaColor = key;
    const ids: string[] = [];
    doc = {
      ...doc,
      items: doc.items.map((x) => {
        if (x.t !== 'area' || x.status === 'fail' || markupOf(x) !== n || (x.color ?? AREA_COLOR_KEYS[0]) === key) return x;
        ids.push(x.id);
        const { color: _c, ...rest } = x;
        return key === AREA_COLOR_KEYS[0] ? rest : { ...rest, color: key };
      }),
    };
    if (!ids.length && doc === this.doc) return this.#events.selection(this.#selection, this.#vertexSel);
    // areas of the markup left in other colours (a map from before markups) are one colour now: those that overlap join
    for (const id of ids) if (doc.items.some((x) => x.id === id)) doc = this.#place(doc, id).doc;
    if (it?.t === 'area' && it.status === 'fail') doc = this.#place(doc, it.id).doc;
    this.commit(doc);
    if (this.#selection && !isSharedLabel(this.#selection) && !this.getItem(this.#selection)) this.select(null);
    else this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** Whether the selected area is failing, else whether the next one will be. */
  get areaFailing(): boolean {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (it?.t === 'area') return it.status === 'fail';
    if (this.#selection === FAIL_LABEL) return true;
    return this.#selection && isSharedLabel(this.#selection) ? false : this.#areaFail;
  }
  /** Choose a work area or a failing one: for the next area drawn, and for the selected area (as the materials do for marks). */
  applyAreaFailing(on: boolean): void {
    this.#areaFail = on;
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    if (it?.t === 'area') this.setAreaFailing(it.id, on);
    else this.#events.selection(this.#selection, this.#vertexSel);
  }
  /**
   * Mark a work area failing (red, under the failing areas' own label), or no longer: then it is a
   * work area of the markup being marked, in its colour. Either way it joins the areas of its new
   * kind it overlaps (a failing area never joins a work area).
   */
  setAreaFailing(id: string, on: boolean): void {
    const it = this.getItem(id);
    if (!it || it.t !== 'area' || (it.status === 'fail') === on) return;
    const n = Math.max(1, Math.min(this.markupCount + 1, this.#markup));
    const colour = this.markupColour(n);
    const next = updateItem<WorkArea>(this.doc, id, (a) => {
      const { status: _s, markup: _m, color: _c, ...rest } = a;
      if (on) return { ...rest, status: 'fail', ...(a.color ? { color: a.color } : {}) };
      return { ...rest, ...(colour !== AREA_COLOR_KEYS[0] ? { color: colour } : {}), ...(n !== 1 ? { markup: n } : {}) };
    });
    const placed = this.#place(next, id);
    this.commit(placed.doc);
    const first = placed.ids[0] ?? null;
    if (first !== this.#selection) this.select(first);
    else this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** The selected drawn shape's point made round (or a round point a corner). */
  toggleRound(i: number | null = this.#vertexSel): void {
    this.#reformPoint(i, (f, k) => toggleRound(f, k));
  }
  /** What the selected drawn shape's selected point is (null: no such point). */
  get selectedPointKind(): PointKind | null {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    const i = this.#vertexSel;
    if (i === null || !isShaped(it) || it.shape.k !== 'path' || it.shape.form?.f !== 'poly') return null;
    return pointKind(it.shape.form, i);
  }
  /** The selected drawn shape's selected point made a corner, a rounded corner or a point the outline curves through. */
  setSelectedPointKind(kind: PointKind): void {
    this.#reformPoint(this.#vertexSel, (f, k) => setPointKind(f, k, kind, this.#defaultRounding()));
  }
  #reformPoint(i: number | null, reform: (f: PolyForm, i: number) => PolyForm): void {
    const id = this.#selection;
    const it = id ? this.getItem(id) : undefined;
    if (i === null || !isShaped(it) || it.shape.k !== 'path' || it.shape.form?.f !== 'poly') return;
    const f = reform(it.shape.form, i);
    const next = updateItem(this.doc, it.id, (x) => ({ ...x, shape: keeping(it.shape, formShape(f)) }) as Item);
    if (it.t === 'cut') {
      this.commit(next);
      this.#events.selection(this.#selection, this.#vertexSel);
      return;
    }
    const placed = this.#place(next, it.id);
    this.commit(placed.doc);
    const first = placed.ids[0] ?? null;
    const now = first ? this.getItem(first) : undefined;
    this.select(first, isShaped(now) && now.shape.k === 'path' && now.shape.form ? i : null);
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** Whether the selected drawn shape's selected point is round (null: no such point). */
  get selectedPointRound(): boolean | null {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    const i = this.#vertexSel;
    if (i === null || !isShaped(it) || it.shape.k !== 'path' || it.shape.form?.f !== 'poly') return null;
    return !!it.shape.form.round[i];
  }
  /** The selected mark (or cut) is a drawn shape (its handles are its own). */
  get selectionIsShape(): boolean {
    const it = this.#selection ? this.getItem(this.#selection) : undefined;
    return isShaped(it) && it.shape.k === 'path' && !!it.shape.form;
  }
  /**
   * A drawn shape's rounded corners' handles (and, its points out, the selected corner's: pulled in,
   * it rounds): each corner's index and where its handle stands. Also every corner of a shape large
   * enough on screen that its handle stands clear of its point and sides.
   */
  #roundingsOf(it: Item, f: Form): { i: number; at: Vec }[] {
    if (f.f === 'ellipse') return [];
    const points = this.#pointsMode(it);
    const vs = this.view.scale;
    const P = formHandles(f);
    const n = P.length;
    const out: { i: number; at: Vec }[] = [];
    for (let i = 0; i < n; i++) {
      if (f.f === 'poly' && f.round[i]) continue;
      const has = radiusOf(f, i) > 0;
      const roomy = Math.min(dist(P[i]!, P[(i - 1 + n) % n]!), dist(P[i]!, P[(i + 1) % n]!)) * vs >= ROUNDING_ROOM;
      if (!has && !(points && this.#vertexSel === i) && !roomy) continue;
      const c = cornerRounding(f, i);
      if (c) out.push({ i, at: this.#roundingHandle(c) });
    }
    return out;
  }
  /**
   * A drawn shape's handle under a point, the nearest within a fingertip's reach: a point (a
   * corner, an axis end), else a rounded corner's handle, else the turning knob, else a side (a
   * box's side bar, a custom shape's mid-side dot) — sides too short on screen to hold a handle have none.
   */
  #formHit(ip: Vec): Hit | null {
    const id = this.#selection;
    const it = id ? this.displayDoc.items.find((i) => i.id === id) : undefined;
    if (!id || !isShaped(it) || it.shape.k !== 'path' || !it.shape.form) return null;
    // a mark's own handles are put away while shapes are cut out of it
    if (it.t !== 'cut' && this.#cutting) return null;
    const f = it.shape.form;
    const points = this.#pointsMode(it);
    const vs = this.view.scale;
    let best: Hit | null = null;
    let bd = HANDLE_REACH / vs;
    const P = formHandles(f);
    P.forEach((q, i) => {
      const d = dist(q, ip);
      if (d < bd) {
        bd = d;
        best = { id, part: 'fpoint', index: i };
      }
    });
    // a rounding handle nearer the finger than any point is the one it takes
    for (const h of this.#roundingsOf(it, f)) {
      const d = dist(h.at, ip);
      if (d < bd) {
        bd = d;
        best = { id, part: 'frad', index: h.i };
      }
    }
    if (best) return best;
    const knob = points ? null : this.#knobOf(f);
    if (knob && dist(knob.knob, ip) < HANDLE_REACH / vs) return { id, part: 'frot' };
    if (f.f === 'ellipse') return null;
    // its sides: a custom shape's (and any shape's with its points out) a dot mid-side — pulled, a new
    // point there; a square's a bar (the side moves square to itself)
    const dots = points || f.f === 'poly';
    bd = (dots ? MID_REACH : HANDLE_REACH) / vs;
    formMids(f).forEach((q, i) => {
      const side = dist(P[i]!, P[(i + 1) % P.length]!) * vs;
      if (side < (dots ? 40 : 56)) return;
      const d = dist(q, ip);
      if (d < bd) {
        bd = d;
        best = { id, part: dots ? 'fmid' : 'fside', index: i };
      }
    });
    return best;
  }
  /** A box's or oval's turning knob: where it stands (a fixed distance on screen beyond its top) and where it is joined on. */
  #knobOf(f: Form): { at: Vec; knob: Vec } | null {
    const k = formKnob(f);
    if (!k || f.f === 'poly') return null;
    const off = 30 / this.view.scale;
    const knob = { x: k.at.x + k.out.x * off, y: k.at.y + k.out.y * off };
    const half = f.f === 'rect' ? f.h / 2 : f.ry;
    const at2 = { x: f.cx - k.out.x * half, y: f.cy - k.out.y * half };
    const knob2 = { x: at2.x - k.out.x * off, y: at2.y - k.out.y * off };
    // above the shape, unless that is up under the controls or off the screen, or on a label or a
    // callout (a tap there is for the label): then below it, if that is clear
    const blocked = (q: Vec): boolean => {
      const sq = this.toScreen(q);
      if (sq.y < 72) return true;
      return this.labelRects().some((r) => sq.x > r.x - 14 && sq.x < r.x + r.w + 14 && sq.y > r.y - 14 && sq.y < r.y + r.h + 14);
    };
    if (blocked(knob) && !blocked(knob2)) return { at: at2, knob: knob2 };
    return { at: k.at, knob };
  }
  /** The drawn form of a mark in a document, if it has one. */
  #formOf(doc: MapDoc, id: string): Form | undefined {
    const it = doc.items.find((i) => i.id === id);
    return isShaped(it) && it.shape.k === 'path' ? it.shape.form : undefined;
  }
  /** The smallest a box or circle may be made (image px): never so small its handles crowd each other out. */
  #minSize(): number {
    return 16 / this.view.scale;
  }
  #clampToPlan(p: Vec): Vec {
    return { x: clamp(p.x, 0, this.doc.w), y: clamp(p.y, 0, this.doc.h) };
  }
  /** Where a drawn shape's handle is (null for other handles). */
  #formHandleAt(hit: Hit): Vec | null {
    if (hit.part === 'frot') {
      const f = this.#formOf(this.displayDoc, hit.id);
      return f ? (this.#knobOf(f)?.knob ?? null) : null;
    }
    if (hit.part === 'frad') {
      const it = this.displayDoc.items.find((i) => i.id === hit.id);
      const f = it && isShaped(it) && it.shape.k === 'path' ? it.shape.form : undefined;
      return f ? (this.#roundingsOf(it!, f).find((h) => h.i === hit.index)?.at ?? null) : null;
    }
    if (hit.part !== 'fpoint' && hit.part !== 'fmid' && hit.part !== 'fside') return null;
    const it = this.displayDoc.items.find((i) => i.id === hit.id);
    const f = isShaped(it) && it.shape.k === 'path' ? it.shape.form : undefined;
    if (!f) return null;
    return (hit.part === 'fpoint' ? formHandles(f) : formMids(f))[hit.index ?? 0] ?? null;
  }
  /**
   * Where a drawn shape's point i, carried to q, takes hold of the plan (with the Snap switch on):
   * a corner on the plan's corner its sides run along (or the line along one side), a round point
   * on the line beside it. Null: nothing to take hold of.
   */
  #pointSnap(it: Shaped, i: number, q: Vec): Vec | null {
    const engine = this.#engine;
    const f = it.shape.k === 'path' ? it.shape.form : undefined;
    if (!engine || !this.snapAnchors || !f || f.f === 'ellipse') return null;
    const P = formHandles(f);
    const n = P.length;
    const a = P[(i - 1 + n) % n]!,
      b = P[(i + 1) % n]!;
    if (f.f === 'rect') {
      // a box's corner: the plan's corner its two sides run along (the box keeps its turn)
      const s = snapCorner(engine, q, unitVec({ x: a.x - P[i]!.x, y: a.y - P[i]!.y }), unitVec({ x: b.x - P[i]!.x, y: b.y - P[i]!.y }), 1.2 * this.tolerance);
      return s?.p ?? null;
    }
    if (f.round[i]) {
      const c = polyCentroid(formPolygon(f, 8));
      return snapToLine(engine, q, c, this.tolerance)?.p ?? null;
    }
    return snapCorner(engine, q, unitVec({ x: a.x - q.x, y: a.y - q.y }), unitVec({ x: b.x - q.x, y: b.y - q.y }), 1.2 * this.tolerance)?.p ?? null;
  }

  /**
   * Where a drawn shape's side i, carried to q by its bar, takes hold of the plan (Snap on): the plan
   * line running along it (within 3°) in reach, the side landing on that line's outer edge. Null:
   * nothing there.
   */
  #sideSnap(it: Shaped, i: number, q: Vec): Vec | null {
    const engine = this.#engine;
    const f = it.shape.k === 'path' ? it.shape.form : undefined;
    if (!engine || !this.snapAnchors || !f || f.f === 'ellipse') return null;
    const P = formHandles(f);
    const a = P[i]!,
      b = P[(i + 1) % P.length]!;
    const along = unitVec({ x: b.x - a.x, y: b.y - a.y });
    // the outside: away from the middle of the shape
    const c = polyCentroid(formPolygon(f, 8));
    const n = { x: along.y, y: -along.x };
    const out = (q.x - c.x) * n.x + (q.y - c.y) * n.y >= 0 ? n : { x: -n.x, y: -n.y };
    return snapSide(engine, q, along, out, 1.2 * this.tolerance)?.p ?? null;
  }

  /* ------------------------------------------------- squares and circles */

  /** The square or circle drawn out by the finger so far (null: not yet a shape: a tap). */
  #placingForm(): Form | null {
    const pl = this.#placing;
    if (!pl || !pl.moved) return null;
    const min = this.#minSize();
    if (pl.kind === 'square') {
      // corner to corner, square to the screen; within a hair of square it is square
      let w = Math.max(min, Math.abs(pl.at.x - pl.start.x)),
        h = Math.max(min, Math.abs(pl.at.y - pl.start.y));
      if (Math.abs(w - h) <= 0.04 * Math.max(w, h)) w = h = Math.max(w, h);
      const sx = pl.at.x >= pl.start.x ? 1 : -1,
        sy = pl.at.y >= pl.start.y ? 1 : -1;
      return { f: 'rect', cx: pl.start.x + (sx * w) / 2, cy: pl.start.y + (sy * h) / 2, w, h, rot: 0 };
    }
    // from its middle out to the finger
    const r = Math.max(min / 2, dist(pl.start, pl.at));
    return { f: 'ellipse', cx: pl.start.x, cy: pl.start.y, rx: r, ry: r, rot: 0 };
  }
  /** The finger lifts with Square or Circle in hand: the shape drawn joins the map, selected; the tool stays in hand for the next. */
  #finishPlacing(): void {
    const pl = this.#placing!;
    const drawn = this.#placingForm();
    this.#placing = null;
    this.#dirty.live = true;
    this.#schedule();
    if (!drawn) {
      // a tap makes no square (one is drawn by a drag): it takes what is under it in hand — the square
      // just made, to adjust it before its callout is typed — or lets the selection go (and ends a
      // callout's typing)
      if (this.#cutting) return this.#tapWhileCutting(pl.start);
      const at = this.hitTest(pl.start);
      this.select(at ? at.id : null);
      return;
    }
    this.#commitShape(formShape(drawn), false);
  }

  /* ----------------------------------------------- the Pen: point by point */

  /** Points are being placed one at a time (how many so far), or not (null). */
  get building(): number | null {
    return this.#building ? this.#building.pts.length : null;
  }
  /** What the next point placed is (the Pen's choice, kept from shape to shape). */
  get penKind(): PenKind {
    return this.#building?.kind ?? this.#penKind;
  }
  /** The point of the path being placed that is in hand (its bar), or none. */
  get buildingSelection(): number | null {
    return this.#building?.sel ?? null;
  }
  /** What the point in hand of the path being placed is. */
  get buildingPointKind(): PointKind | null {
    const b = this.#building;
    if (!b || b.sel === null) return null;
    return b.round[b.sel] ? 'curve' : b.r[b.sel]! > 0 ? 'rounded' : 'corner';
  }
  /** Whether the path being placed can be undone a step. */
  get buildingCanUndo(): boolean {
    return !!this.#building?.undo.length;
  }
  /**
   * What the next point is — a corner, a rounded corner, a point the outline curves through, or the
   * finger draws (Draw). Kept from shape to shape. With a point of the path in hand it is that point
   * that changes.
   */
  setPenKind(kind: PenKind): void {
    const b = this.#building;
    if (b && b.sel !== null && kind !== 'draw') {
      const i = b.sel;
      this.#buildStep();
      b.round[i] = kind === 'curve' ? 1 : 0;
      b.r[i] = kind === 'rounded' ? b.r[i] || this.#defaultRounding() : 0;
      this.#buildChanged();
      return;
    }
    this.#penKind = kind;
    if (b) {
      b.kind = kind;
      b.sel = null;
      this.#buildChanged();
    }
    this.#events.building(b ? b.pts.length : null);
  }
  /** A rounding a finger can see at this zoom (image px), or the last one set by hand. */
  #defaultRounding(): number {
    return this.#lastRounding ?? 30 / this.view.scale;
  }
  /** Take away the point of the path in hand (a path keeps one). */
  deleteBuildingPoint(): void {
    const b = this.#building;
    if (!b || b.sel === null) return;
    if (b.pts.length <= 1) return this.cancelBuilding();
    this.#buildStep();
    const i = b.sel;
    for (const arr of [b.pts, b.raw, b.round, b.r, b.snapped] as unknown[][]) arr.splice(i, 1);
    b.sel = null;
    this.#buildChanged();
  }
  /** The path's state before a step, kept so the step can be undone. */
  #buildStep(): void {
    const b = this.#building!;
    b.undo.push({ pts: b.pts.slice(), raw: b.raw.slice(), round: b.round.slice(), r: b.r.slice(), snapped: b.snapped.slice() });
    if (b.undo.length > 200) b.undo.shift();
  }
  #buildChanged(): void {
    this.#dirty.live = true;
    this.#schedule();
    this.#events.building(this.#building ? this.#building.pts.length : null);
  }
  /** Begin a shape at a point already placed (with what took hold there). */
  #startBuilding(at: Vec, raw: Vec, snapped: boolean): void {
    // cutting goes on: the shape is a cut of the mark in hand
    this.select(this.#cutting);
    const kind = this.#penKind === 'draw' ? 'corner' : this.#penKind;
    this.#building = { pts: [at], raw: [raw], round: [kind === 'curve' ? 1 : 0], r: [kind === 'rounded' ? this.#defaultRounding() : 0], snapped: [snapped], kind: this.#penKind, sel: null, touch: null, stroke: null, undo: [] };
    this.#events.snapped();
    this.#buildChanged();
  }
  /**
   * Where a point of the path being placed takes hold (`index` null: the one being placed, after
   * the last): another mark's corner; the plan's corner its sides run along (to the point before
   * it and on to the next — for the one being placed, back to the first), or the plan's line; a
   * point on another mark's outline; failing all of those, a guide — square to the plan or to the
   * side before it, or in line with a point placed. Null: nothing in reach.
   */
  #buildSnap(q: Vec, index: number | null): Vec | null {
    const b = this.#building;
    this.#guides = null;
    if (!this.snapAnchors) return null;
    const pts = b?.pts ?? [];
    const n = pts.length;
    const at = (k: number): Vec | null => (n ? pts[((k % n) + n) % n]! : null);
    const prev = index === null ? at(n - 1) : n >= 2 && (index > 0 || n >= 3) ? at(index - 1) : null;
    const next = index === null ? (n >= 2 ? at(0) : null) : n >= 2 && (index < n - 1 || n >= 3) ? at(index + 1) : null;
    const prevPrev = !prev ? null : index === null ? (n >= 2 ? at(n - 2) : null) : n >= 3 ? at(index - 2) : null;
    const nextNext = !next ? null : index === null ? (n >= 3 ? at(1) : null) : n >= 3 ? at(index + 2) : null;
    const r = this.#placeReach();
    const curve = index === null ? this.penKind === 'curve' : !!b?.round[index];
    const cands: { p: Vec; score: number; guides?: GuideSnap['guides'] }[] = [];
    // another mark's corner, and its outline
    const own = this.#cutting;
    for (const it of this.displayDoc.items) {
      if (it.t !== 'hl' && it.t !== 'area' && it.t !== 'cut') continue;
      if (it.t === 'cut' && it.of !== own) continue;
      const f = it.shape.k === 'path' ? it.shape.form : undefined;
      const keys = f && f.f !== 'ellipse' ? formHandles(f) : [];
      for (const k of keys) {
        const d = dist(k, q);
        if (d <= r) cands.push({ p: k, score: d });
      }
      const poly = this.#polyOf(it.shape);
      const e = nearestOnPolyline(q, poly, true);
      if (e.d <= 0.6 * r) cands.push({ p: e.p, score: e.d + 0.55 * r });
    }
    // the plan
    const engine = this.#engine;
    if (engine) {
      let s: Snap | null = null;
      if (!curve && prev && next && dist(prev, next) > 1e-6) s = snapCorner(engine, q, unitVec({ x: prev.x - q.x, y: prev.y - q.y }), unitVec({ x: next.x - q.x, y: next.y - q.y }), r);
      else if (!curve && prev) s = snapCorner(engine, q, unitVec({ x: prev.x - q.x, y: prev.y - q.y }), unitVec({ x: q.x - prev.x, y: q.y - prev.y }), r);
      if (s) cands.push({ p: s.p, score: dist(s.p, q) + (s.kind === 'corner' ? 0 : 0.5 * r) });
      else {
        const any = engine.snapPoint(q, r);
        if (any.kind) cands.push({ p: any.p, score: dist(any.p, q) + (any.kind === 'node' ? 0.1 * r : 0.5 * r) });
      }
    }
    // the guides, where the plan and the marks have no corner to offer: two crossing (a rectangle's
    // last corner) better than the plan's line, one no better than the outline of a mark
    const cornered = cands.some((c) => c.score <= dist(c.p, q) + 1e-9);
    if ((prev || next) && !cornered) {
      const g = snapToGuides(q, penGuides(pts.filter((_, k) => k !== index), prev, prevPrev, next, nextNext), 0.6 * r);
      if (g) cands.push({ p: g.p, score: dist(g.p, q) + (g.guides.length > 1 ? 0.3 * r : 0.6 * r), guides: g.guides });
    }
    if (!cands.length) return null;
    cands.sort((x, y) => x.score - y.score);
    const best = cands[0]!;
    if (best.guides) this.#guides = { p: best.p, guides: best.guides };
    return best.p;
  }
  /** How far a point placed by a tap reaches for the plan's corner (image px): a fingertip lands ~4 mm from where it aims (Holz & Baudisch 2011). */
  #placeReach(): number {
    return 2 * this.tolerance;
  }
  /**
   * A touch with the Pen in hand and no shape under way. A finger: moving off at once, it moves the
   * plan (as it does with every tool but for drawing); lifted at once, it is a tap (see #onTap: the
   * first point there, or the mark tapped in hand); held a moment, it is the first point, with the
   * loupe to put it exactly (see #penHeld). The Pencil places the point at once — on bare plan; on a
   * mark it waits to see as a finger's tap would.
   */
  #penDown(p: Sample, ip: Vec, raw: Hit | null, info: StrokeInfo): DownAction {
    if (info.kind !== 'pen') return 'hold';
    // (the Pencil on the backdrop round the plan moves the plan: no point is ever there)
    if (!this.#onPlan(ip) && !(raw && raw.part === 'shape')) return 'pan';
    // (cutting, the mark itself is what the points are placed on: only its cuts take a tap)
    const over = raw && raw.part === 'shape' && raw.id !== this.#cutting ? raw.id : null;
    const sel = this.#selection ? this.getItem(this.#selection) : undefined;
    const typing = sel?.t === 'callout';
    const drag = new PreciseDrag(ip, p);
    const pp: PenPress = { drag, over, typing, moveable: over !== null && over === this.#selection, promoted: false, down: { x: p.x, y: p.y }, timer: 0, shown: false };
    this.#penPress = pp;
    if (!over && !typing) this.#promote(pp);
    else pp.timer = window.setTimeout(() => this.#promote(pp), PEN_HOLD_MS);
    return 'point';
  }
  /** A finger held still with the Pen and no shape under way: the first point, there, with the loupe to put it exactly. */
  #penHeld(p: Sample): void {
    const ip = this.toImage(p);
    // (held on the backdrop round the plan: no point there)
    if (!this.#onPlan(ip)) return;
    if (this.#selection && this.#selection !== this.#cutting) this.select(null);
    const pp: PenPress = { drag: new PreciseDrag(ip, p), over: null, typing: false, moveable: false, promoted: false, down: { x: p.x, y: p.y }, timer: 0, shown: false };
    this.#penPress = pp;
    this.#promote(pp);
    this.#events.hold('armed');
    if (this.#penPress === pp) {
      clearTimeout(this.#loupeTimer);
      this.#lastFinger = { x: p.x, y: p.y };
      this.#loupe = { at: pp.drag.at, finger: { x: p.x, y: p.y }, snapped: pp.drag.hold !== null, fine: false };
    }
  }
  /**
   * A tap with the Pen and no shape under way: on a mark (while cutting, on a cut) it takes that in
   * hand; one that ends a callout's typing does only that; anywhere else it is the first point of a
   * shape, there (on the plan's corner or line in reach).
   */
  #penTap(ip: Vec, raw: Hit | null): void {
    const over = raw && raw.part === 'shape' && raw.id !== this.#cutting ? raw.id : null;
    if (over) {
      if (this.#cutting) return this.#tapWhileCutting(ip);
      return this.select(over);
    }
    // a tap that lets something go — a label, a test, a mark, a callout — does only that: the next
    // tap is a point (2026-10-23, Adam: a point made by the tap that deselects "is not what the user
    // EVER wants"). (Cutting, the mark in hand is what the points are placed on.)
    if (this.#selection && this.#selection !== this.#cutting) return this.select(null);
    // a point is only ever on the plan: a tap on the backdrop round it makes none
    if (!this.#onPlan(ip)) return this.#events.notice('Tap on the plan to place a point');
    const q = this.#clampToPlan(ip);
    const s = this.#buildSnap(q, null);
    this.#guides = null;
    const at = s && dist(s, q) <= this.#placeReach() ? s : q;
    this.#startBuilding(at, q, at !== q);
  }
  /** The press is a point: it shows where it will land (the loupe comes once the finger stays). */
  #promote(pp: PenPress): void {
    if (this.#penPress !== pp || pp.promoted) return;
    pp.promoted = true;
    clearTimeout(pp.timer);
    if (this.#penKind === 'draw') {
      // Draw: the finger draws the shape from here (a tap is its first corner)
      this.#penPress = null;
      const ip = pp.drag.raw;
      this.select(this.#cutting);
      this.#building = { pts: [], raw: [], round: [], r: [], snapped: [], kind: 'draw', sel: null, touch: null, stroke: { pts: [ip.x, ip.y], times: [performance.now()], fromLast: false }, undo: [] };
      this.#buildChanged();
      return;
    }
    const s = this.#buildSnap(pp.drag.at, null);
    if (s && dist(s, pp.drag.at) <= this.#placeReach()) {
      pp.drag.hold = s;
      pp.drag.at = s;
    }
    this.#showLoupeSoon(pp.drag, pp.down);
    this.#dirty.live = true;
    this.#schedule();
  }
  /** The loupe for a point being placed — at once if the finger is already carrying it, else once it stays a moment (a quick tap flashes nothing). */
  #showLoupeSoon(drag: PreciseDrag, finger: Vec): void {
    clearTimeout(this.#loupeTimer);
    this.#loupeTimer = window.setTimeout(() => {
      const t = this.#building?.touch;
      const live = this.#penPress?.drag === drag || (t && t.drag === drag);
      if (!live || this.#loupe) return;
      this.#loupe = { at: drag.at, finger: this.#lastFinger ?? finger, snapped: drag.hold !== null, fine: drag.fine };
      this.#dirty.live = true;
      this.#schedule();
    }, LOUPE_DELAY_MS);
  }
  #penMove(last: Sample): void {
    const pp = this.#penPress!;
    this.#lastFinger = { x: last.x, y: last.y };
    if (!pp.promoted) {
      if (Math.hypot(last.x - pp.down.x, last.y - pp.down.y) <= this.#surface.opts.tapSlop) return;
      if (pp.moveable) {
        // the shape in hand, dragged at once: it moves (as with any tool)
        clearTimeout(pp.timer);
        this.#penPress = null;
        const down = { x: pp.down.x, y: pp.down.y, pressure: 0.5, t: last.t };
        this.#beginDrag({ id: pp.over!, part: 'shape' }, down, this.toImage(down));
        this.#onMove([last], last);
        return;
      }
      this.#promote(pp);
      // (Draw: the press became a stretch being drawn)
      if (this.#penPress !== pp) return this.#buildMove(last);
    }
    const m = pp.drag.move(last, this.view.scale, (q) => this.#buildSnap(q, null), this.#placeReach(), 1.6 * this.#placeReach());
    if (m.took) this.#events.snapped();
    if (this.#loupe || Math.hypot(last.x - pp.down.x, last.y - pp.down.y) > this.#surface.opts.tapSlop) this.#loupe = { at: m.at, finger: { x: last.x, y: last.y }, snapped: pp.drag.hold !== null, fine: pp.drag.fine };
    this.#edgePan(last);
    this.#dirty.live = true;
    this.#schedule();
  }
  #penUp(p: Sample): void {
    const pp = this.#penPress!;
    this.#penPress = null;
    clearTimeout(pp.timer);
    this.#stopEdgePan();
    this.#loupe = null;
    this.#dirty.live = true;
    this.#schedule();
    if (!pp.promoted) {
      // a tap: the mark under it in hand (or, while cutting, the cut under it); a tap that ends a callout's typing does only that
      const ip = this.toImage(p);
      if (this.#cutting) return this.#tapWhileCutting(ip);
      this.select(pp.over);
      return;
    }
    const at = this.#clampToPlan(pp.drag.release(p));
    this.#startBuilding(at, pp.drag.raw, pp.drag.hold !== null);
  }
  #buildDown(p: Sample, ip: Vec): DownAction {
    const b = this.#building!;
    this.#lastFinger = { x: p.x, y: p.y };
    // a rounded corner's rounding, by its handle (the nearest in reach, unless a point is nearer)
    const form = penForm(b);
    let rh: { i: number; h: Vec; d: number } | null = null;
    for (let i = 0; form && i < b.r.length; i++) {
      if (!(b.r[i]! > 0) || b.round[i]) continue;
      const c = cornerRounding(form, i);
      if (!c || !(c.r > 0)) continue;
      const h = this.#roundingHandle(c);
      const d = dist(h, ip);
      if (d * this.view.scale <= HANDLE_REACH && (!rh || d < rh.d)) rh = { i, h, d };
    }
    if (rh && !b.pts.some((q) => dist(q, ip) < rh.d)) {
      this.#buildStep();
      b.touch = { drag: new PreciseDrag(rh.h, p), index: rh.i, moved: false, part: 'radius' };
      b.sel = rh.i;
      return 'point';
    }
    // on a point already placed: that one is adjusted (a tap selects it; the first, with three or more, closes the shape)
    let index: number | null = null;
    let bd = HANDLE_REACH / this.view.scale;
    b.pts.forEach((q, i) => {
      const d = dist(q, ip);
      if (d < bd) {
        bd = d;
        index = i;
      }
    });
    if (index === null && b.kind !== 'draw') {
      // the middle of a side placed: pulled, a new point there (a tap there is a point as anywhere)
      const mids = this.#buildMids(b);
      let side = -1;
      let md = MID_REACH / this.view.scale;
      mids.forEach((q, i) => {
        const d = q ? dist(q, ip) : Infinity;
        if (d < md) {
          md = d;
          side = i;
        }
      });
      if (side >= 0) {
        b.touch = { drag: new PreciseDrag(mids[side]!, p), index: null, moved: false, part: 'mid', side };
        this.#dirty.live = true;
        this.#schedule();
        return 'point';
      }
    }
    // (no new point, nor a stretch drawn, from the backdrop round the plan: there the finger moves the plan)
    if (index === null && !this.#onPlan(ip)) return 'pan';
    if (b.kind === 'draw' && (index === null || index === b.pts.length - 1)) {
      // Draw: the finger draws the outline on (from the last point, when it sets off there); a tap is a corner
      b.stroke = { pts: [ip.x, ip.y], times: [p.t], fromLast: index !== null };
      this.#dirty.live = true;
      this.#schedule();
      return 'point';
    }
    const at = index !== null ? b.pts[index]! : ip;
    const drag = new PreciseDrag(at, p);
    if (index === null) {
      // a new point shows at once where it will land
      const s = this.#buildSnap(ip, null);
      if (s && dist(s, ip) <= this.#placeReach()) {
        drag.hold = s;
        drag.at = s;
      }
    }
    b.touch = { drag, index, moved: false, part: 'point' };
    this.#showLoupeSoon(drag, { x: p.x, y: p.y });
    this.#dirty.live = true;
    this.#schedule();
    return 'point';
  }
  /** Where a rounded corner's handle stands: just inside its arc's middle, never on the point itself. */
  #roundingHandle(c: { p: Vec; b: Vec; mid: Vec }): Vec {
    const off = Math.max(dist(c.mid, c.p), 0) + ROUNDING_OFF / this.view.scale;
    return { x: c.p.x + c.b.x * off, y: c.p.y + c.b.y * off };
  }
  /** The rounding a corner's handle carried to q asks for: its distance in along the corner's middle, less the handle's standoff. */
  #roundingAt(c: { p: Vec; b: Vec; sinHalf: number; maxR: number }, q: Vec): number {
    const t = (q.x - c.p.x) * c.b.x + (q.y - c.p.y) * c.b.y - ROUNDING_OFF / this.view.scale;
    const k = 1 / c.sinHalf - 1;
    return clamp(k > 1e-6 ? t / k : c.maxR, 0, c.maxR);
  }
  /**
   * The middle of each side placed so far (side i runs from point i to the next; not the side that
   * will close it), on the outline as it will be — null where a side is too short on screen for a dot.
   */
  #buildMids(b: Building): (Vec | null)[] {
    const n = b.pts.length;
    if (n < 2) return [];
    const form = penForm(b);
    const mids = form ? formMids(form) : [{ x: (b.pts[0]!.x + b.pts[1]!.x) / 2, y: (b.pts[0]!.y + b.pts[1]!.y) / 2 }];
    return b.pts.slice(0, n - 1).map((a, i) => (dist(a, b.pts[i + 1]!) * this.view.scale >= 40 ? (mids[i] ?? null) : null));
  }
  /** The shape being placed as it is now (the point carried where it is, the one being placed last), or the first point pressed. */
  #penDrawing(): PenDrawing | null {
    const pp = this.#penPress;
    if (pp?.promoted) return { pts: [pp.drag.at], round: [this.#penKind === 'curve' ? 1 : 0], r: [0], form: null, placing: true, snapped: pp.drag.hold !== null, sel: null, stroke: null, guides: null, roundings: [], mids: [] };
    const b = this.#building;
    if (!b) return null;
    const t = b.touch && b.touch.part === 'point' ? b.touch : null;
    const placing = !!t && t.index === null;
    const kind: PointKind = b.kind === 'draw' ? 'corner' : b.kind;
    const pending = t ? { at: t.drag.at, index: t.index, kind, r: this.#defaultRounding() } : null;
    const pts = t && t.index !== null ? b.pts.map((q, i) => (i === t.index ? t.drag.at : q)) : placing ? [...b.pts, t!.drag.at] : b.pts;
    const round = placing ? [...b.round, kind === 'curve' ? 1 : 0] : b.round;
    const r = placing ? [...b.r, kind === 'rounded' ? this.#defaultRounding() : 0] : b.r;
    const form = penForm(b, pending);
    // the rounded corners' handles (not while a point is carried: they would jump about)
    const roundings: Vec[] = [];
    if (form && !t) {
      b.r.forEach((x, i) => {
        if (!(x > 0) || b.round[i]) return;
        const c = cornerRounding(form, i);
        if (c && c.r > 0) roundings.push(this.#roundingHandle(c));
      });
    }
    const mids = b.touch || b.stroke ? [] : this.#buildMids(b).filter((q): q is Vec => q !== null);
    return { pts, round, r, form, placing, snapped: !!t && t.drag.hold !== null, sel: b.sel, stroke: b.stroke?.pts ?? null, guides: t ? this.#guides : null, roundings, mids };
  }
  #buildMove(last: Sample): void {
    const b = this.#building!;
    this.#lastFinger = { x: last.x, y: last.y };
    if (b.stroke) {
      const ip = this.toImage(last);
      const s = b.stroke.pts;
      if (Math.hypot(ip.x - s[s.length - 2]!, ip.y - s[s.length - 1]!) > 0.75) {
        s.push(ip.x, ip.y);
        b.stroke.times.push(last.t);
      }
      this.#edgePan(last);
      this.#dirty.live = true;
      this.#schedule();
      return;
    }
    const t = b.touch!;
    if (t.part === 'mid') {
      // pulled far enough to mean it: a new point in the middle of that side, carried from there
      t.drag.move(last, this.view.scale, () => null, 0, 0);
      if (dist(this.toScreen(t.drag.raw), this.toScreen(this.#buildMids(b)[t.side!] ?? t.drag.raw)) <= 3) return;
      this.#buildStep();
      const k = t.side! + 1;
      const round = b.round[t.side!] || b.round[k % b.pts.length] ? 1 : 0;
      b.pts.splice(k, 0, t.drag.at);
      b.raw.splice(k, 0, t.drag.at);
      b.round.splice(k, 0, round);
      b.r.splice(k, 0, 0);
      b.snapped.splice(k, 0, false);
      b.sel = null;
      t.part = 'point';
      t.index = k;
      t.moved = true;
      this.#events.building(b.pts.length);
    }
    if (t.part === 'radius') {
      const f = penForm(b);
      const c = f ? cornerRounding(f, t.index!) : null;
      const m = t.drag.move(last, this.view.scale, () => null, 0, 0);
      if (c) {
        b.r[t.index!] = this.#roundingAt(c, m.at);
        t.moved = true;
      }
      this.#dirty.live = true;
      this.#schedule();
      return;
    }
    const m = t.drag.move(last, this.view.scale, (q) => this.#buildSnap(q, t.index), this.#placeReach(), 1.6 * this.#placeReach());
    if (dist(this.toScreen(t.drag.raw), this.toScreen(b.pts[t.index ?? 0] ?? t.drag.raw)) > 3 || t.index === null) t.moved = true;
    if (m.took) this.#events.snapped();
    this.#loupe = { at: m.at, finger: { x: last.x, y: last.y }, snapped: t.drag.hold !== null, fine: t.drag.fine };
    this.#edgePan(last);
    this.#dirty.live = true;
    this.#schedule();
  }
  #buildUp(p: Sample, wasTap: boolean): void {
    const b = this.#building!;
    const t = b.touch;
    const stroke = b.stroke;
    b.touch = null;
    b.stroke = null;
    this.#loupe = null;
    this.#guides = null;
    this.#stopEdgePan();
    clearTimeout(this.#loupeTimer);
    this.#dirty.live = true;
    this.#schedule();
    if (stroke) {
      const s0 = this.#clampToPlan({ x: stroke.pts[0]!, y: stroke.pts[1]! });
      if (polylineLength(stroke.pts) * this.view.scale >= 12) return this.#drawnOn(stroke);
      // (Draw) a tap: on the last point, it is in hand; elsewhere a corner there — or, on the first, the shape closes
      if (stroke.fromLast) {
        b.sel = b.sel === b.pts.length - 1 ? null : b.pts.length - 1;
        return this.#buildChanged();
      }
      const snap = this.#buildSnap(s0, null);
      const at = snap && dist(snap, s0) <= this.#placeReach() ? snap : s0;
      this.#guides = null;
      return this.#placeNext(at, s0, at !== s0);
    }
    if (!t) return;
    if (t.part === 'radius') {
      if (t.moved) this.#lastRounding = b.r[t.index!]!;
      else b.undo.pop();
      return this.#buildChanged();
    }
    if (t.part === 'mid') {
      // a tap by the middle of a side: a point there, as anywhere
      const ip = this.#clampToPlan(this.toImage(p));
      const s = this.#buildSnap(ip, null);
      this.#guides = null;
      const on = s && dist(s, ip) <= this.#placeReach() ? s : ip;
      return this.#placeNext(on, ip, on !== ip);
    }
    const at = this.#clampToPlan(t.drag.release(p));
    if (t.index !== null) {
      if (!t.moved) {
        // a tap on the first point, with three or more placed, closes the shape; on any other, it is in hand (its bar)
        if (t.index === 0 && b.pts.length >= 3 && wasTap) return this.finishBuilding();
        b.sel = b.sel === t.index ? null : t.index;
        return this.#buildChanged();
      }
      this.#buildStep();
      b.pts[t.index] = at;
      b.raw[t.index] = t.drag.raw;
      b.snapped[t.index] = t.drag.hold !== null;
      b.sel = t.index;
      return this.#buildChanged();
    }
    this.#placeNext(at, t.drag.raw, t.drag.hold !== null);
  }
  /** The next point placed, after the last (lifted on the first point, three or more placed, the shape closes). */
  #placeNext(at: Vec, raw: Vec, snapped: boolean): void {
    const b = this.#building!;
    if (b.pts.length >= 3 && dist(this.toScreen(at), this.toScreen(b.pts[0]!)) <= HANDLE_REACH) return this.finishBuilding();
    this.#buildStep();
    const kind = b.kind === 'draw' ? 'corner' : b.kind;
    b.pts.push(at);
    b.raw.push(raw);
    b.round.push(kind === 'curve' ? 1 : 0);
    b.r.push(kind === 'rounded' ? this.#defaultRounding() : 0);
    b.snapped.push(snapped);
    b.sel = null;
    // the point before it now has both its sides: landed again on the plan's corner they make
    this.#relandCorner(b.pts.length - 2);
    this.#events.snapped();
    this.#buildChanged();
  }
  /**
   * A stretch drawn with Draw: the points it makes go on after the last (from where it began, if
   * that is away from the last point); drawn back to the first point, it closes the shape.
   */
  #drawnOn(stroke: { pts: number[]; times: number[]; fromLast: boolean }): void {
    const b = this.#building!;
    const n = stroke.pts.length;
    const start = { x: stroke.pts[0]!, y: stroke.pts[1]! },
      end = { x: stroke.pts[n - 2]!, y: stroke.pts[n - 1]! };
    if (!b.pts.length && dist(this.toScreen(start), this.toScreen(end)) <= 2 * HANDLE_REACH) {
      // a whole loop drawn from nothing: the shape it is, as drawn, made perfect
      const reading = readSketch(stroke.pts, { tol: this.tolerance, times: stroke.times });
      const f = reading?.forms.drawn;
      if (f && f.f === 'poly') {
        this.#building = null;
        this.#events.building(null);
        this.#commitShape(formShape(f), false);
        this.#schedule();
        return;
      }
    }
    const fit = fitOpenStroke(stroke.pts, { tol: this.tolerance, times: stroke.times });
    if (!fit || fit.pts.length < 2) return b.pts.length ? this.#buildChanged() : this.cancelBuilding();
    this.#buildStep();
    const pts = fit.pts.slice();
    const round = fit.round.slice();
    // begun on the last point: it goes on from there
    if (stroke.fromLast) {
      pts.shift();
      round.shift();
    }
    // ended on the first point: that is where it closes
    const first = b.pts[0] ?? pts[0]!;
    const closes = b.pts.length + pts.length >= 4 && dist(this.toScreen(pts[pts.length - 1]!), this.toScreen(first)) <= HANDLE_REACH;
    if (closes) {
      pts.pop();
      round.pop();
    }
    pts.forEach((q0, k) => {
      const q = this.#clampToPlan(q0);
      b.pts.push(q);
      b.raw.push(q);
      b.round.push(round[k]!);
      b.r.push(0);
      b.snapped.push(false);
    });
    b.sel = null;
    if (closes && b.pts.length >= 3) return this.finishBuilding();
    this.#buildChanged();
  }
  /** A corner that took hold of the plan, landed again now that both its sides are known (on the plan's corner they run along). */
  #relandCorner(i: number): void {
    const b = this.#building!;
    const n = b.pts.length;
    const engine = this.#engine;
    if (!engine || !this.snapAnchors || i < 0 || i >= n || !b.snapped[i] || b.round[i] || n < 2) return;
    const q = b.raw[i]!;
    const prev = n >= 3 || i > 0 ? b.raw[(i - 1 + n) % n]! : null;
    const next = n >= 3 || i < n - 1 ? b.raw[(i + 1) % n]! : null;
    if (!prev || !next || dist(prev, next) < 1e-6) return;
    const r = this.#placeReach();
    const s = snapCorner(engine, q, unitVec({ x: prev.x - q.x, y: prev.y - q.y }), unitVec({ x: next.x - q.x, y: next.y - q.y }), r);
    // only ever a refinement of where it was seen to land: onto the corner's ink edge nearby, never somewhere else
    if (s && s.kind === 'corner' && dist(s.p, q) <= r && dist(s.p, b.pts[i]!) <= r) b.pts[i] = s.p;
  }
  /** Close the shape whose points have been placed: it joins the map (or, while cutting, is cut out of the mark). */
  finishBuilding(): void {
    const b = this.#building;
    if (!b) return;
    if (b.pts.length < 3) return this.#events.notice('Place at least three points');
    // the first and last corners have both their sides now
    this.#relandCorner(0);
    this.#relandCorner(b.pts.length - 1);
    const f = penForm(b);
    this.#building = null;
    this.#loupe = null;
    this.#guides = null;
    this.#dirty.live = true;
    this.#events.building(null);
    if (!f) return;
    this.#commitShape(formShape(f), false);
    this.#schedule();
  }
  cancelBuilding(): void {
    if (!this.#building) return;
    this.#building = null;
    this.#loupe = null;
    this.#guides = null;
    this.#stopEdgePan();
    this.#dirty.live = true;
    this.#schedule();
    this.#events.building(null);
  }
  /** Take back the last step of the path being placed (the first point taken back ends it). */
  undoBuildingPoint(): void {
    const b = this.#building;
    if (!b) return;
    const was = b.undo.pop();
    if (!was) return this.cancelBuilding();
    Object.assign(b, was);
    b.sel = null;
    if (!b.pts.length) return this.cancelBuilding();
    this.#buildChanged();
  }
  /* ------------------------------------------------ the plan runs on under the finger */

  /**
   * A point (or a stretch being drawn) carried to the edge of the screen: the plan glides on the
   * other way, the faster the nearer the edge, so a shape bigger than the screen is placed in one go.
   */
  #edgePan(finger: Vec): void {
    const W = this.#size.w,
      H = this.#size.h;
    const zone = EDGE_ZONE;
    // (eased: barely moving at the zone's inner side, full speed at the edge)
    const push = (d: number) => (d < zone ? ((zone - Math.max(0, d)) / zone) ** 2 : 0);
    const vx = push(finger.x) - push(W - finger.x);
    const vy = push(finger.y) - push(H - finger.y);
    if (!vx && !vy) return this.#stopEdgePan();
    const ep = this.#edge;
    if (ep) {
      ep.v = { x: vx, y: vy };
      return;
    }
    const e = { v: { x: vx, y: vy }, t: performance.now(), raf: 0 };
    this.#edge = e;
    const step = (now: number) => {
      if (this.#edge !== e) return;
      const dt = Math.min(50, now - e.t) / 1000;
      e.t = now;
      const was = this.#view;
      this.#setView(constrain(pan(was, e.v.x * EDGE_SPEED * dt, e.v.y * EDGE_SPEED * dt), this.doc.w, this.doc.h, this.#screen(), 120));
      // the plan moves under the finger (as far as it goes), and what the finger carries moves on with it
      const dx = this.#view.tx - was.tx,
        dy = this.#view.ty - was.ty;
      const shift = { x: -dx / this.view.scale, y: -dy / this.view.scale };
      const carried = this.#penPress?.drag ?? this.#building?.touch?.drag ?? this.#drag?.point ?? null;
      carried?.shift(shift.x, shift.y);
      const st = this.#building?.stroke;
      if (st && this.#lastFinger) {
        const ip = this.toImage(this.#lastFinger);
        st.pts.push(ip.x, ip.y);
        st.times.push(now);
      }
      if (this.#lastFinger) {
        const ip = this.toImage(this.#lastFinger);
        // a mark, a label, a dot, a handle or an arrowhead in hand: where the finger now is on the plan
        const dg = this.#drag;
        if (dg?.moved) {
          this.#working = this.#applyDrag(dg, ip.x - dg.start.x, ip.y - dg.start.y, ip);
          dg.last = ip;
          if (dg.point && this.#loupe) this.#loupe = { ...this.#loupe, at: dg.point.at };
          this.#invalidate(true);
        }
        // a square or circle being drawn out, a stroke being drawn: on to where the finger is
        if (this.#placing) this.#placing.at = this.#clampToPlan(ip);
        const s = this.#stroke;
        if (s) {
          s.push(ip.x, ip.y);
          this.#times.push(now);
          this.#tracer?.add([ip]);
        }
      }
      if (this.#loupe && carried) this.#loupe = { ...this.#loupe, at: carried.at };
      this.#dirty.live = true;
      this.#schedule();
      e.raf = requestAnimationFrame(step);
    };
    e.raf = requestAnimationFrame(step);
  }
  #stopEdgePan(): void {
    if (this.#edge) cancelAnimationFrame(this.#edge.raf);
    this.#edge = null;
  }
  #onTwoFingerTap(): void {
    if (this.#building) return this.undoBuildingPoint();
    if (!this.canUndo) return;
    this.undo();
    this.#events.notice('Undone');
  }
  #endRest(): void {
    if (this.#dwell) clearTimeout(this.#dwell.timer);
    this.#dwell = null;
  }
  /** A callout grows an arrow to each of these marks that no callout points at yet. */
  #pointAt(doc: MapDoc, calloutId: string, markIds: readonly string[]): MapDoc {
    for (const id of markIds) {
      const mark = doc.items.find((x) => x.id === id);
      if (mark?.t === 'hl' && !calloutsAt(doc.items, id).length) doc = updateItem<Callout>(doc, calloutId, (c) => addArrow(c, { target: this.#clampToPlan(anchorOnShape(mark.shape)), targetId: id }));
    }
    return doc;
  }
  /**
   * The callout that speaks for a pour's marks of one material: the selected one when it does,
   * else the latest. (Another material in the same pour is another statement: its own callout.)
   */
  #pourCallout(doc: MapDoc, placement: number, material: string): Callout | null {
    const marks = new Set(doc.items.filter((x) => x.t === 'hl' && placementOf(x) === placement && x.material === material).map((x) => x.id));
    let found: Callout | null = null;
    for (const x of doc.items) {
      if (x.t !== 'callout' || !markIdsOf(x).some((id) => marks.has(id))) continue;
      if (x.id === this.#selection) return x;
      found = x;
    }
    return found;
  }
  /** Pieces of a mark smaller than this are not worth keeping. */
  get #minPiece(): number {
    const u = this.metrics.u;
    return Math.max(9, (3 * u) * (3 * u));
  }
  /**
   * A mark settles into its placement: it yields to the marks of other placements (cut
   * back to their sides, so the two abut exactly and never overlap; a mark drawn wholly
   * over another placement leaves nothing), then takes in the marks of its own placement
   * and material it overlaps (one shape; it keeps its id, and their callouts' arrows point at it).
   * A cut that leaves several pieces makes each a mark of the placement. Returns the doc
   * and the ids of the resulting marks, the main one first (none when nothing is left).
   */
  #place(doc: MapDoc, id: string): { doc: MapDoc; ids: string[] } {
    const it = doc.items.find((x) => x.id === id);
    if (!it || (it.t !== 'area' && it.t !== 'hl')) return { doc, ids: [] };
    let ids = [id];
    {
      const poly = shapeToPolygon(it.shape);
      const cuts = this.#yieldsTo(doc, it);
      if (it.t === 'hl' && poly.length >= 6 && cuts.length) this.#wallsFor(doc, it, poly, cuts);
      if (poly.length >= 6 && cuts.length) {
        const parts = differencePolygons(poly, cuts, this.#minPiece);
        if (!parts.length) return { doc: withoutMarks(doc, new Set([id])).doc, ids: [] };
        if (parts[0] !== poly) {
          ids = [];
          const at = doc.items.findIndex((x) => x.id === id);
          const pieces: (Highlight | WorkArea)[] = parts.map((part, k) => {
            const pid = k === 0 ? id : newId();
            ids.push(pid);
            return { ...it, id: pid, shape: this.#reshaped(part, it.shape.k === 'path' && !!it.shape.form) };
          });
          doc = { ...doc, items: [...doc.items.slice(0, at), ...pieces, ...doc.items.slice(at + 1)] };
          // the callouts pointing at the mark point at every piece of it
          for (const c of calloutsAt(doc.items, id)) for (const piece of pieces.slice(1)) doc = updateItem<Callout>(doc, c.id, (x) => addArrow(x, { target: this.#clampToPlan(anchorOnShape(piece.shape)), targetId: piece.id }));
        }
      }
    }
    for (const mid of ids) doc = this.#absorbInto(doc, mid);
    const left = new Set(doc.items.map((x) => x.id));
    ids = ids.filter((x) => left.has(x));
    return { doc, ids };
  }
  /**
   * A mark drawn out of another placement's mark — its own end inside that mark, heading
   * back into it, and that mark's end inside it — starts where the other ends: nothing of
   * it stays behind that end's plane (the finger seldom runs exactly on the other's sides,
   * so the plain cut would leave a strip beside the other mark back to where the finger
   * started). Such walls are added to `cuts`.
   */
  #wallsFor(doc: MapDoc, it: Highlight, poly: number[], cuts: number[][]): void {
    const border = borderOf(it.shape);
    if (!border) return;
    const d = doc;
    const u = this.metrics.u;
    const myEnds = bandEnds(border, 4 * u, 0.12 * Math.max(d.w, d.h), 8 * u);
    if (!myEnds.length) return;
    const b = polyBounds(poly);
    const depth = Math.hypot(b.w, b.h) + 10 * u;
    for (const nb of d.items) {
      if (nb.t !== 'hl' || nb.id === it.id || placementOf(nb) === placementOf(it)) continue;
      const nbPoly = this.#polyOf(nb.shape);
      for (const e of this.#endsOf(nb.shape, nb)) {
        if (!pointInPoly(e.mid, poly)) continue;
        if (!myEnds.some((f) => pointInPoly(f.mid, nbPoly) && f.out.x * e.out.x + f.out.y * e.out.y < -0.5)) continue;
        const w = e.width;
        const p = { x: e.a.x - e.along.x * w, y: e.a.y - e.along.y * w },
          q = { x: e.b.x + e.along.x * w, y: e.b.y + e.along.y * w };
        cuts.push([p.x, p.y, q.x, q.y, q.x - e.out.x * depth, q.y - e.out.y * depth, p.x - e.out.x * depth, p.y - e.out.y * depth]);
      }
    }
  }
  /**
   * The borders a mark yields to: a pour's mark to the marks of every other pour; a work area to the
   * work areas of every other markup (a failing area yields to nothing, and nothing to it).
   */
  #yieldsTo(doc: MapDoc, it: WorkArea | Highlight): number[][] {
    if (it.t === 'hl') return this.#cutsFor(doc, placementOf(it), it.id);
    if (it.status === 'fail') return [];
    const n = markupOf(it);
    const out: number[][] = [];
    for (const x of doc.items) if (x.t === 'area' && x.id !== it.id && x.status !== 'fail' && markupOf(x) !== n) out.push(this.#polyOf(x.shape));
    return out;
  }
  /** Marks of the same kind, placement and material that the item now overlaps are taken into it; it keeps its id. */
  #absorbInto(doc: MapDoc, id: string): MapDoc {
    const it = doc.items.find((x) => x.id === id);
    if (!it || (it.t !== 'area' && it.t !== 'hl')) return doc;
    let poly = shapeToPolygon(it.shape);
    if (poly.length < 6) return doc;
    // work areas join work areas of their markup and colour, failing areas failing ones; a pour's marks those of its material
    const colourOf = (a: WorkArea) => (a.status === 'fail' ? 'fail' : `${markupOf(a)}:${a.color ?? AREA_COLOR_KEYS[0]}`);
    const peers = doc.items.filter((x): x is WorkArea | Highlight => x.id !== id && x.t === it.t && ((x.t === 'area' && it.t === 'area' && colourOf(x) === colourOf(it)) || (it.t === 'hl' && x.t === 'hl' && x.material === it.material && placementOf(x) === placementOf(it))));
    const gone = new Set<string>();
    let changed = true;
    while (changed) {
      changed = false;
      for (const x of peers) {
        if (gone.has(x.id)) continue;
        const xp = shapeToPolygon(x.shape);
        if (xp.length < 6 || !polygonsOverlap(poly, xp)) continue;
        // a join is at least as large as either mark; one that is not (an outline crossed over
        // itself confuses it) is not made, and the two marks stay as they are
        const joined = unionPolygons([poly, xp]);
        if (joined.length < 6 || Math.abs(polyAreaOf(joined)) < 0.98 * Math.max(Math.abs(polyAreaOf(poly)), Math.abs(polyAreaOf(xp)))) continue;
        poly = joined;
        gone.add(x.id);
        changed = true;
      }
    }
    if (!gone.size) return doc;
    const joined = [it, ...peers.filter((x) => gone.has(x.id))];
    const hadForm = joined.some((x) => x.shape.k === 'path' && !!x.shape.form);
    let next = updateItem<WorkArea | Highlight>(doc, id, (x) => ({ ...x, shape: this.#reshaped(poly, hadForm) }));
    // what was cut out of the marks taken in is cut out of it
    next = { ...next, items: next.items.map((x) => (x.t === 'cut' && gone.has(x.of) ? { ...x, of: id } : x)) };
    return retargetArrows(removeItems(next, gone), gone, id);
  }
  addTest(p: Vec, floor = 0): TestPoint {
    const d = this.doc;
    const method = this.#testMethod;
    // a test placed in a failing area fails with it
    const t: TestPoint = { t: 'test', id: newId(), p, n: this.nextNumber(method, floor), date: d.soils.date, initials: d.soils.initials, status: this.#failingAt(d, p) ? 'fail' : 'none', retestOf: null, note: '', label: null, ...(method === 'sandcone' ? { method } : {}) };
    // the map's numbering of the method starts here, once its first test is placed
    this.commit(this.#startingAt(addItem(d, t), method, floor));
    // placed, not selected: nothing comes up beside it where the next test may go (a tap on it opens its bar)
    this.select(null);
    return t;
  }
  /** Whether the topmost work area at a point (less what is cut out of it) is a failing one. */
  #failingAt(doc: MapDoc, p: Vec): boolean {
    for (let i = doc.items.length - 1; i >= 0; i--) {
      const it = doc.items[i]!;
      if (it.t === 'area' && this.#markHit(doc, it, p, 0)) return it.status === 'fail';
    }
    return false;
  }
  /**
   * The number the next test of a method takes: the lowest not in use from where the map's numbering
   * of it starts — so a test deleted (or undone) frees its number for the next. `floor`: the highest
   * number on the day's other maps, for a map that has not begun numbering the method.
   */
  nextNumber(method: TestMethod = this.#testMethod, floor = 0): number {
    const d = this.doc;
    return nextTestNumber(d.items, d.soils.date, floor, method, d.soils.start?.[method]);
  }
  /** The map's numbering of a method made to start from where it does now, if it has no start yet. */
  #startingAt(doc: MapDoc, method: TestMethod, floor: number): MapDoc {
    if (doc.soils.start?.[method] !== undefined) return doc;
    return { ...doc, soils: { ...doc.soils, start: { ...doc.soils.start, [method]: numberingStart(this.doc.items, doc.soils.date, floor, method) } } };
  }
  /**
   * Renumber a test. The map's first test renumbered moves where its numbering starts (a map begun at
   * the wrong number is put right by renumbering its first test); `start` sets it outright (Test labels).
   */
  renumberTest(id: string, n: number, floor = 0, dropName = false): void {
    const it = this.getItem(id);
    if (it?.t !== 'test') return;
    if (n === it.n && !(dropName && it.name)) return;
    const method = methodOf(it);
    const from = numberingStart(this.doc.items, this.doc.soils.date, floor, method, this.doc.soils.start?.[method]);
    let doc = updateItem<TestPoint>(this.doc, id, (x) => {
      const { name: _name, ...rest } = x;
      return { ...(dropName ? rest : x), n };
    });
    if (it.n === from) doc = { ...doc, soils: { ...doc.soils, start: { ...doc.soils.start, [method]: n } } };
    this.commit(doc);
  }
  /**
   * What a test is called on the map: a number alone is its number (it is renumbered, and the next
   * test follows on from it); anything else — "94R", "A-3", "SC 2" — is its own name, shown as typed,
   * its number kept for the counting; nothing typed takes the name away and shows the number again.
   */
  nameTest(id: string, text: string, floor = 0): void {
    const it = this.getItem(id);
    if (it?.t !== 'test') return;
    const name = text.trim().slice(0, TEST_NAME_MAX);
    if (/^\d{1,4}$/.test(name) && Number(name) >= 1) return this.renumberTest(id, Number(name), floor, true);
    if ((it.name ?? '') === name) return;
    this.commit(
      updateItem<TestPoint>(this.doc, id, (x) => {
        const { name: _name, ...rest } = x;
        return name ? { ...rest, name } : rest;
      }),
    );
  }
  /** Where the map's numbering of a method starts (Test labels: "the next test placed will be number …"). */
  setNumberingStart(method: TestMethod, n: number): void {
    if (this.doc.soils.start?.[method] === n) return;
    this.commit({ ...this.doc, soils: { ...this.doc.soils, start: { ...this.doc.soils.start, [method]: n } } });
  }
  /** How the next test is taken (nuclear gauge or sand cone): each is numbered on its own. */
  #testMethod: TestMethod = 'nuke';
  /** How the next test placed is taken. */
  get testMethod(): TestMethod {
    return this.#testMethod;
  }
  /**
   * Choose nuke or sand cone for the tests placed from now on. Like the next number beside it, this is
   * about the next test only: the test just placed (selected while Test is in hand) is left as it is.
   */
  setNextTestMethod(method: TestMethod): void {
    this.#testMethod = method;
  }
  /**
   * A test taken by the other method after all: it takes the next number of its new method (`floor`:
   * that method's count on the other maps of the day), so each stays counted on its own. One undo step.
   */
  setTestMethod(id: string, method: TestMethod, floor = 0): void {
    const it = this.getItem(id);
    if (it?.t !== 'test' || methodOf(it) === method) return;
    const n = this.nextNumber(method, floor);
    const doc = updateItem<TestPoint>(this.doc, id, (t) => {
      const { method: _m, ...rest } = t;
      return { ...rest, n, ...(method === 'sandcone' ? { method } : {}) };
    });
    this.commit(this.#startingAt(doc, method, floor));
  }
  /* --------------------------------------------------------- elevations */

  /** The shot whose elevation is being typed (its tag is a field, in place), if any. */
  #elevEditing: string | null = null;
  /** What its tag shows of the value meanwhile (the field's text, or its placeholder): the tag is laid out from it. */
  #elevDraft: ElevDraft | null = null;
  get elevEditing(): string | null {
    return this.#elevEditing;
  }
  /** How the map's elevation tags read. */
  get elevMeta(): ElevMeta | undefined {
    return this.doc.elev;
  }
  /** The number the next shot takes: the lowest free from where the map's numbering starts. */
  nextElevNumber(): number {
    return nextElevNumber(this.doc.items, this.doc.elev?.start);
  }
  /**
   * A shot where the finger was: the next number, its elevation to be typed straight away (the tag is
   * the field). Typing it is part of placing it — one undo step takes both back — and a shot left
   * before anything was typed is gone, as if it had never been.
   */
  addElev(p: Vec): Elevation {
    this.#endElevEdit();
    const e: Elevation = { t: 'elev', id: newId(), p, n: this.nextElevNumber(), value: '', label: null };
    this.commit(addItem(this.doc, e));
    this.select(e.id);
    this.#elevEditing = e.id;
    this.#coalesce = `elev:${e.id}:value`;
    this.#events.selection(this.#selection, this.#vertexSel);
    return e;
  }
  /** Type into a shot's elevation again (its tag is the field). */
  beginElevEdit(id: string): void {
    const it = this.getItem(id);
    if (it?.t !== 'elev') return;
    if (this.#selection !== id) this.select(id);
    this.#elevEditing = id;
    this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** Typing into the shot is over (Return, a tap elsewhere): the tag shows the value as typed. `drop`: a shot left untyped goes. */
  endElevEdit(): void {
    this.#endElevEdit();
  }
  #endElevEdit(notify = true): void {
    const id = this.#elevEditing;
    if (!id) return;
    this.#elevEditing = null;
    this.setElevDraft(null);
    this.#dropUntyped(id);
    // a value that looks like a slip of the finger is said — however the typing ended
    const it = this.getItem(id);
    const warn = it?.t === 'elev' ? elevWarning(it, this.doc.items) : null;
    if (warn) this.#events.warn(warn);
    if (notify) this.#events.selection(this.#selection, this.#vertexSel);
  }
  /** A shot whose elevation was never typed is taken away — the step that placed it withdrawn when it was the last one. */
  #dropUntyped(id: string): void {
    if (id === this.#elevEditing) this.#elevEditing = null;
    const it = this.getItem(id);
    // (nothing that reads as an elevation — nothing at all, or a minus sign alone — is no shot)
    if (it?.t !== 'elev' || isElevation(it.value)) return;
    const before = this.#history.previous;
    const without = removeItems(this.doc, new Set([id]));
    const same = before && before.items.length === without.items.length && before.items.every((x, i) => x === without.items[i]);
    if (this.#selection === id) this.#setSelection(null, null);
    if (same && this.#history.withdraw()) {
      this.#coalesce = null;
      this.#invalidate(true);
      this.#events.change(this.doc);
    } else this.commit(without);
  }
  /** The shot's elevation as it is typed (kept as typed: never rounded). Keystrokes into it are one undo step. */
  setElevValue(id: string, raw: string): void {
    const it = this.getItem(id);
    if (it?.t !== 'elev') return;
    const value = cleanElevation(raw);
    if (value === it.value) return;
    this.commitCoalesced(updateItem<Elevation>(this.doc, id, (e) => ({ ...e, value })), `elev:${id}:value`);
  }
  /** What the tag shows of the value while it is typed (null: the value itself). True when the tag may have changed size. */
  setElevDraft(draft: ElevDraft | null): boolean {
    const a = this.#elevDraft;
    if (a === draft || (a && draft && a.id === draft.id && a.shown === draft.shown)) return false;
    this.#elevDraft = draft;
    this.#layout = null;
    this.#invalidate(false);
    return true;
  }
  /** What a shot's tag calls its elevation ("BOE", "FG", "" for nothing; null: the map's word). */
  setElevPrefix(id: string, prefix: string | null): void {
    this.updateItem<Elevation>(id, (e) => {
      const { prefix: _p, ...rest } = e;
      return prefix === null ? rest : { ...rest, prefix: prefix.trim().slice(0, ELEV_PREFIX_MAX) };
    });
  }
  /** A few words after a shot's elevation (empty: none). */
  setElevNote(id: string, note: string): void {
    const t = note.trim().slice(0, ELEV_NOTE_MAX);
    this.updateItem<Elevation>(id, (e) => {
      const { note: _n, ...rest } = e;
      return t ? { ...rest, note: t } : rest;
    });
  }
  /** The grade a shot is measured against (null: none): its tag then says how far below it the shot is. */
  setElevRef(id: string, ref: ElevRef | null): void {
    this.updateItem<Elevation>(id, (e) => {
      const { ref: _r, ...rest } = e;
      const value = ref ? cleanElevation(ref.value) : '';
      return ref && value ? { ...rest, ref: { name: ref.name.trim().slice(0, ELEV_PREFIX_MAX), value } } : rest;
    });
  }
  /** Renumber a shot; the map's first shot renumbered moves where the numbering starts (as a test's does). */
  renumberElev(id: string, n: number): void {
    const it = this.getItem(id);
    if (it?.t !== 'elev' || n === it.n || !Number.isInteger(n) || n < 1 || n > 9999) return;
    const lowest = Math.min(...this.doc.items.flatMap((x) => (x.t === 'elev' ? [x.n] : [])));
    // (a number another shot has is given to this one, and that one takes the next free)
    let doc = updateItem<Elevation>(this.doc, id, (e) => ({ ...e, n }));
    const other = doc.items.find((x): x is Elevation => x.t === 'elev' && x.id !== id && x.n === n);
    if (other) doc = updateItem<Elevation>(doc, other.id, (e) => ({ ...e, n: nextElevNumber(doc.items, doc.elev?.start) }));
    if (it.n === lowest && it.n === (this.doc.elev?.start ?? 1)) doc = { ...doc, elev: { ...doc.elev, start: n } };
    this.commit(doc);
  }
  /** How the map's elevation tags read: their word, their colour, where their numbering starts (one undo step). */
  setElevMeta(patch: { [K in keyof ElevMeta]?: ElevMeta[K] | undefined }): void {
    const merged: Record<string, unknown> = { ...this.doc.elev, ...patch };
    for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k];
    const next = merged as ElevMeta;
    if (JSON.stringify(next) === JSON.stringify(this.doc.elev ?? {})) return;
    const { elev: _e, ...rest } = this.doc;
    this.commit(Object.keys(next).length ? { ...rest, elev: next } : rest);
  }

  /** A blank callout for a mark (or a free point), placed automatically and selected for typing. */
  addCallout(material: string | null, target: Vec, targetId: string | null): Callout {
    const c = withAutoStations(this.#newCallout(material, this.#clampToPlan(target), targetId), this.doc, this.#stations);
    // a pour's mark given a callout of its own leaves the ones it only shared: their words are no longer
    // about it (a soils callout is a remark of its own: an area may have several)
    const doc = targetId && this.doc.kind === 'concrete' ? { ...this.doc, items: this.doc.items.map((x) => (x.t === 'callout' ? removeArrowAt(x, targetId) : x)) } : this.doc;
    this.commit(bringToFront(addItem(doc, c), c.id));
    this.select(c.id);
    return c;
  }
  #newCallout(material: string | null, target: Vec, targetId: string | null): Callout {
    // the alignment carries over from the last callout on this map: usually the same line
    let alignment = '';
    for (const it of this.doc.items) if (it.t === 'callout' && it.alignment.trim()) alignment = it.alignment;
    // a colour-only material leaves the material word for the person to type
    const materialName = material && materialByKey(material).swatchOnly ? { materialName: '' } : {};
    // on a soils map a callout is words of the inspector's own (there are no stations to fill in)
    const template = this.doc.kind === 'soils' ? 'custom' : 'between';
    return { t: 'callout', id: newId(), material, template, alignment, from: '', to: '', custom: '', box: null, target, targetId, ...materialName };
  }


  /* -------------------------------------------------------------- export */

  async export(format: 'png' | 'jpeg', quality = 0.92): Promise<Blob> {
    const d = this.doc;
    const c = document.createElement('canvas');
    c.width = d.w;
    c.height = d.h;
    const ctx = c.getContext('2d')!;
    const m = metrics(d.w, d.h, d.scale, d.kind);
    const layout = computeLayout(d, m, sharedMeasurer(), layoutRects(this.#layout?.layout ?? this.#lastLayout), null, null, null, this.#planAround);
    renderAll(ctx, d, this.#image, m, layout, {});
    const blob = await canvasToBlob(c, format === 'jpeg' ? 'image/jpeg' : 'image/png', quality);
    releaseCanvas(c);
    return blob;
  }
  /** The whole map drawn small, at once (the caller releases the canvas). */
  snapshot(maxSide = 480): HTMLCanvasElement {
    const d = this.doc;
    const k = Math.min(1, maxSide / Math.max(d.w, d.h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(d.w * k));
    c.height = Math.max(1, Math.round(d.h * k));
    const ctx = c.getContext('2d')!;
    ctx.scale(k, k);
    const m = metrics(d.w, d.h, d.scale, d.kind);
    renderAll(ctx, d, this.#image, m, this.layoutFor(d), {});
    return c;
  }
  async thumbnail(maxSide = 480): Promise<Blob> {
    const c = this.snapshot(maxSide);
    const blob = await canvasToBlob(c, 'image/jpeg', 0.8);
    releaseCanvas(c);
    return blob;
  }

  destroy(): void {
    cancelAnimationFrame(this.#raf);
    this.#stopGlide();
    clearTimeout(this.#motionTimer);
    if (this.#shapesCache) releaseCanvas(this.#shapesCache.canvas);
    this.#shapesCache = null;
    this.#ro.disconnect();
    this.#surface.destroy();
    for (const c of [this.#base, this.#overlay, this.#live]) releaseCanvas(c);
    releaseScratch();
    this.stack.remove();
  }
}

/**
 * A callout in a new material: its word follows the material's name, except that the
 * colour-only materials leave the word for the person to type.
 */
function recolourCallout(c: Callout, key: string): Callout {
  const { materialName: _was, ...rest } = c;
  return materialByKey(key).swatchOnly ? { ...rest, material: key, materialName: '' } : { ...rest, material: key };
}

/** A sensible callout anchor on a legacy band or polygon. */
function anchorOnShape(shape: Shape): Vec {
  if (shape.k === 'stroke') return { x: shape.pts[Math.floor(shape.pts.length / 4) * 2]!, y: shape.pts[Math.floor(shape.pts.length / 4) * 2 + 1]! };
  if (shape.k === 'rect') return { x: shape.x + shape.w / 2, y: shape.y + shape.h / 2 };
  if (shape.k === 'path') return anchorInside(flattenPath(shape.pts, shape.h));
  return anchorInside(shape.pts);
}

/** Rectangles become polygons the moment they are edited by vertex. */
function toEditable(s: Shape): Extract<Shape, { k: 'poly' | 'stroke' | 'path' }> {
  if (s.k === 'rect') return { k: 'poly', pts: rectToPoly(shapeBounds(s)) };
  return s;
}
function moveShape(s: Shape, dx: number, dy: number): Shape {
  if (s.k === 'rect') return { ...s, x: s.x + dx, y: s.y + dy };
  if (s.k === 'path') {
    const moved: PathShape = s.form ? (formShape(translateForm(s.form, dx, dy)) as PathShape) : { k: 'path', ...translatePath(s, dx, dy) };
    if (s.edit && moved.form) moved.edit = true;
    return moved;
  }
  return { ...s, pts: translatePts(s.pts, dx, dy) };
}
/**
 * A shape reshaped (a point, a side or the knob carried, a point taken away, an end pulled) keeps
 * what the reshaping is not about: whether its points are being edited.
 */
function keeping(prev: Shape, next: Shape): Shape {
  if (prev.k !== 'path' || !prev.edit || next.k !== 'path' || !next.form) return next;
  return { ...next, edit: true };
}
function union(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x),
    y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/** The last `len` image px of a finger route (for the glow trail). */
function recentRoute(stroke: number[], len: number): number[] {
  const out: number[] = [];
  let acc = 0;
  for (let i = stroke.length - 2; i >= 0; i -= 2) {
    out.unshift(stroke[i]!, stroke[i + 1]!);
    if (i >= 2) acc += Math.hypot(stroke[i]! - stroke[i - 2]!, stroke[i + 1]! - stroke[i - 1]!);
    if (acc > len) break;
  }
  return out;
}

/** What each reading is called where it is offered (a sketch's own reading is "As drawn"; any other shape's points, "Custom"). */
export const READING_LABEL: Record<Reading, string> = { drawn: 'Custom', rect: 'Square', circle: 'Circle', ellipse: 'Oval', plan: 'Plan outline' };

/** Four corners that are square (within 1.5°) as a box; else null. */
function polyPointsOf(pts: readonly number[]): Vec[] {
  const out: Vec[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) out.push({ x: pts[i]!, y: pts[i + 1]! });
  return out;
}
function nearestIndex(P: readonly Vec[], q: Vec): number {
  let best = 0,
    bd = Infinity;
  P.forEach((p, i) => {
    const d = dist(p, q);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
}
function asBox(f: Form): RectForm | null {
  if (f.f !== 'poly' || f.pts.length !== 8 || f.round.some((r) => r)) return null;
  for (let i = 0; i < 4; i++) {
    const ax = f.pts[(2 * i + 6) % 8]! - f.pts[2 * i]!,
      ay = f.pts[(2 * i + 7) % 8]! - f.pts[2 * i + 1]!;
    const bx = f.pts[(2 * i + 2) % 8]! - f.pts[2 * i]!,
      by = f.pts[(2 * i + 3) % 8]! - f.pts[2 * i + 1]!;
    const c = (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by) || 1);
    if (Math.abs(c) > Math.sin((1.5 * Math.PI) / 180)) return null;
  }
  return boxAround(f.pts);
}

function polyAreaOf(pts: number[]): number {
  let a = 0;
  const n = pts.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
  }
  return a / 2;
}

function unitVec(a: Vec): Vec {
  const l = Math.hypot(a.x, a.y) || 1;
  return { x: a.x / l, y: a.y / l };
}

/**
 * A path shape made from a form (the form rides along, so it can be handled as drawn). A form the
 * document could not keep — too few or too many points, no size, a number that is not a number —
 * is never kept: the shape is then its outline alone.
 */
export function formShape(f: Form): Shape {
  const path = formPath(f);
  return formFits(f) && path.pts.length >= 6 && path.pts.every(Number.isFinite) && path.h.every(Number.isFinite) ? { k: 'path', ...path, form: f } : { k: 'path', ...path };
}

function formFits(f: Form): boolean {
  if (f.f === 'poly') return f.pts.length >= 6 && f.pts.length <= 400 && f.round.length === f.pts.length / 2 && f.pts.every(Number.isFinite);
  const nums = f.f === 'rect' ? [f.cx, f.cy, f.w, f.h, f.rot] : [f.cx, f.cy, f.rx, f.ry, f.rot];
  const size = f.f === 'rect' ? Math.min(f.w, f.h) : Math.min(f.rx, f.ry);
  return nums.every(Number.isFinite) && size >= 0.1 && Math.abs(f.rot) <= 100;
}

/** Every ~120 px of a polyline, rounded (dev diagnostics). */
function decimate(pts: number[]): number[] {
  const out: number[] = [];
  let acc = 120;
  for (let i = 0; i + 1 < pts.length; i += 2) {
    if (i > 0) acc += Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!);
    if (acc >= 120 || i + 2 >= pts.length) {
      out.push(Math.round(pts[i]!), Math.round(pts[i + 1]!));
      acc = 0;
    }
  }
  return out;
}

/** A line of the plan's lettering as one box (image px): from its first glyph to its last. */
function textLineBox(glyphs: readonly { x0: number; y0: number; x1: number; y1: number }[]): Rect {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const g of glyphs) {
    x0 = Math.min(x0, g.x0);
    y0 = Math.min(y0, g.y0);
    x1 = Math.max(x1, g.x1);
    y1 = Math.max(y1, g.y1);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
